import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  FormHelperText,
  CircularProgress,
  Paper,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CloudUpload,
  Dataset,
  Apps,
  Computer,
  InsertDriveFile,
  PictureAsPdf,
  Close,
  CheckCircle,
  Folder,
  FolderOpen,
  Description,
  ArrowBack,
  ArrowForward,
  Image as ImageIcon,
  VideoLibrary,
  AudioFile,
  TableChart,
  ChevronRight,
  ExpandMore,
  ExpandLess,
  FolderShared,
  Storage,
  Search as SearchIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Web,
} from '@mui/icons-material';
import { modalStyles } from '../../styles/modalStyles';
import { API_CONFIG, API_ENDPOINTS, ML_API_ENDPOINTS, STORAGE_KEYS, SOCKET_KEYS, GOOGLE_DRIVE_CLIENT_ID, GOOGLE_CALENDAR_CLIENT_ID, OUTLOOK_CALENDAR_CLIENT_ID, SHAREPOINT_CLIENT_ID, isDriveAppKey, shouldShowAppInMarketplace, DISABLE_SUBSCRIPTION } from '../../helpers/constants';
import { DATA_SOURCES_DIALOG_Z_INDEX } from '../../helpers/zIndexLayers';
import { LanguageConstants } from '../../helpers/LanguageConstants';
import { useToast } from '../../contexts/ToastContext';
import channelService from '../../services/channelService';
import socketService from '../../services/socketService';
import { capitalizeFirstLetter } from '@/helpers/stringUtils';
import { appsService, AppWithConnectionStatus } from '../../services/appsService';
import { useTheme } from '@mui/material/styles';
import { useTheme as useThemeContext } from '../../contexts/ThemeContext';
import { 
  getTableHeaderStyles, 
  getTableContainerStyles, 
  getTableRowStyles, 
  getTableCellStyles 
} from '../../styles/tableStyles';
import ConnectedDatasourcesCount from '../ConnectedDatasourcesCount';
import AppsGrid from '../shared/AppsGrid';
import { getFreshNodeApiHeaders, refreshAccessToken } from '../../services/authService';
import BasicAuthConnectionDialog, { BasicAuthConnectionData } from '../oauth/BasicAuthConnectionDialog';
import ChannelSelectDropdown from './ChannelSelectDropdown';
import { encryptPassword } from '../../utils/crypto';
import ChannelRestrictionModal from '../ChannelRestrictionModal';
import { useSubscriptionValidation } from '../../hooks/useSubscriptionValidation';
import { fetchCompanySubscriptionDetails, nonNegativeUsageStat } from '../../services/subscriptionService';
import { getCompanyId as getCompanyIdFromStorage } from '../../helpers/storageUtils';
import { encryptSecretForStorage } from '../../services/secretEncryptionService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-source-tabpanel-${index}`}
      aria-labelledby={`data-source-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1.5 }}>{children}</Box>}
    </div>
  );
}

interface DataSourceStepProps {
  dataSources: any[];
  onDataSourcesChange: (sources: any[]) => void;
  onClose?: () => void;
  onDataSourceAdded?: () => void;
  onFirstBulkSuccess?: (pendingFilenames?: string[]) => void; // Called when first bulk API (upload) succeeds; pass filenames so panel can show "In progress"
  channelId?: string;
  hideInternalButtons?: boolean; // New prop to hide internal buttons
  showStepNavigationButtons?: boolean; // New prop to control step navigation buttons visibility (default: true for backward compatibility)
}

// Allowed file formats for data sources
const ALLOWED_FILE_FORMATS = [
  'csv', 'xlsx', 'xls', 'json', 
  'pdf', 'docx', 'doc', 'txt',
  'pptx', 'ppt', 'md',
  // Google Drive specific formats
  'application/vnd.google-apps.document',     // Google Docs
  'application/vnd.google-apps.spreadsheet',  // Google Sheets
  'application/vnd.google-apps.presentation'  // Google Slides
];
const DATA_SOURCE_LIMIT = 20;

interface WebsiteUrlValidationResult {
  normalizedUrl: string | null;
  error: string | null;
}

/**
 * Accept normal website input with or without a protocol. The URL constructor
 * treats "giggso.com" as an invalid relative URL, so normalize bare domains
 * before validating and sending them to the scraper.
 */
const validateWebsiteUrl = (value: string): WebsiteUrlValidationResult => {
  const trimmedUrl = value.trim();
  if (!trimmedUrl) {
    return { normalizedUrl: null, error: 'Please enter a website URL' };
  }

  const hasProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmedUrl);
  const candidateUrl = hasProtocol ? trimmedUrl : `https://${trimmedUrl}`;

  try {
    const parsedUrl = new URL(candidateUrl);
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return { normalizedUrl: null, error: 'URL must use http:// or https://' };
    }

    if (!parsedUrl.hostname || parsedUrl.hostname.includes('..')) {
      return { normalizedUrl: null, error: 'Please enter a valid website URL' };
    }

    return { normalizedUrl: parsedUrl.toString(), error: null };
  } catch {
    return { normalizedUrl: null, error: 'Please enter a valid website URL' };
  }
};

const getEncryptedClientSecret = (oauthData: any, provider: string): string => {
  const clientSecret = oauthData?.clientSecret;
  if (typeof clientSecret !== 'string' || !clientSecret.trim()) {
    throw new Error(`${provider} OAuth response did not include encrypted client secret`);
  }

  return clientSecret.trim();
};

interface Channel {
  id: string;
  name: string;
  description: string;
  workspace_id: string;
  workspace_name: string;
  company_id: string;
  is_active: boolean;
  is_public: boolean;
  ai_enabled: boolean;
  channel_settings: any;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ChannelsResponse {
  channels: Channel[];
  total: number;
  page: number;
  size: number;
}

const DataSourceStep = forwardRef<any, DataSourceStepProps>(({ 
  dataSources, 
  onDataSourcesChange, 
  onClose, 
  onDataSourceAdded, 
  onFirstBulkSuccess,
  channelId,
  hideInternalButtons = false,
  showStepNavigationButtons = true // Default to true for backward compatibility
}, ref) => {
  const { showSuccess, showError, showWarning } = useToast();
  const muiTheme = useTheme();
  const { isDarkMode } = useThemeContext();
  // Drive/SharePoint stepper surfaces — MUI grey[50]/grey[100] stay light in dark mode; use paper + scaled greys
  const driveWizardSurface = muiTheme.palette.background.paper;
  const driveWizardMutedSurface = isDarkMode
    ? muiTheme.palette.grey[800]
    : muiTheme.palette.grey[50];
  const driveSearchInputFill = isDarkMode
    ? muiTheme.palette.grey[900]
    : muiTheme.palette.common.white;
  const driveReviewTableHeaderBg = isDarkMode
    ? muiTheme.palette.grey[800]
    : muiTheme.palette.grey[100];

  // Subscription validation hook for attachment restrictions
  const { validate: validateSubscription } = useSubscriptionValidation();
  
  // State for attachment restriction modal
  const [openAttachmentRestrictionModal, setOpenAttachmentRestrictionModal] = useState<boolean>(false);
  // When drive multi-select would exceed subscription storage, block selecting more until user deselects
  const [driveStorageSelectionBlocked, setDriveStorageSelectionBlocked] = useState<boolean>(false);
  // One fetch per browse session: API "available" bytes for plan storage (remaining quota)
  const [driveStorageAvailableBaseline, setDriveStorageAvailableBaseline] = useState<number | null>(null);
  const driveQuotaFetchedSessionKeyRef = useRef<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [optimisticConnectedAppId, setOptimisticConnectedAppId] = useState<string | null>(null);
  const optimisticClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Clear optimistic-app timeout on unmount
  useEffect(() => () => {
    if (optimisticClearTimeoutRef.current) clearTimeout(optimisticClearTimeoutRef.current);
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showAccessKeyId, setShowAccessKeyId] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>(channelId || '');
  const [workspaceError, setWorkspaceError] = useState<string>('');
  const [hasTouchedFile, setHasTouchedFile] = useState(false);
  const [deviceDescription, setDeviceDescription] = useState<string>('');
  const [deviceDescriptionError, setDeviceDescriptionError] = useState<string>('');
  // const [logType, setLogType] = useState<string>('');
  // const [logTypeError, setLogTypeError] = useState<string>('');

  // Apps tab state
  const [datasourceApps, setDatasourceApps] = useState<AppWithConnectionStatus[]>([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingMoreApps, setLoadingMoreApps] = useState(false);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [connectingApps, setConnectingApps] = useState<{ [key: string]: boolean }>({});
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const [selectedApp, setSelectedApp] = useState<AppWithConnectionStatus | null>(null);
  const [connectionState, setConnectionState] = useState<{ token: string; appKey: string; appId: string } | null>(null);
  const [currentAppsPage, setCurrentAppsPage] = useState(1);
  const [totalAppsPages, setTotalAppsPages] = useState(1);
  
  // Basic Auth states (for Freshdesk)
  const [basicAuthDialogOpen, setBasicAuthDialogOpen] = useState(false);
  const [pendingBasicAuthConnection, setPendingBasicAuthConnection] = useState<{ appKey: string; appId: string; appName: string } | null>(null);
  const [hasMoreApps, setHasMoreApps] = useState(true);
  const appsScrollContainerRef = useRef<HTMLDivElement>(null);

  // Google Drive OAuth specific states
  const [googleDriveOauthData, setGoogleDriveOauthData] = useState<any>(null);
  const googleDrivePopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const googleDriveOAuthProcessedRef = useRef<boolean>(false);
  
  // Google Calendar OAuth specific states
  const [googleCalendarOauthData, setGoogleCalendarOauthData] = useState<any>(null);
  const googleCalendarPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const googleCalendarOAuthProcessedRef = useRef<boolean>(false);
  
  // Outlook Calendar OAuth specific states
  const [outlookCalendarOauthData, setOutlookCalendarOauthData] = useState<any>(null);
  const outlookCalendarPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const outlookCalendarOAuthProcessedRef = useRef<boolean>(false);
  
  // SharePoint OAuth specific states
  const [sharePointOauthData, setSharePointOauthData] = useState<any>(null);
  const sharePointPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sharePointOAuthProcessedRef = useRef<boolean>(false);
  // Per-provider: avoid double-toast from duplicate postMessage; reset when starting each OAuth flow
  const googleDriveOAuthCancelledToastShownRef = useRef(false);
  const sharePointOAuthCancelledToastShownRef = useRef(false);
  
  // SharePoint site selection states
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedSiteName, setSelectedSiteName] = useState<string | null>(null);
  const [selectedSiteUrl, setSelectedSiteUrl] = useState<string | null>(null);
  const [availableSites, setAvailableSites] = useState<any[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);
  
  // Ref for scrolling to "Yes, I understand" checkbox
  const acceptanceCheckboxRef = useRef<HTMLDivElement>(null);
  
  // Ref for scrolling to file browser section
  const fileBrowserRef = useRef<HTMLDivElement>(null);
  
  // Ref for scrolling to selected items section
  const selectedItemsRef = useRef<HTMLDivElement>(null);

  // File browser state
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [dataSourceInfoAccepted, setDataSourceInfoAccepted] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined);
  const [folderPath, setFolderPath] = useState<Array<{ id: string | undefined; name: string }>>([{ id: undefined, name: 'Home' }]);
  const [filesAndFolders, setFilesAndFolders] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  // Google Drive: 'home' = show 3 options (My Drive, Shared drives, Shared with me); then myDrive | sharedWithMe | sharedDrives
  const [googleDriveType, setGoogleDriveType] = useState<'home' | 'myDrive' | 'sharedWithMe' | 'sharedDrives'>('home');
  const [googleSharedDriveId, setGoogleSharedDriveId] = useState<string | undefined>(undefined);
  const [driveSearchQuery, setDriveSearchQuery] = useState('');
  const driveSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // SharePoint file browser search (same pattern as Google Drive)
  const [sharePointSearchQuery, setSharePointSearchQuery] = useState('');
  const sharePointSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // Prevent auto-fetch loop when Shared with me / Shared drives return empty (effect would re-run on filesAndFolders.length === 0)
  const didInitialFetchForStep2Ref = useRef(false);
  // Prevent SharePoint files/sites API loop on error – stop retrying after first error until user changes site or step
  const sharePointFetchErrorRef = useRef(false);
  
  // Step-based navigation for Google Drive flow
  const [fileSelectionStep, setFileSelectionStep] = useState(0);
  
  // Folder selection state
  const [selectedFolders, setSelectedFolders] = useState<any[]>([]);
  const [folderContents, setFolderContents] = useState<{ [folderId: string]: any[] }>({});
  const [loadingFolderContents, setLoadingFolderContents] = useState<{ [folderId: string]: boolean }>({});
  const [folderValidationErrors, setFolderValidationErrors] = useState<{ [folderId: string]: string }>({});
  
  // Track selection order for display
  const [selectionOrder, setSelectionOrder] = useState<Array<{ type: 'file' | 'folder', id: string }>>([]);
  
  // Track expanded folders in review selection
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Expose handleAddDataSource method to parent component
  useImperativeHandle(ref, () => ({
    handleAddDataSource,
    isCreatingDataSource,
    tabValue, // Expose tabValue for footer component
    selectedWorkspace, // Expose for ConnectedDatasourcesCount when modal has no channelId (e.g. Admin Hub)
    // Expose step navigation state and handlers
    fileSelectionStep,
    fileSelectionSteps,
    handleNextStep,
    handleBackStep,
    canGoNext: () => {
      if (fileSelectionStep === 1 && !dataSourceInfoAccepted) return false;
      if (fileSelectionStep === 2 && selectedFiles.length === 0 && selectedFolders.length === 0) return false;
      return true;
    },
    canGoBack: () => fileSelectionStep > 1
  }));

  // Cloud tab state
  const [cloudWorkspace, setCloudWorkspace] = useState<string>(channelId || '');
  const [cloudWorkspaceError, setCloudWorkspaceError] = useState<string>('');
  const [storageProvider, setStorageProvider] = useState<string>('');
  const [storageProviderError, setStorageProviderError] = useState<string>('');
  const [locationUrl, setLocationUrl] = useState<string>('');
  const [locationUrlError, setLocationUrlError] = useState<string>('');
  // Webscrapping tab — single-request flow: is_embedding_required: true on create, so the
  // backend crawls and embeds automatically with no separate "Start Embedding" step (see the
  // datasourcePayload construction below for the actual create-time payload).
  const [websiteName, setWebsiteName] = useState<string>('');
  const [websiteNameError, setWebsiteNameError] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState<string>('');
  const [websiteUrlError, setWebsiteUrlError] = useState<string>('');
  // "Level" (config.crawl_depth) — dropdown, 1-3, default 1 (webscrapping_fix branch: 0 removed
  // as a selectable/default level — every scrape now crawls at least one level deep).
  const [websiteCrawlDepth, setWebsiteCrawlDepth] = useState<string>('1');
  const [showWebsiteAdvanced, setShowWebsiteAdvanced] = useState<boolean>(false);
  const [secretAccessKey, setSecretAccessKey] = useState<string>('');
  const [secretAccessKeyError, setSecretAccessKeyError] = useState<string>('');
  const [accessKeyId, setAccessKeyId] = useState<string>('');
  const [accessKeyIdError, setAccessKeyIdError] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [descriptionError, setDescriptionError] = useState<string>('');
  const [cloudLogType, setCloudLogType] = useState<string>('');
  const [cloudLogTypeError, setCloudLogTypeError] = useState<string>('');

  // GCP-specific state
  const [gcpServiceAccountFile, setGcpServiceAccountFile] = useState<File | null>(null);
  const [gcpServiceAccountKey, setGcpServiceAccountKey] = useState<string>('');
  const [gcpServiceAccountKeyError, setGcpServiceAccountKeyError] = useState<string>('');
  const [gcpProjectId, setGcpProjectId] = useState<string>(''); // Extracted from JSON

  // Database tab state
  const [databaseType, setDatabaseType] = useState<string>('');
  const [databaseTypeError, setDatabaseTypeError] = useState<string>('');
  const [dbHostname, setDbHostname] = useState<string>('');
  const [dbHostnameError, setDbHostnameError] = useState<string>('');
  const [dbPort, setDbPort] = useState<string>('');
  const [dbPortError, setDbPortError] = useState<string>('');
  const [dbUsername, setDbUsername] = useState<string>('');
  const [dbUsernameError, setDbUsernameError] = useState<string>('');
  const [dbPassword, setDbPassword] = useState<string>('');
  const [dbPasswordError, setDbPasswordError] = useState<string>('');
  const [dbDatabaseName, setDbDatabaseName] = useState<string>('');
  const [dbDatabaseNameError, setDbDatabaseNameError] = useState<string>('');
  const [dbServiceName, setDbServiceName] = useState<string>(''); // Oracle
  const [dbSchemaName, setDbSchemaName] = useState<string>(''); // PostgreSQL, Oracle, Redshift, Snowflake
  const [dbWarehouse, setDbWarehouse] = useState<string>(''); // Snowflake
  const [dbAccountId, setDbAccountId] = useState<string>(''); // Snowflake
  const [dbAccessKeyId, setDbAccessKeyId] = useState<string>(''); // DynamoDB
  const [dbSecretAccessKey, setDbSecretAccessKey] = useState<string>(''); // DynamoDB
  const [dbRegionName, setDbRegionName] = useState<string>(''); // DynamoDB
  const [bigQueryServiceAccountFile, setBigQueryServiceAccountFile] = useState<File | null>(null);
  const [bigQueryServiceAccountKey, setBigQueryServiceAccountKey] = useState<string>('');
  const [bigQueryServiceAccountKeyError, setBigQueryServiceAccountKeyError] = useState<string>('');
  const [testingConnection, setTestingConnection] = useState<boolean>(false);
  const [connectionTested, setConnectionTested] = useState<boolean>(false);
  const [vaultId, setVaultId] = useState<string>('');
  const [tables, setTables] = useState<string[]>([]);
  const [loadingTables, setLoadingTables] = useState<boolean>(false);
  const [tablesError, setTablesError] = useState<string>('');
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [openPreviewModal, setOpenPreviewModal] = useState<boolean>(false);
  const [tablePreview, setTablePreview] = useState<{
    [tableName: string]: {
      columns: string[];
      rows: any[][];
    };
  }>({});
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [previewTableName, setPreviewTableName] = useState<string>('');

  // Existing database connections state
  interface ExistingDbConnection {
    id: string;
    name: string;
    database_type: string;
    vault_id: string;
    config: any;
    tables: string[]; // tables already added for this connection
  }
  const [existingDbConnections, setExistingDbConnections] = useState<ExistingDbConnection[]>([]);
  const [loadingExistingConnections, setLoadingExistingConnections] = useState<boolean>(false);
  const [selectedExistingConnection, setSelectedExistingConnection] = useState<string>('');
  const [showNewConnectionForm, setShowNewConnectionForm] = useState<boolean>(false);
  const [originalSelectedTables, setOriginalSelectedTables] = useState<string[]>([]); // Track original tables for diff

  // API state
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelsError, setChannelsError] = useState<string>('');
  const [isCreatingDataSource, setIsCreatingDataSource] = useState(false);
  // Tracks whether the user confirmed "Add Data Source" — drives immediate visual feedback
  // before the fire-and-forget API resolves and the modal closes.
  const [datasourcesQueued, setDatasourcesQueued] = useState(false);

  // Reset queued state whenever the user leaves step 3 (e.g. clicks Back after confirming).
  // Without this, the "Add Data Source" button stays disabled/green if the user goes back
  // to change their selection and returns to the review screen in the same wizard session.
  useEffect(() => {
    if (fileSelectionStep !== 3) {
      setDatasourcesQueued(false);
    }
  }, [fileSelectionStep]);
  
  // Log types state
  // const [supportedLogTypes, setSupportedLogTypes] = useState<string[]>([]);
  // const [loadingLogTypes, setLoadingLogTypes] = useState(false);
  // const [logTypesError, setLogTypesError] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const gcpServiceAccountFileRef = useRef<HTMLInputElement>(null);
  const bigQueryServiceAccountFileRef = useRef<HTMLInputElement>(null);

  // Set channelId when provided
  useEffect(() => {
    if (channelId) {
      setSelectedWorkspace(channelId);
      setCloudWorkspace(channelId);
    }
  }, [channelId]);

  // Fetch datasource apps when Drives or Connect App tab is active
  useEffect(() => {
    if (tabValue === 3 || tabValue === 4) {
      // Only fetch if we have a channelId or selectedWorkspace
      if (channelId || selectedWorkspace) {
        fetchDatasourceApps();
      }
    }
  }, [tabValue, channelId, selectedWorkspace]);



  // Listen for Google Drive OAuth data from callback page.
  // Depends on selectedWorkspace so the listener uses the channel selected in the dropdown when opened from Admin Hub.
  useEffect(() => {
    const handleGoogleDriveOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'google_drive') {
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (googleDriveOAuthProcessedRef.current) return;
        googleDriveOAuthProcessedRef.current = true;
        try {
          setGoogleDriveOauthData(event.data.data);
          // Clear popup monitoring interval
          if (googleDrivePopupIntervalRef.current) {
            clearInterval(googleDrivePopupIntervalRef.current);
            googleDrivePopupIntervalRef.current = null;
          }
          // Clear loading state for Google Drive
          setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
          // Automatically create app account with OAuth data (uses channelId || selectedWorkspace)
          await handleGoogleDriveConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { googleDriveOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleGoogleDriveOAuthData);
    return () => window.removeEventListener('message', handleGoogleDriveOAuthData);
  }, [channelId, selectedWorkspace]);

  // Listen for Google Calendar OAuth data from callback page (depends on selectedWorkspace for Admin Hub flow)
  useEffect(() => {
    const handleGoogleCalendarOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'google_calendar') {
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (googleCalendarOAuthProcessedRef.current) return;
        googleCalendarOAuthProcessedRef.current = true;
        try {
          setGoogleCalendarOauthData(event.data.data);
          // Clear popup monitoring interval
          if (googleCalendarPopupIntervalRef.current) {
            clearInterval(googleCalendarPopupIntervalRef.current);
            googleCalendarPopupIntervalRef.current = null;
          }
          // Clear loading state for Google Calendar
          setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
          // Automatically create app account with OAuth data (uses channelId || selectedWorkspace)
          await handleGoogleCalendarConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { googleCalendarOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleGoogleCalendarOAuthData);
    return () => window.removeEventListener('message', handleGoogleCalendarOAuthData);
  }, [channelId, selectedWorkspace]);

  // Listen for Outlook Calendar OAuth data from callback page (depends on selectedWorkspace for Admin Hub flow)
  useEffect(() => {
    const handleOutlookCalendarOAuthData = (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'microsoft_outlook_calendar') {
        setOutlookCalendarOauthData(event.data.data);
        // Clear popup monitoring interval
        if (outlookCalendarPopupIntervalRef.current) {
          clearInterval(outlookCalendarPopupIntervalRef.current);
          outlookCalendarPopupIntervalRef.current = null;
        }
        // Clear loading state for Outlook Calendar
        setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
        // Automatically create app account with OAuth data (uses channelId || selectedWorkspace)
        handleOutlookCalendarConnection(event.data.data);
      }
    };

    window.addEventListener('message', handleOutlookCalendarOAuthData);
    return () => window.removeEventListener('message', handleOutlookCalendarOAuthData);
  }, [channelId, selectedWorkspace]);

  // Listen for SharePoint OAuth data from callback page (depends on selectedWorkspace for Admin Hub flow)
  useEffect(() => {
    const handleSharePointOAuthData = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'OAUTH_SUCCESS' && event.data.data.provider === 'sharepoint') {
        // Avoid processing the same OAuth result twice (e.g. duplicate postMessage or strict mode)
        if (sharePointOAuthProcessedRef.current) return;
        sharePointOAuthProcessedRef.current = true;
        try {
          setSharePointOauthData(event.data.data);
          // Clear popup monitoring interval
          if (sharePointPopupIntervalRef.current) {
            clearInterval(sharePointPopupIntervalRef.current);
            sharePointPopupIntervalRef.current = null;
          }
          // Clear loading state for SharePoint
          setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
          // Automatically create app account with OAuth data (uses channelId || selectedWorkspace)
          await handleSharePointConnection(event.data.data);
        } finally {
          // Allow future OAuth flows to be processed after a short delay
          setTimeout(() => { sharePointOAuthProcessedRef.current = false; }, 2000);
        }
      }
    };

    window.addEventListener('message', handleSharePointOAuthData);
    return () => window.removeEventListener('message', handleSharePointOAuthData);
  }, [channelId, selectedWorkspace]);

  // User cancelled Drive/SharePoint OAuth in popup (Cancel / decline consent) — clear loading without error page
  useEffect(() => {
    const handleDriveSharePointOAuthCancelled = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const d = event.data;
      if (!d || d.type !== 'OAUTH_CANCELLED') return;
      const key =
        d.provider === 'google_drive'
          ? 'google_drive'
          : d.provider === 'sharepoint'
            ? 'sharepoint'
            : null;
      if (!key) return;
      if (key === 'google_drive' && googleDrivePopupIntervalRef.current) {
        clearInterval(googleDrivePopupIntervalRef.current);
        googleDrivePopupIntervalRef.current = null;
      }
      if (key === 'sharepoint' && sharePointPopupIntervalRef.current) {
        clearInterval(sharePointPopupIntervalRef.current);
        sharePointPopupIntervalRef.current = null;
      }
      setConnectingApps((prev) => ({ ...prev, [key]: false }));
      const toastRef =
        key === 'google_drive'
          ? googleDriveOAuthCancelledToastShownRef
          : sharePointOAuthCancelledToastShownRef;
      if (!toastRef.current) {
        toastRef.current = true;
        showWarning('Authentication cancelled by user');
      }
    };
    window.addEventListener('message', handleDriveSharePointOAuthCancelled);
    return () => window.removeEventListener('message', handleDriveSharePointOAuthCancelled);
  }, [showWarning]);

  // Cleanup for Google Drive, Google Calendar, Outlook Calendar, and SharePoint popup intervals on unmount
  useEffect(() => {
    return () => {
      if (googleDrivePopupIntervalRef.current) {
        clearInterval(googleDrivePopupIntervalRef.current);
      }
      if (googleCalendarPopupIntervalRef.current) {
        clearInterval(googleCalendarPopupIntervalRef.current);
      }
      if (outlookCalendarPopupIntervalRef.current) {
        clearInterval(outlookCalendarPopupIntervalRef.current);
      }
      if (sharePointPopupIntervalRef.current) {
        clearInterval(sharePointPopupIntervalRef.current);
      }
    };
  }, []);

  // Smooth scroll to "Yes, I understand" checkbox when Google Drive or SharePoint is selected
  useEffect(() => {
    if ((selectedApp?.app_key === 'google_drive' || selectedApp?.app_key === 'sharepoint') && showFileBrowser && acceptanceCheckboxRef.current) {
      // Use setTimeout to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        acceptanceCheckboxRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedApp?.app_key, showFileBrowser]);

  // Reset "initial fetch done" when leaving step 2 or changing app so we auto-fetch once when re-entering
  useEffect(() => {
    if (fileSelectionStep < 2) {
      didInitialFetchForStep2Ref.current = false;
      sharePointFetchErrorRef.current = false;
    }
  }, [fileSelectionStep]);
  useEffect(() => {
    didInitialFetchForStep2Ref.current = false;
    sharePointFetchErrorRef.current = false;
  }, [selectedApp?.id]);
  // Reset SharePoint error ref when user changes site so they can retry
  useEffect(() => {
    sharePointFetchErrorRef.current = false;
  }, [selectedSiteId]);

  // Auto-fetch files once when first entering Browse Files step (step 2). For Google Drive, show Home first (no fetch when home).
  useEffect(() => {
    if (
      fileSelectionStep !== 2 ||
      !selectedApp ||
      !dataSourceInfoAccepted ||
      loadingFiles ||
      didInitialFetchForStep2Ref.current
    ) {
      return;
    }
    if (selectedApp.app_key === 'sharepoint' && sharePointFetchErrorRef.current) {
      didInitialFetchForStep2Ref.current = true;
      return;
    }
    didInitialFetchForStep2Ref.current = true;
    if (selectedApp.app_key === 'sharepoint') {
      // M-1: selectedSiteId is undefined on first entry so no drive_id is sent in this call.
      // The backend defaults to OneDrive root when drive_id is omitted — this is intentional
      // and matches the UX default (OneDrive shown first; user switches via the site dropdown).
      fetchFilesAndFolders(selectedApp, undefined);
      if (availableSites.length === 0 && !loadingSites) {
        fetchSharePointSites(selectedApp);
      }
    } else if (selectedApp.app_key === 'google_drive') {
      // Google Drive: default to Home view (3 options); no fetch until user selects My Drive / Shared drives / Shared with me
      if (googleDriveType === 'home') {
        return;
      }
      fetchFilesAndFolders(selectedApp, undefined);
    } else {
      fetchFilesAndFolders(selectedApp, undefined);
    }
  }, [fileSelectionStep, selectedApp, dataSourceInfoAccepted, loadingFiles, availableSites.length, loadingSites, googleDriveType]);

  // // Fetch supported log types on component mount
  // useEffect(() => {
  //   fetchSupportedLogTypes();
  // }, []);

  // // Fetch supported log types from API
  // const fetchSupportedLogTypes = async () => {
  //   try {
  //     setLoadingLogTypes(true);
  //     setLogTypesError('');
  //     const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

  //     const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.SUPPORTED_LOG_TYPES}`, {
  //       method: 'GET',
  //       headers: {
  //         'accept': 'application/json',
  //         ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  //       }
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const data = await response.json();
  //     setSupportedLogTypes(data);
  //   } catch (err) {
  //     setLogTypesError(err instanceof Error ? err.message : 'Failed to fetch log types');
  //     console.error('Error fetching log types:', err);
  //   } finally {
  //     setLoadingLogTypes(false);
  //   }
  // };

  // Fetch channels from API
  const fetchChannels = async () => {
    try {
      setLoadingChannels(true);
      setChannelsError('');
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST}?page=1&size=10`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ChannelsResponse = await response.json();
      setChannels(data.channels);
    } catch (err) {
      setChannelsError(err instanceof Error ? err.message : 'Failed to fetch channels');
      console.error('Error fetching channels:', err);
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const previousTab = tabValue;
    setTabValue(newValue);
    // Reset app selection and file browser when switching away from Drives/Connect App tabs
    // or when switching between Drives and Connect App tabs
    if (newValue !== 3 && newValue !== 4) {
      setSelectedApp(null);
      setShowFileBrowser(false);
      setSelectedFiles([]);
      setSelectedFolders([]);
      setFilesAndFolders([]);
      setCurrentFolderId(undefined);
      setFolderPath([{ id: undefined, name: 'Home' }]);
      setFileSelectionStep(0);
      setDataSourceInfoAccepted(false);
      setDriveStorageSelectionBlocked(false);
      setDriveStorageAvailableBaseline(null);
      driveQuotaFetchedSessionKeyRef.current = '';
    } else if ((previousTab === 3 && newValue === 4) || (previousTab === 4 && newValue === 3)) {
      // Reset when switching between Drives and Connect App tabs
      setSelectedApp(null);
      setShowFileBrowser(false);
      setSelectedFiles([]);
      setSelectedFolders([]);
      setFilesAndFolders([]);
      setCurrentFolderId(undefined);
      setFolderPath([{ id: undefined, name: 'Home' }]);
      setFileSelectionStep(0);
      setDataSourceInfoAccepted(false);
      setDriveStorageSelectionBlocked(false);
      setDriveStorageAvailableBaseline(null);
      driveQuotaFetchedSessionKeyRef.current = '';
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleSecretKeyVisibility = () => {
    setShowSecretKey(!showSecretKey);
  };

  const handleToggleAccessKeyIdVisibility = () => {
    setShowAccessKeyId(!showAccessKeyId);
  };

  // Helper function to download a file from a URL
  const downloadFileFromUrl = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.setAttribute('href', objectUrl);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      showError('Failed to download sample file. Please try again.');
    }
  };

  // Handler for downloading key-value pairs sample file
  const handleDownloadKeyValueSample = () => {
    downloadFileFromUrl('/docs/SampleFAQ.xlsx', 'SampleFAQ.xlsx');
  };

  // Handler for downloading text-to-SQL sample file
  const handleDownloadTextToSQLSample = () => {
    downloadFileFromUrl('/docs/samplefileTxt2Sql.xlsx', 'samplefileTxt2Sql.xlsx');
  };

  // Open file picker synchronously on click so browser allows it (user activation required)
  const handleClickUploadBox = () => {
    fileInputRef.current?.click();
  };

  // Match From Device: validate subscription before adding attachments / cloud DB / drive flows
  const ensureCanCreateAttachment = async (): Promise<boolean> => {
    const effectiveChannelId = channelId || selectedWorkspace;
    try {
      const validationData = await validateSubscription(
        effectiveChannelId ? { channelId: effectiveChannelId } : {}
      );
      if (validationData && !validationData.canCreateAttachment) {
        setOpenAttachmentRestrictionModal(true);
        return false;
      }
    } catch (error) {
      console.error('Error validating subscription for attachment:', error);
    }
    return true;
  };

  // Total byte size of current drive selection (direct files + files inside selected folders)
  const getDriveSelectedTotalBytes = (): number => {
    const filesSum = selectedFiles.reduce((s, f) => s + (Number(f.size) || 0), 0);
    const foldersSum = selectedFolders.reduce((acc, folder) => {
      const files = folderContents[folder.id] || [];
      return acc + files.reduce((t, cf: any) => t + (Number(cf.size) || 0), 0);
    }, 0);
    return filesSum + foldersSum;
  };

  // Compare selection total to one-time baseline from fetchCompanySubscriptionDetails (no per-click API)
  const checkBeforeAddDriveSelection = (additionalBytes: number): boolean => {
    if (tabValue !== 3 || !showFileBrowser || fileSelectionStep !== 2) return true;
    if (DISABLE_SUBSCRIPTION) return true;
    if (driveStorageAvailableBaseline === null) return true;
    const current = getDriveSelectedTotalBytes();
    if (current + additionalBytes <= driveStorageAvailableBaseline) {
      if (driveStorageSelectionBlocked) setDriveStorageSelectionBlocked(false);
      return true;
    }
    setDriveStorageSelectionBlocked(true);
    setOpenAttachmentRestrictionModal(true);
    return false;
  };

  // After deselecting drive items, clear checkbox lock when back within baseline quota
  const refreshDriveStorageBlockAfterDeselect = (newTotalBytes: number) => {
    if (DISABLE_SUBSCRIPTION) {
      setDriveStorageSelectionBlocked(false);
      return;
    }
    if (driveStorageAvailableBaseline === null || newTotalBytes <= driveStorageAvailableBaseline) {
      setDriveStorageSelectionBlocked(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Copy FileList to array immediately so it stays valid after any async work or input reset
    const filesArray = event.target.files ? Array.from(event.target.files) : [];
    if (filesArray.length === 0) return;

    // Validate subscription after user selected files (avoids async before fileInput.click which breaks user activation)
    const effectiveChannelId = channelId || selectedWorkspace;
    try {
      const validationData = await validateSubscription(
        effectiveChannelId ? { channelId: effectiveChannelId } : {}
      );
      if (validationData && !validationData.canCreateAttachment) {
        setOpenAttachmentRestrictionModal(true);
        event.target.value = '';
        return;
      }
    } catch (error) {
      console.error('Error validating subscription for attachment:', error);
      // On error, allow user to proceed (fail open)
    }

    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    filesArray.forEach((file) => {
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      showError(`${invalidFiles.length} file(s) exceed 100MB limit: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      const currentFiles = Array.isArray(dataSources) ? dataSources.filter(f => f instanceof File) as File[] : [];
      const newFiles = [...currentFiles, ...validFiles];
      onDataSourcesChange(newFiles);
      if (hasTouchedFile) setHasTouchedFile(false);
    }
    // Reset input after processing so same file(s) can be selected again
    event.target.value = '';
  };

  const handleGcpServiceAccountFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setGcpServiceAccountKeyError('Please upload a JSON file');
      if (gcpServiceAccountFileRef.current) {
        gcpServiceAccountFileRef.current.value = '';
      }
      return;
    }

    // Read file content
    try {
      const fileContent = await file.text();
      // Validate JSON format and extract project ID
      const jsonData = JSON.parse(fileContent);
      
      // Extract project_id from JSON
      const projectId = jsonData.project_id || '';
      if (!projectId) {
        setGcpServiceAccountKeyError('Service account JSON is missing project_id field');
        setGcpServiceAccountFile(null);
        setGcpServiceAccountKey('');
        if (gcpServiceAccountFileRef.current) {
          gcpServiceAccountFileRef.current.value = '';
        }
        return;
      }
      
      setGcpServiceAccountFile(file);
      setGcpServiceAccountKey(fileContent);
      setGcpProjectId(projectId);
      setGcpServiceAccountKeyError('');
    } catch (error) {
      setGcpServiceAccountKeyError('Invalid JSON file format');
      setGcpServiceAccountFile(null);
      setGcpServiceAccountKey('');
      setGcpProjectId('');
      if (gcpServiceAccountFileRef.current) {
        gcpServiceAccountFileRef.current.value = '';
      }
    }
  };

  const handleBigQueryServiceAccountFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setBigQueryServiceAccountKeyError('Please upload a JSON file');
      if (bigQueryServiceAccountFileRef.current) {
        bigQueryServiceAccountFileRef.current.value = '';
      }
      return;
    }

    // Read file content
    try {
      const fileContent = await file.text();
      // Validate JSON format
      const jsonData = JSON.parse(fileContent);
      
      // Validate that it's a valid service account JSON (check for common fields)
      if (!jsonData.type || jsonData.type !== 'service_account') {
        setBigQueryServiceAccountKeyError('Invalid service account JSON format');
        setBigQueryServiceAccountFile(null);
        setBigQueryServiceAccountKey('');
        if (bigQueryServiceAccountFileRef.current) {
          bigQueryServiceAccountFileRef.current.value = '';
        }
        return;
      }
      
      setBigQueryServiceAccountFile(file);
      setBigQueryServiceAccountKey(fileContent);
      setBigQueryServiceAccountKeyError('');
    } catch (error) {
      setBigQueryServiceAccountKeyError('Invalid JSON file format');
      setBigQueryServiceAccountFile(null);
      setBigQueryServiceAccountKey('');
      if (bigQueryServiceAccountFileRef.current) {
        bigQueryServiceAccountFileRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = (indexToRemove?: number) => {
    if (indexToRemove !== undefined) {
      // Remove specific file
      const currentFiles = Array.isArray(dataSources) ? dataSources.filter(f => f instanceof File) as File[] : [];
      const updatedFiles = currentFiles.filter((_, index) => index !== indexToRemove);
      onDataSourcesChange(updatedFiles);
    } else {
      // Remove all files
      onDataSourcesChange([]);
      setDeviceDescription('');
      setDeviceDescriptionError('');
    }
  };

  const handleWorkspaceDropdownOpen = () => {
    if (channels.length === 0 && !loadingChannels) {
      fetchChannels();
    }
  };

  // Fetch datasource apps from API (initial load)
  const fetchDatasourceApps = async (forceCategory?: string): Promise<AppWithConnectionStatus[]> => {
    try {
      setLoadingApps(true);
      setAppsError(null);
      setCurrentAppsPage(1);
      
      // Determine category based on active tab or use forced category
      // tabValue === 3 (Drives tab) -> 'Cloud Drive'
      // tabValue === 4 (Connect App tab) -> 'Datasource'
      const category = forceCategory || (tabValue === 3 ? 'Cloud Drive' : 'Datasource');
      
      // Use channelId if provided, otherwise use selectedWorkspace (for AdminHub flow)
      const effectiveChannelId = channelId || selectedWorkspace;
      
      const response = await appsService.getApps(1, 12, effectiveChannelId, category);
      // Filter apps by marketplace image type (gmail vs outlook)
      const filtered = (response.apps || []).filter((app: AppWithConnectionStatus) => shouldShowAppInMarketplace(app.app_key));
      setDatasourceApps(filtered);
      setTotalAppsPages(response.pages);
      setHasMoreApps(response.page < response.pages);
      return filtered;
    } catch (err) {
      setAppsError('Failed to load datasource apps. Please try again.');
      console.error('Error fetching datasource apps:', err);
      return [];
    } finally {
      setLoadingApps(false);
    }
  };

  // Load more apps when scrolling
  const loadMoreApps = async () => {
    if (loadingMoreApps || !hasMoreApps) return;
    
    try {
      setLoadingMoreApps(true);
      const nextPage = currentAppsPage + 1;
      
      // Determine category based on active tab
      // tabValue === 3 (Drives tab) -> 'Cloud Drive'
      // tabValue === 4 (Connect App tab) -> 'Datasource'
      const category = tabValue === 3 ? 'Cloud Drive' : 'Datasource';
      
      // Use channelId if provided, otherwise use selectedWorkspace (for AdminHub flow)
      const effectiveChannelId = channelId || selectedWorkspace;
      
      const response = await appsService.getApps(nextPage, 12, effectiveChannelId, category);
      // Filter apps by marketplace image type (gmail vs outlook)
      const filtered = (response.apps || []).filter((app: AppWithConnectionStatus) => shouldShowAppInMarketplace(app.app_key));
      setDatasourceApps(prev => [...prev, ...filtered]);
      setCurrentAppsPage(nextPage);
      setHasMoreApps(nextPage < response.pages);
    } catch (err) {
      console.error('Error loading more apps:', err);
    } finally {
      setLoadingMoreApps(false);
    }
  };

  // Handle scroll event for infinite scroll
  const handleAppsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    
    // Load more when user scrolls to 80% of the content
    if (scrollPercentage > 0.8 && hasMoreApps && !loadingMoreApps) {
      loadMoreApps();
    }
  };

  // Get current connection state from localStorage


  // Create app account using the API
  const createAppAccount = async (connectionSettings: any, currentConnectionState: any) => {
    try {
      // Use channelId if available (from channel context), otherwise use selectedWorkspace (from Admin Hub)
      const targetChannelId = channelId || selectedWorkspace;
      if (!targetChannelId) {
        throw new Error('Channel ID is required');
      }
      
      if (!currentConnectionState.appId) {
        throw new Error('App ID is required from connection state');
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const requestBody = {
        app_id: currentConnectionState.appId,
        channel_id: targetChannelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchDatasourceApps();
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (targetChannelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: targetChannelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          console.error('Error emitting socket event:', error);
        }
      }
      
      showSuccess('App connected successfully!');
      
    } catch (error) {
      showError('Failed to create app connection. Please try again.');
      throw error;
    }
  };

  // Helper function to get company ID from localStorage
  const getCompanyId = (): string => {
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        return userData.company_id || '';
      }
    } catch (error) {
      console.error('Error retrieving company ID from localStorage:', error);
    }
    return '';
  };

  // Handle Google Drive OAuth flow
  const handleGoogleDriveOAuth = (appId: string) => {
    try {
      // Reset so the message listener will process the upcoming OAuth callback
      googleDriveOAuthProcessedRef.current = false;
      googleDriveOAuthCancelledToastShownRef.current = false;
      // Set loading state for Google Drive
      setConnectingApps(prev => ({ ...prev, ['google_drive']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/googledrive/callback`;
      
      // Google Drive specific scopes
      const scope = [
        'https://www.googleapis.com/auth/drive.readonly',
        'https://www.googleapis.com/auth/userinfo.profile',
        'https://www.googleapis.com/auth/userinfo.email',
        'openid'
      ].join(' ');
      
      const responseType = 'code';
      const accessType = 'offline';
      const prompt = 'consent';
      
      // Use the Google Drive client ID
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GOOGLE_DRIVE_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `access_type=${accessType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(googleAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (googleDrivePopupIntervalRef.current) {
        clearInterval(googleDrivePopupIntervalRef.current);
      }
      
      // Monitor popup closure
      googleDrivePopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(googleDrivePopupIntervalRef.current!);
          googleDrivePopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Google Drive OAuth:', error);
      showError('Failed to initiate Google Drive connection');
      setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
    }
  };

  // PR #656 review (M2, Medium) — the app-account list can be eventually consistent right after
  // a successful OAuth connection create: a single read immediately afterward can still miss the
  // just-created account, which silently skipped the auto-select-and-browse step below while
  // still showing a "connected successfully" toast. Retry briefly (short, bounded delays) instead
  // of accepting the first response as final. Shared by both Drive and SharePoint's connection
  // handlers, which hit the identical gap.
  //
  // 2nd re-review fix (M2) — the previous version matched against app.connectionDetails.id from
  // fetchDatasourceApps(), which calls appsService.getApps(). That method's connectedAppsMap is
  // keyed by app_id (appsService.ts ~150-154, and searchApps() ~233-237, and AppsGrid's own
  // refresh-merge does the same) — it collapses EVERY connected account of the same catalog app
  // into a single connectionDetails slot. In a channel with two Drive accounts, only one survives
  // that projection; if it isn't the new one, this function could retry forever and still never
  // see newAccountId, even once the backend is fully consistent. That's a bug one layer below the
  // helper, in a service shared by AppsGrid/AppAccountManageModal/search — not something safe to
  // change here without risking those other consumers.
  //
  // Fixed by bypassing that projection entirely for this one lookup: appsService.getAppAccountsForChannel()
  // already exists (used elsewhere for exactly this reason, see its own doc comment) and returns
  // the raw, unprojected ConnectedAppAccount[] straight from GET /app-accounts?channel_id=...,
  // with no app_id collapsing at all. Match the exact record there, then attach it to the catalog
  // App fields (identical for every account of this app_key, so any entry in initialApps supplies
  // them) to build the final AppWithConnectionStatus — never depending on the shared service's
  // single-slot projection for this per-account identity check.
  const findConnectedAppWithRetry = async (
    initialApps: AppWithConnectionStatus[],
    appKey: string,
    accountId: string | undefined,
    targetChannelId: string,
    maxRetries: number = 3,
    delayMs: number = 500
  ): Promise<AppWithConnectionStatus | undefined> => {
    const catalogApp = initialApps.find(app => app.app_key === appKey);

    if (!accountId || !catalogApp) {
      // Defensive fallback for the case the backend genuinely returned no new-account id at all
      // (should not normally happen) — only safe when exactly one connected account of this
      // provider exists; with more than one, guessing which is the new one is exactly the bug
      // this fix removes, so it's better to leave the wizard where it is than pick wrong.
      const candidates = initialApps.filter(app => app.app_key === appKey && app.isConnected);
      return candidates.length === 1 ? candidates[0] : undefined;
    }

    let attempt = 0;
    while (attempt <= maxRetries) {
      try {
        const accounts = await appsService.getAppAccountsForChannel(targetChannelId);
        const match = accounts.find(acc => acc.id === accountId);
        if (match) {
          return { ...catalogApp, isConnected: true, connectionDetails: match };
        }
      } catch (e) {
        console.error('Failed to fetch app accounts while resolving new connection:', e);
      }
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
      attempt += 1;
    }
    return undefined;
  };

  // Handle Google Drive connection after OAuth success
  const handleGoogleDriveConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['google_drive']) {
      console.warn('Google Drive connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      // Use channelId if available (from channel context), otherwise use selectedWorkspace (from Admin Hub)
      const targetChannelId = channelId || selectedWorkspace;
      if (!targetChannelId) {
        showError('Channel ID is required');
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['google_drive']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        showError('No authentication token found');
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'google_drive',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        connection_method: 'oauth',
        oauth_provider: 'google',
        // Keep the OAuth client credentials with the connection. The token
        // exchange returns an encrypted secret specifically for persistence;
        // omitting these fields makes a newly-created Drive account unable to
        // refresh its access token after the app list is reloaded.
        client_id: oauthData.clientId || GOOGLE_DRIVE_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'Google Drive')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: targetChannelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      // PR #656 re-review (M2) — the create response returns the new ConnectedAppAccount record
      // itself; its own id (not the App catalog id) is the only reliable way to tell this
      // specific connection apart from any other Drive account already connected in the channel.
      const newAccountId: string | undefined = data?.id;

      // Refresh the apps list to show updated connection status
      // Google Drive is a Cloud Drive app, so always refresh with 'Cloud Drive' category
      const refreshedApps = await fetchDatasourceApps('Cloud Drive');

      // A successful OAuth connection is also the user's next action in this
      // wizard. Select the new Drive account automatically and open the notes
      // step so the user can continue without having to find the card again.
      const connectedDriveApp = await findConnectedAppWithRetry(
        refreshedApps, 'google_drive', newAccountId, targetChannelId
      );

      if (connectedDriveApp) {
        // The app-account list can briefly lag behind the successful OAuth
        // response. Keep the just-issued tokens authoritative for this wizard
        // session so the first Browse Files request cannot use stale account
        // data from the list response.
        const selectedDriveApp: AppWithConnectionStatus = {
          ...connectedDriveApp,
          connectionDetails: connectedDriveApp.connectionDetails
            ? {
                ...connectedDriveApp.connectionDetails,
                connection_settings: {
                  ...(connectedDriveApp.connectionDetails.connection_settings || {}),
                  ...connectionSettings
                }
              }
            : connectedDriveApp.connectionDetails
        };

        setSelectedApp(selectedDriveApp);
        setShowFileBrowser(true);
        setDataSourceInfoAccepted(false);
        setFileSelectionStep(1);
        setCurrentFolderId(undefined);
        setFolderPath([{ id: undefined, name: 'Home' }]);
        setGoogleDriveType('home');
        setGoogleSharedDriveId(undefined);
        setSelectedFiles([]);
        setFilesAndFolders([]);
        setFilesError(null);
        didInitialFetchForStep2Ref.current = false;
      }
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (targetChannelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: targetChannelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          console.error('Error emitting socket event:', error);
        }
      }
      
      // Clear OAuth data after successful connection
      setGoogleDriveOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
      
      // Bump refresh so Connect App tab fetches app-accounts for channel and merges into app list
      setRefreshTrigger(prev => prev + 1);
      
      // PR #656 review (L2, Low) — a flat success toast here was misleading whenever
      // connectedDriveApp came back empty (auto-select couldn't find the new account in time):
      // the connection itself did succeed, but the promised "connect and continue into browse"
      // didn't happen, and the toast gave no indication the user still needs to find the card
      // themselves. Distinguish the two outcomes instead of asserting the same message either way.
      showSuccess(
        connectedDriveApp
          ? 'Google Drive connected successfully!'
          : 'Google Drive connected successfully! Select it below to browse its files.'
      );

    } catch (error) {
      console.error('Error creating Google Drive connection:', error);
      showError('Failed to connect Google Drive. Please try again.');
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['google_drive']: false }));
    }
  };

  // Handle Google Calendar OAuth flow
  const handleGoogleCalendarOAuth = (appId: string) => {
    try {
      // Reset so the message listener will process the upcoming OAuth callback
      googleCalendarOAuthProcessedRef.current = false;
      // Set loading state for Google Calendar
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/googlecalendar/callback`;
      
      // Google Calendar specific scopes
      // https://www.googleapis.com/auth/calendar - Full read/write access to calendars and events
      // https://www.googleapis.com/auth/calendar.events - Read/write access to events only
      // https://www.googleapis.com/auth/calendar.readonly - Read-only access to calendars and events
      // https://www.googleapis.com/auth/calendar.events.readonly - Read-only access to events only
      const scope = [
        'https://www.googleapis.com/auth/calendar', // Full access: view, read, write, and modify calendars and events
        'https://www.googleapis.com/auth/userinfo.profile',
        'https://www.googleapis.com/auth/userinfo.email',
        'openid'
      ].join(' ');
      
      const responseType = 'code';
      const accessType = 'offline';
      const prompt = 'consent';
      
      // Use the Google Calendar client ID
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GOOGLE_CALENDAR_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `access_type=${accessType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(googleAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (googleCalendarPopupIntervalRef.current) {
        clearInterval(googleCalendarPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      googleCalendarPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(googleCalendarPopupIntervalRef.current!);
          googleCalendarPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Google Calendar OAuth:', error);
      showError('Failed to initiate Google Calendar connection');
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
    }
  };

  // Handle Outlook Calendar OAuth flow
  const handleOutlookCalendarOAuth = (appId: string) => {
    try {
      // Set loading state for Outlook Calendar
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/outlookcalendar/callback`;
      
      // Outlook Calendar specific scopes
      const scope = [
        'https://graph.microsoft.com/Calendars.Read',
        'https://graph.microsoft.com/Calendars.ReadWrite',
        'https://graph.microsoft.com/User.Read',
        'openid',
        'offline_access'
      ].join(' ');
      
      const responseType = 'code';
      const responseMode = 'query';
      
      // Use the Outlook Calendar client ID
      const outlookAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${OUTLOOK_CALENDAR_CLIENT_ID}&` +
        `response_type=${responseType}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_mode=${responseMode}&` +
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(outlookAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (outlookCalendarPopupIntervalRef.current) {
        clearInterval(outlookCalendarPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      outlookCalendarPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(outlookCalendarPopupIntervalRef.current!);
          outlookCalendarPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Outlook Calendar OAuth:', error);
      showError('Failed to initiate Outlook Calendar connection');
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
    }
  };

  // Handle Google Calendar connection after OAuth success
  const handleGoogleCalendarConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['google_calendar']) {
      console.warn('Google Calendar connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      // Use channelId if available (from channel context), otherwise use selectedWorkspace (from Admin Hub)
      const targetChannelId = channelId || selectedWorkspace;
      if (!targetChannelId) {
        showError('Channel ID is required');
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        showError('No authentication token found');
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'google_calendar',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        connection_method: 'oauth',
        oauth_provider: 'google',
        client_id: oauthData.clientId || GOOGLE_CALENDAR_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'Google Calendar')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: targetChannelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchDatasourceApps();
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (targetChannelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: targetChannelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          console.error('Error emitting socket event:', error);
        }
      }
      
      // Clear OAuth data after successful connection
      setGoogleCalendarOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
      // Show this app as connected immediately (optimistic) until app-accounts fetch completes
      if (optimisticClearTimeoutRef.current) clearTimeout(optimisticClearTimeoutRef.current);
      setOptimisticConnectedAppId(oauthData.app_id);
      optimisticClearTimeoutRef.current = setTimeout(() => { setOptimisticConnectedAppId(null); optimisticClearTimeoutRef.current = null; }, 2500);
      // Bump refresh so Connect App tab fetches app-accounts for channel and merges into app list
      setRefreshTrigger(prev => prev + 1);
      
      showSuccess('Google Calendar connected successfully!');
      
    } catch (error) {
      console.error('Error creating Google Calendar connection:', error);
      showError('Failed to connect Google Calendar. Please try again.');
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['google_calendar']: false }));
    }
  };

  // Handle Outlook Calendar connection after OAuth success
  const handleOutlookCalendarConnection = async (oauthData: any) => {
    try {
      // Use channelId if available (from channel context), otherwise use selectedWorkspace (from Admin Hub)
      const targetChannelId = channelId || selectedWorkspace;
      if (!targetChannelId) {
        showError('Channel ID is required');
        throw new Error('Channel ID is required');
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        showError('No authentication token found');
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'microsoft_outlook_calendar',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        connection_method: 'oauth',
        oauth_provider: 'microsoft',
        client_id: oauthData.clientId || OUTLOOK_CALENDAR_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'Outlook Calendar')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: targetChannelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      
      // Refresh the apps list to show updated connection status
      await fetchDatasourceApps();
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (channelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: channelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          console.error('Error emitting socket event:', error);
        }
      }
      
      // Clear OAuth data after successful connection
      setOutlookCalendarOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
      
      // Bump refresh so Connect App tab fetches app-accounts for channel and merges into app list
      setRefreshTrigger(prev => prev + 1);
      
      showSuccess('Outlook Calendar connected successfully!');
      
    } catch (error) {
      console.error('Error creating Outlook Calendar connection:', error);
      showError('Failed to connect Outlook Calendar. Please try again.');
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['microsoft_outlook_calendar']: false }));
    }
  };

  // Handle SharePoint OAuth flow
  const handleSharePointOAuth = (appId: string) => {
    try {
      // Reset so the message listener will process the upcoming OAuth callback
      sharePointOAuthProcessedRef.current = false;
      sharePointOAuthCancelledToastShownRef.current = false;
      // Set loading state for SharePoint
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: true }));
      
      // Dynamically determine redirectUri based on current domain
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/sharepoint/callback`;
      
      // SharePoint specific scopes
      const scope = [
        'https://graph.microsoft.com/Files.ReadWrite.All',
        'https://graph.microsoft.com/Sites.FullControl.All',
        'https://graph.microsoft.com/Sites.Manage.All',
        'https://graph.microsoft.com/SharePointCrossTenantMigration.Manage.All',
        'https://graph.microsoft.com/SharePointTenantSettings.ReadWrite.All',
        'https://graph.microsoft.com/User.Read',
        'https://graph.microsoft.com/Organization.Read.All',
        'offline_access'
      ].join(' ');
      
      const responseType = 'code';
      const responseMode = 'query';
      const accessType = 'offline';
      const prompt = 'consent';
      
      // Build Microsoft OAuth URL
      const microsoftAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${SHAREPOINT_CLIENT_ID}&` +
        `response_type=${responseType}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_mode=${responseMode}&` +
        `access_type=${accessType}&` +
        `prompt=${prompt}&` + 
        `state=${encodeURIComponent(appId)}`;
      
      // Open popup and monitor for closure
      const popup = window.open(microsoftAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
      
      // Clear any existing interval
      if (sharePointPopupIntervalRef.current) {
        clearInterval(sharePointPopupIntervalRef.current);
      }
      
      // Monitor popup closure
      sharePointPopupIntervalRef.current = setInterval(() => {
        if (popup?.closed) {
          clearInterval(sharePointPopupIntervalRef.current!);
          sharePointPopupIntervalRef.current = null;
          // Clear loading state if popup closed without success
          setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating SharePoint OAuth:', error);
      showError('Failed to initiate SharePoint connection');
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
    }
  };

  // Handle SharePoint connection after OAuth success
  const handleSharePointConnection = async (oauthData: any) => {
    // Prevent duplicate API calls - check if already connecting
    if (connectingApps['sharepoint']) {
      console.warn('SharePoint connection already in progress, skipping duplicate call');
      return;
    }
    
    try {
      // Use channelId if available (from channel context), otherwise use selectedWorkspace (from Admin Hub)
      const targetChannelId = channelId || selectedWorkspace;
      if (!targetChannelId) {
        showError('Channel ID is required');
        throw new Error('Channel ID is required');
      }
      
      // Set connecting state to prevent duplicate calls
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: true }));
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!token) {
        showError('No authentication token found');
        throw new Error('No authentication token found');
      }
      
      // Create connection settings in the format expected by backend
      const connectionSettings = {
        provider: 'sharepoint',
        access_token: oauthData.access_token,
        refresh_token: oauthData.refresh_token,
        expires_in: oauthData.expires_in,
        token_type: oauthData.token_type,
        scope: oauthData.scope,
        email_id: oauthData.email_id,
        tenant_id: oauthData.tenant_id,
        connection_method: 'oauth',
        oauth_provider: 'microsoft',
        client_id: oauthData.clientId || SHAREPOINT_CLIENT_ID,
        client_secret: getEncryptedClientSecret(oauthData, 'SharePoint')
      };
      
      const requestBody = {
        app_id: oauthData.app_id,
        channel_id: targetChannelId,
        connection_settings: connectionSettings
      };
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      // PR #656 re-review (M2) — see the identical comment in handleGoogleDriveConnection: the
      // create response's own id is the only reliable per-connection identity, never app.id.
      const newAccountId: string | undefined = data?.id;

      // Refresh the apps list to show updated connection status
      // SharePoint is a Cloud Drive app, so always refresh with 'Cloud Drive' category
      const refreshedApps = await fetchDatasourceApps('Cloud Drive');

      // A successful OAuth connection is the user's next action in this
      // wizard. Select SharePoint automatically and open the browse flow.
      const connectedSharePointApp = await findConnectedAppWithRetry(
        refreshedApps, 'sharepoint', newAccountId, targetChannelId
      );

      if (connectedSharePointApp) {
        const selectedSharePointApp: AppWithConnectionStatus = {
          ...connectedSharePointApp,
          connectionDetails: connectedSharePointApp.connectionDetails
            ? {
                ...connectedSharePointApp.connectionDetails,
                connection_settings: {
                  ...(connectedSharePointApp.connectionDetails.connection_settings || {}),
                  ...connectionSettings
                }
              }
            : connectedSharePointApp.connectionDetails
        };

        setSelectedApp(selectedSharePointApp);
        setShowFileBrowser(true);
        setDataSourceInfoAccepted(false);
        setFileSelectionStep(1);
        setCurrentFolderId(undefined);
        setFolderPath([{ id: undefined, name: 'Home' }]);
        setSelectedFiles([]);
        setFilesAndFolders([]);
        setFilesError(null);
        setSelectedSiteId('onedrive');
        setSelectedSiteUrl('');
        sharePointFetchErrorRef.current = false;
        didInitialFetchForStep2Ref.current = false;
      }
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (targetChannelId) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: targetChannelId
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          console.error('Error emitting socket event:', error);
        }
      }
      
      // Clear OAuth data after successful connection
      setSharePointOauthData(null);
      
      // Clear connecting state after successful connection
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
      // Show this app as connected immediately (optimistic) until app-accounts fetch completes
      if (optimisticClearTimeoutRef.current) clearTimeout(optimisticClearTimeoutRef.current);
      setOptimisticConnectedAppId(oauthData.app_id);
      optimisticClearTimeoutRef.current = setTimeout(() => { setOptimisticConnectedAppId(null); optimisticClearTimeoutRef.current = null; }, 2500);
      // Bump refresh so Connect App tab (AppsGrid) refetches and shows connected/SELECT
      setRefreshTrigger(prev => prev + 1);
      
      // PR #656 review (L2, Low) — see the identical comment in handleGoogleDriveConnection:
      // distinguish "connected and auto-opened" from "connected, but you'll need to select it
      // yourself" instead of asserting the same success message regardless of outcome.
      showSuccess(
        connectedSharePointApp
          ? 'SharePoint connected successfully!'
          : 'SharePoint connected successfully! Select it below to browse its files.'
      );

    } catch (error) {
      console.error('Error creating SharePoint connection:', error);
      showError('Failed to connect SharePoint. Please try again.');
      // Clear loading state on error
      setConnectingApps(prev => ({ ...prev, ['sharepoint']: false }));
    }
  };

  // Helper function to determine if an app is a "Drive" type app
  const isDriveApp = (app: AppWithConnectionStatus): boolean => isDriveAppKey(app.app_key);

  // Single subscription-details fetch when user reaches Drives "Browse files" (step 2); selections subtract from baseline locally
  useEffect(() => {
    const app = selectedApp;
    const appIsDrive = !!app && isDriveAppKey(app.app_key);
    const shouldLoadQuota =
      tabValue === 3 &&
      showFileBrowser &&
      fileSelectionStep === 2 &&
      appIsDrive &&
      !DISABLE_SUBSCRIPTION;
    if (!shouldLoadQuota || !app) {
      return;
    }
    const sessionKey = `${app.id}|step2`;
    if (driveQuotaFetchedSessionKeyRef.current === sessionKey) {
      return;
    }
    const companyId = getCompanyIdFromStorage();
    if (!companyId) {
      setDriveStorageAvailableBaseline(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchCompanySubscriptionDetails(companyId);
        if (cancelled) return;
        const stats = res.success && res.data?.usageStatistics ? res.data.usageStatistics : null;
        if (!stats) {
          setDriveStorageAvailableBaseline(null);
          return;
        }
        const r = stats as unknown as Record<string, unknown>;
        const limit = nonNegativeUsageStat(stats.StorageLimit ?? r.storageLimit);
        const remaining = nonNegativeUsageStat(stats.Storageavailable ?? r.storageAvailable);
        if (limit === 0) {
          setDriveStorageAvailableBaseline(null);
          return;
        }
        driveQuotaFetchedSessionKeyRef.current = sessionKey;
        setDriveStorageAvailableBaseline(remaining);
      } catch (e) {
        if (!cancelled) {
          console.error('Error fetching drive storage quota:', e);
          setDriveStorageAvailableBaseline(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, showFileBrowser, fileSelectionStep, selectedApp?.id, selectedApp?.app_key, DISABLE_SUBSCRIPTION]);

  // Handle app connection
  const handleConnect = async (appKey: string, appId: string) => {
    try {
      // Check if this is Google Drive - use OAuth
      if (appKey === 'google_drive') {
        handleGoogleDriveOAuth(appId);
        return; // Exit early for Google Drive
      }
      
      // Check if this is Google Calendar - use OAuth
      if (appKey === 'google_calendar') {
        handleGoogleCalendarOAuth(appId);
        return; // Exit early for Google Calendar
      }
      
      // Check if this is Outlook Calendar - use OAuth
      if (appKey === 'microsoft_outlook_calendar') {
        handleOutlookCalendarOAuth(appId);
        return; // Exit early for Outlook Calendar
      }
      
      // Check if this is SharePoint - use OAuth
      if (appKey === 'sharepoint') {
        handleSharePointOAuth(appId);
        return; // Exit early for SharePoint
      }
      
      // Check if this is Freshdesk - use basic auth (domain + API key)
      if (appKey === 'freshdesk') {
        const app = datasourceApps.find(a => a.app_key === appKey);
        setPendingBasicAuthConnection({ 
          appKey, 
          appId, 
          appName: app?.app_name || 'Freshdesk' 
        });
        setBasicAuthDialogOpen(true);
        return; // Exit early for Freshdesk
      }
      
      // For other apps, show an error message since third-party integration has been removed
      console.error(`App connection not supported: ${appKey}. Third-party integration has been removed.`);
      showError(`Connection for ${appKey} is not currently supported. Please contact support for assistance.`);
      return;
    } catch (error) {
      showError('Failed to initiate app connection. Please try again.');
      console.error('Error connecting app:', error);
    } finally {
      setConnectingApps(prev => ({ ...prev, [appKey]: false }));
    }
  };

  // Handle Basic Auth connection (for Freshdesk)
  const handleBasicAuthConnect = async (connectionData: BasicAuthConnectionData) => {
    if (!pendingBasicAuthConnection) {
      console.error('No pending basic auth connection found');
      return;
    }

    try {
      const { appKey, appId } = pendingBasicAuthConnection;
      const encryptedApiKey = await encryptSecretForStorage(connectionData.apiKey);

      // Build connection settings for basic auth
      // Freshdesk uses basic auth with domain and API key
      const connectionSettings = {
        domain: connectionData.domain,
        api_key: encryptedApiKey,
        auth_type: 'basic'
      };

      // Create app account with connection_settings
      await createAppAccount(connectionSettings, { appId });

      // Close the dialog
      setBasicAuthDialogOpen(false);
      setPendingBasicAuthConnection(null);

      // Clear loading state
      setConnectingApps(prev => ({ ...prev, [appKey]: false }));

    } catch (error) {
      console.error('Error creating basic auth connection:', error);
      showError('Failed to complete connection. Please check your credentials and try again.');
      
      // Clear loading state on error
      if (pendingBasicAuthConnection) {
        setConnectingApps(prev => ({ ...prev, [pendingBasicAuthConnection.appKey]: false }));
      }
    }
  };

  // Handle Basic Auth dialog close
  const handleBasicAuthDialogClose = () => {
    setBasicAuthDialogOpen(false);
    
    // Clear loading state if connection was cancelled
    if (pendingBasicAuthConnection) {
      setConnectingApps(prev => ({ ...prev, [pendingBasicAuthConnection.appKey]: false }));
      setPendingBasicAuthConnection(null);
    }
  };

  // Handle app selection (for already connected apps)
  const handleSelectApp = async (app: AppWithConnectionStatus) => {
    if (selectedApp?.id === app.id) {
      setSelectedApp(null);
      setShowFileBrowser(false);
      setSelectedFiles([]);
      setFilesAndFolders([]);
      setCurrentFolderId(undefined);
      setFolderPath([{ id: undefined, name: app.app_key === 'google_drive' ? 'Home' : 'Home' }]);
      setFileSelectionStep(0); // Reset step
      setDriveStorageSelectionBlocked(false);
      setDriveStorageAvailableBaseline(null);
      driveQuotaFetchedSessionKeyRef.current = '';
    } else {
      // Drives tab only: same subscription gate as From Device before opening drive flow
      if (tabValue === 3 && isDriveApp(app)) {
        const allowed = await ensureCanCreateAttachment();
        if (!allowed) return;
      }
      setDriveStorageSelectionBlocked(false);
      setDriveStorageAvailableBaseline(null);
      driveQuotaFetchedSessionKeyRef.current = '';
      setSelectedApp(app);
      // For drive apps (Google Drive, Dropbox, OneDrive, SharePoint), show file browser
      if (isDriveApp(app)) {
        setShowFileBrowser(true);
        setDataSourceInfoAccepted(false); // Reset acceptance when app changes
        setFileSelectionStep(1); // Start at step 1 (Review Notes)
        setCurrentFolderId(undefined);
        setFolderPath([{ id: undefined, name: 'Home' }]);
        if (app.app_key === 'google_drive') setGoogleDriveType('home');
        setSelectedFiles([]);
        setFilesAndFolders([]);
        // Reset SharePoint site selection when switching apps
        if (app.app_key === 'sharepoint') {
          setSelectedSiteId(null);
          setSelectedSiteName(null);
          setSelectedSiteUrl(null);
          setAvailableSites([]);
        }
      } else {
        setShowFileBrowser(false);
        setDataSourceInfoAccepted(false);
        setSelectedFiles([]);
        setFilesAndFolders([]);
      }
    }
  };

  // Optional drive context override (used for breadcrumb navigation so request uses correct context immediately)
  type DriveContext = { driveType: 'myDrive' | 'sharedWithMe' | 'sharedDrives'; sharedDriveId?: string; searchQuery?: string };

  // Fetch files and folders from drive apps (Google Drive, SharePoint) using server API
  const fetchFilesAndFolders = async (app: AppWithConnectionStatus, folderId?: string, driveContext?: DriveContext, siteIdOverride?: string) => {
    if (!app.connectionDetails) return;
    // Determine app type at function scope so catch block can set SharePoint error ref
    const isSharePoint = app.app_key === 'sharepoint';
    const isGoogleDrive = app.app_key === 'google_drive';

    try {
      setLoadingFiles(true);
      setFilesError(null);

      // ── SharePoint: mirror the Google Drive token-reading pattern ──
      // Read tokens from connection_settings (support both camelCase from old OAuth and snake_case)
      // then ensure the backend DB has the snake_case versions before calling the new Python API.
      if (isSharePoint) {
        try {
          const appAccountId = app.connectionDetails.id;
          const authToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

          // Read tokens — same dual-case lookup as Google Drive (lines below match GD pattern exactly)
          const cs = app.connectionDetails.connection_settings as any;
          const refreshToken = cs?.refresh_token ?? cs?.refreshToken;
          let accessToken  = cs?.access_token  ?? cs?.accessToken;
          const tenantId     = cs?.tenant_id     ?? cs?.tenantId;
          const clientId     = cs?.client_id     ?? cs?.clientId;
          const clientSecret = cs?.client_secret ?? cs?.clientSecret;

          const refreshSharePointToken = async (): Promise<boolean> => {
            if (!refreshToken) return false;

            const nodeHeaders = await getFreshNodeApiHeaders();
            if (!nodeHeaders.Authorization) return false;

            const refreshResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/refreshToken`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...nodeHeaders,
              },
              body: JSON.stringify({
                refresh_token: refreshToken,
                redirectUri: `${window.location.origin}/auth/sharepoint/callback`,
                client_id: clientId,
                client_secret: clientSecret
              })
            });

            if (!refreshResponse.ok) return false;

            const tokenData = await refreshResponse.json();
            accessToken = tokenData.access_token;
            if (!accessToken || !authToken) return false;

            const updatedConnectionSettings = {
              ...cs,
              access_token: accessToken,
              ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
              ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
            };

            const persistResponse = await fetch(
              `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${appAccountId}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'accept': 'application/json',
                  'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ connection_settings: updatedConnectionSettings })
              }
            );

            if (!persistResponse.ok) return false;

            app.connectionDetails!.connection_settings = updatedConnectionSettings;
            return true;
          };

          // Populate an access token before the first SharePoint request when
          // the account only contains its long-lived refresh credential.
          if (!accessToken && refreshToken && !(await refreshSharePointToken())) {
            setFilesError('Your SharePoint permission needs to be renewed. Please reconnect SharePoint and try again.');
            sharePointFetchErrorRef.current = true;
            return;
          }

          // If snake_case keys are absent (tokens stored only as camelCase by old OAuth flow),
          // await the PUT so backend has tokens before the GET fires — same as GD token PUT on refresh.
          // W-2: PUT is awaited and its result is checked; failure surfaces as a user-facing error
          // so the user is not left seeing a cryptic 400 from the subsequent GET.
          if (authToken && (refreshToken || accessToken) && (!cs?.refresh_token || !cs?.access_token)) {
            const normalized = {
              ...cs,
              ...(refreshToken ? { refresh_token: refreshToken } : {}),
              ...(accessToken  ? { access_token:  accessToken  } : {}),
              ...(tenantId     ? { tenant_id:     tenantId     } : {}),
            };
            const syncRes = await fetch(
              `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${appAccountId}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify({ connection_settings: normalized }),
              }
            ).catch(() => null);

            // M-2: distinguish network failure (syncRes===null) from a server-side credential error
            // so users are not told to reconnect when the issue is just a transient network blip.
            if (syncRes === null) {
              setFilesError('Network error while preparing SharePoint credentials. Please try again.');
              sharePointFetchErrorRef.current = true;
              return;
            }
            if (!syncRes.ok) {
              setFilesError('Failed to prepare SharePoint credentials. Please reconnect your SharePoint account.');
              sharePointFetchErrorRef.current = true;
              return;
            }
          }

          // Build query params
          const params = new URLSearchParams({ app_account_id: appAccountId });
          if (folderId) params.set('folder_id', folderId);
          // M-2: siteIdOverride exists because React state batching means setSelectedSiteId(newId)
          // is not visible in the same synchronous call that immediately invokes fetchFilesAndFolders.
          // The site-selector onChange passes the new value directly so the correct drive_id is sent
          // without waiting for the next render cycle. selectedSiteId is used for all other callers
          // where state is already up-to-date.
          const effectiveSiteId = siteIdOverride ?? selectedSiteId;
          if (effectiveSiteId && effectiveSiteId !== 'onedrive') params.set('drive_id', effectiveSiteId);

          const spRes = await fetch(
            `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.SHAREPOINT.FILES}?${params}`,
            {
              method: 'GET',
              headers: {
                'accept': 'application/json',
                ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
              },
            }
          );

          if (!spRes.ok) {
            if (spRes.status === 401 && refreshToken && await refreshSharePointToken()) {
              const retryParams = new URLSearchParams({ app_account_id: appAccountId });
              if (folderId) retryParams.set('folder_id', folderId);
              if (effectiveSiteId && effectiveSiteId !== 'onedrive') retryParams.set('drive_id', effectiveSiteId);

              const retryResponse = await fetch(
                `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.SHAREPOINT.FILES}?${retryParams}`,
                {
                  method: 'GET',
                  headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                  },
                }
              );

              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                if (Array.isArray(retryData.files)) {
                  setFilesAndFolders(retryData.files.map((f: any) => ({
                    id: f.id,
                    name: f.name,
                    size: f.size,
                    mimeType: f.mime_type,
                    isFolder: f.is_folder,
                    webViewLink: f.web_url,
                    downloadUrl: f.download_url,
                    lastModified: f.last_modified,
                    fileExtension: !f.is_folder && f.name?.includes('.') ? f.name.split('.').pop()?.toLowerCase() : undefined,
                  })));
                  sharePointFetchErrorRef.current = false;
                  return;
                }
              }
            }

            const errData = await spRes.json().catch(() => ({}));
            if (spRes.status === 400) {
              setFilesError('SharePoint credentials are missing. Please reconnect your SharePoint account.');
            } else if (spRes.status === 403) {
              setFilesError('Access denied. Please check your SharePoint permissions and try again.');
            } else if (spRes.status === 401) {
              setFilesError('Your SharePoint permission needs to be renewed. Please reconnect SharePoint and try again.');
            } else {
              // M-1: backend may return detail, message, or error field — check all three
              setFilesError(errData.detail || errData.message || errData.error || 'Failed to fetch SharePoint files. Please try again.');
            }
            sharePointFetchErrorRef.current = true;
            return;
          }

          const spData = await spRes.json();
          if (Array.isArray(spData.files)) {
            setFilesAndFolders(spData.files.map((f: any) => ({
              id: f.id,
              name: f.name,
              size: f.size,
              mimeType: f.mime_type,
              isFolder: f.is_folder,
              webViewLink: f.web_url,
              downloadUrl: f.download_url,
              lastModified: f.last_modified,
              // Extract extension from filename so isFileSelectable can match against ALLOWED_FILE_FORMATS
              fileExtension: !f.is_folder && f.name?.includes('.') ? f.name.split('.').pop()?.toLowerCase() : undefined,
            })));
            sharePointFetchErrorRef.current = false;
          } else {
            setFilesError('Failed to fetch SharePoint files.');
            sharePointFetchErrorRef.current = true;
          }
        } catch (spErr) {
          console.error('Error fetching SharePoint files:', spErr);
          setFilesError('Failed to fetch SharePoint files. Please try again.');
          sharePointFetchErrorRef.current = true;
        }
        return; // outer finally runs → setLoadingFiles(false)
      }

      // Get app-specific details (Google Drive and other apps)
      const appName = isGoogleDrive ? 'Google Drive' : app.app_name;

      // Get tokens from connection settings (support snake_case and camelCase)
      const connectionSettings = app.connectionDetails.connection_settings as any;
      let accessToken = connectionSettings?.access_token ?? connectionSettings?.accessToken;
      const refreshToken = connectionSettings?.refresh_token ?? connectionSettings?.refreshToken;
      const clientId = connectionSettings?.client_id ?? connectionSettings?.clientId;
      const clientSecret = connectionSettings?.client_secret ?? connectionSettings?.clientSecret;

      if (!accessToken && !refreshToken) {
        setFilesError(
          isGoogleDrive
            ? 'Google Drive is connected, but its access permission is unavailable. Please reconnect Google Drive and try again.'
            : `Your ${appName} connection is missing access permission. Please reconnect ${appName} and try again.`
        );
        return;
      }

      // Determine API endpoint based on app type
      const filesEndpoint = isSharePoint 
        ? `${API_CONFIG.NODE_BASE_URL}/rest/office365/sharepoint/files`
        : `${API_CONFIG.NODE_BASE_URL}/rest/google/drive/files`;
      let nodeApiHeaders = await getFreshNodeApiHeaders();

      if (!nodeApiHeaders.Authorization) {
        setFilesError('Your session has expired. Please sign in again and retry.');
        return;
      }
      
      // Use drive context override when provided (e.g. breadcrumb), else state
      const driveType = driveContext?.driveType ?? googleDriveType;
      const sharedDriveId = driveContext?.sharedDriveId !== undefined ? driveContext.sharedDriveId : googleSharedDriveId;
      const searchQuery = driveContext?.searchQuery !== undefined ? driveContext.searchQuery : driveSearchQuery;

      // A connection can be returned from the account API before its access
      // token has been populated (or after it has been removed by token
      // rotation). Refresh before making a request that would otherwise send
      // an empty token and produce a misleading 401.
      if (!accessToken && refreshToken) {
        const refreshResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/refreshToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
            redirectUri: `${window.location.origin}/auth/googledrive/callback`,
            client_id: clientId,
            client_secret: clientSecret
          })
        });

        if (refreshResponse.ok) {
          const tokenData = await refreshResponse.json();
          accessToken = tokenData.access_token;

          // Keep the refreshed token on the connection so the next browse
          // action does not repeat the same recovery flow.
          const authToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
          if (authToken && accessToken) {
            fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${app.connectionDetails.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
                'accept': 'application/json'
              },
              body: JSON.stringify({
                connection_settings: {
                  ...connectionSettings,
                  access_token: accessToken,
                  ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
                  ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
                }
              })
            }).catch(err => console.error('Failed to save refreshed Drive token:', err));
          }
        }

        if (!accessToken) {
          setFilesError('Your Google Drive permission needs to be renewed. Please reconnect Google Drive and try again.');
          return;
        }
      }

      // Prepare request body (SharePoint: include search_query; Google Drive: drive type, shared drive id, search)
      const sharePointSearch = driveContext?.searchQuery !== undefined ? driveContext.searchQuery : sharePointSearchQuery;
      const requestBody = isSharePoint
        ? {
            access_token: accessToken,
            tenant_id: connectionSettings?.tenant_id ?? connectionSettings?.tenantId,
            site_id: selectedSiteId || 'onedrive',
            folder_id: folderId,
            search_query: (typeof sharePointSearch === 'string' ? sharePointSearch.trim() : '') || undefined
          }
        : {
            access_token: accessToken,
            folder_id: folderId,
            drive_type: driveType,
            shared_drive_id: sharedDriveId || undefined,
            search_query: (typeof searchQuery === 'string' ? searchQuery.trim() : '') || undefined
          };
      
      // First attempt: Try to fetch files with current access token
      let response = await fetch(filesEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...nodeApiHeaders,
        },
        body: JSON.stringify(requestBody)
      });
      
      // A 401 can be caused by the Shay session token (the Node middleware
      // rejects the request before Google is contacted) or by the Drive token.
      // Renew the Shay token first, then only use the Drive refresh flow if the
      // retried request is still unauthorized.
      if (response.status === 401) {
        const newNodeAccessToken = await refreshAccessToken();
        if (!newNodeAccessToken) {
          setFilesError('Your session has expired. Please sign in again and retry.');
          return;
        }

        nodeApiHeaders = { Authorization: `Bearer ${newNodeAccessToken}` };
        response = await fetch(filesEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify(requestBody)
        });
      }

      // If the Drive token is expired (401), refresh it and retry.
      if (response.status === 401 && refreshToken) {
        // Determine refresh token endpoint based on app type
        const refreshEndpoint = isSharePoint
          ? `${API_CONFIG.NODE_BASE_URL}/rest/office365/refreshToken`
          : `${API_CONFIG.NODE_BASE_URL}/rest/google/refreshToken`;
        
        // Prepare refresh request body
        const refreshRequestBody = isSharePoint
          ? {
              refresh_token: refreshToken,
              redirectUri: `${window.location.origin}/auth/sharepoint/callback`
            }
          : {
              refresh_token: refreshToken,
              redirectUri: `${window.location.origin}/auth/googledrive/callback`,
              client_id: clientId,
              client_secret: clientSecret
            };
        
        // Refresh the access token
        const refreshResponse = await fetch(refreshEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify(refreshRequestBody)
        });
        
        if (refreshResponse.ok) {
          const tokenData = await refreshResponse.json();
          accessToken = tokenData.access_token;
          
          // Update the access token in backend (fire and forget)
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
          if (token) {
            // Merge new access token with existing connection settings
            const updatedConnectionSettings = {
              ...connectionSettings,
              access_token: accessToken,
              ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
              ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
            };
            
            fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${app.connectionDetails.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'accept': 'application/json'
              },
              body: JSON.stringify({
                connection_settings: updatedConnectionSettings
              })
            }).catch(err => console.error('Failed to update token in backend:', err));
          }
          
          // Update local state
          if (selectedApp) {
            setSelectedApp({
              ...selectedApp,
              connectionDetails: {
                ...selectedApp.connectionDetails!,
                connection_settings: {
                  ...connectionSettings,
                  access_token: accessToken,
                  ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
                  ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
                }
              }
            });
          }
          
          // Retry with new access token (include search for both)
          const retryRequestBody = isSharePoint
            ? {
                access_token: accessToken,
                tenant_id: connectionSettings.tenant_id,
                site_id: selectedSiteId || 'onedrive',
                folder_id: folderId,
                search_query: (typeof sharePointSearch === 'string' ? sharePointSearch.trim() : '') || undefined
              }
            : {
                access_token: accessToken,
                folder_id: folderId,
                drive_type: driveType,
                shared_drive_id: sharedDriveId || undefined,
                search_query: (typeof searchQuery === 'string' ? searchQuery.trim() : '') || undefined
              };
          
          response = await fetch(filesEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...nodeApiHeaders,
            },
            body: JSON.stringify(retryRequestBody)
          });
        } else {
          setFilesError(
            isGoogleDrive
              ? 'Your Google Drive permission needs to be renewed. Please reconnect Google Drive and try again.'
              : `Your ${appName} permission needs to be renewed. Please reconnect ${appName} and try again.`
          );
          return;
        }
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const message = response.status === 401
          ? (!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
              ? 'Your session has expired. Please sign in again and retry.'
              : isGoogleDrive
                ? 'Google Drive could not verify your access. Please reconnect Google Drive and try again.'
                : `We could not verify your ${appName} session. Please reconnect and try again.`)
          : response.status === 403
            ? `You do not have permission to view these ${appName} files.`
            : errorData.message || `We could not load your ${appName} files. Please try again.`;
        setFilesError(message);
        if (isSharePoint) sharePointFetchErrorRef.current = true;
        return;
      }
      
      const data = await response.json();
      
      if (data.success && data.data?.files) {
        setFilesAndFolders(data.data.files);
        if (isSharePoint) sharePointFetchErrorRef.current = false;
      } else {
        setFilesError('Failed to fetch files and folders');
        if (isSharePoint) sharePointFetchErrorRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setFilesError(
        isGoogleDrive
          ? 'We could not load your Google Drive files. Please try again.'
          : 'We could not load your files. Please try again.'
      );
      if (isSharePoint) sharePointFetchErrorRef.current = true;
    } finally {
      setLoadingFiles(false);
    }
  };

  // Root label for current Google Drive type (for breadcrumb)
  const getGoogleDriveRootLabel = () => {
    if (googleDriveType === 'home') return 'Home';
    if (googleDriveType === 'sharedWithMe') return 'Shared with me';
    if (googleDriveType === 'sharedDrives') return 'Shared drives';
    return 'My Drive';
  };

  // Navigate to Home view (show 3 options: My Drive, Shared drives, Shared with me)
  const handleGoToHome = () => {
    setGoogleDriveType('home');
    setGoogleSharedDriveId(undefined);
    setCurrentFolderId(undefined);
    setFolderPath([{ id: undefined, name: 'Home' }]);
    setFilesAndFolders([]);
    setFilesError(null);
  };

  // Switch Google Drive view from Home (user selected My Drive / Shared drives / Shared with me)
  const handleDriveTypeSelect = async (newType: 'myDrive' | 'sharedWithMe' | 'sharedDrives') => {
    const rootName = newType === 'sharedWithMe' ? 'Shared with me' : newType === 'sharedDrives' ? 'Shared drives' : 'My Drive';
    setGoogleDriveType(newType);
    setGoogleSharedDriveId(undefined);
    setCurrentFolderId(undefined);
    setFolderPath([{ id: undefined, name: 'Home' }, { id: undefined, name: rootName }]);
    if (selectedApp) {
      await fetchFilesAndFolders(selectedApp, undefined, { driveType: newType, sharedDriveId: undefined, searchQuery: driveSearchQuery });
    }
  };

  // Handle folder navigation (including entering a shared drive from Shared drives list)
  const handleFolderClick = async (folder: any) => {
    if (!selectedApp) return;
    const isGoogleDrive = selectedApp.app_key === 'google_drive';
    const isSharedDriveEntry = isGoogleDrive && folder.isSharedDrive;
    if (isSharedDriveEntry) {
      setGoogleSharedDriveId(folder.id);
      setCurrentFolderId(undefined);
      setFolderPath(prev => [...prev, { id: folder.id, name: folder.name }]);
      await fetchFilesAndFolders(selectedApp, undefined, { driveType: 'sharedDrives', sharedDriveId: folder.id, searchQuery: driveSearchQuery });
      return;
    }
    setCurrentFolderId(folder.id);
    setFolderPath(prev => [...prev, { id: folder.id, name: folder.name }]);
    await fetchFilesAndFolders(selectedApp, folder.id);
  };

  // Handle folder selection with validation
  const handleFolderSelect = async (folder: any) => {
    if (!selectedApp) return;
    
    // Check if folder is already selected
    const isAlreadySelected = selectedFolders.some(f => f.id === folder.id);
    if (isAlreadySelected) {
      // Deselect folder; free quota so user can select again after deselect
      const removedFolderBytes = (folderContents[folder.id] || []).reduce((t, cf: any) => t + (Number(cf.size) || 0), 0);
      const totalAfterDeselect = getDriveSelectedTotalBytes() - removedFolderBytes;
      setSelectedFolders(prev => prev.filter(f => f.id !== folder.id));
      setFolderContents(prev => {
        const updated = { ...prev };
        delete updated[folder.id];
        return updated;
      });
      setFolderValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[folder.id];
        return updated;
      });
      setSelectionOrder(prev => prev.filter(item => !(item.type === 'folder' && item.id === folder.id)));
      refreshDriveStorageBlockAfterDeselect(totalAfterDeselect);
      return;
    }
    
    // Calculate current total files (individual files + files in already selected folders)
    const currentTotalFiles = selectedFiles.length + 
      Object.values(folderContents).flat().length;
    
    if (currentTotalFiles >= DATA_SOURCE_LIMIT) {
      showError(`Maximum ${DATA_SOURCE_LIMIT} files can be selected`);
      return;
    }
    
    // Start loading
    setLoadingFolderContents(prev => ({ ...prev, [folder.id]: true }));
    
    try {
      // Validate folder contents
      const validation = await validateFolderContents(folder, selectedApp);
      
      if (!validation.isValid) {
        // Show validation errors
        const errorMessage = validation.errors.join('\n• ');
        showError(`Cannot select folder "${folder.name}":\n• ${errorMessage}`);
        setFolderValidationErrors(prev => ({ ...prev, [folder.id]: errorMessage }));
        return;
      }
      
      // Check if adding this folder would exceed the limit
      const newTotalFiles = currentTotalFiles + validation.validFiles.length;
      if (newTotalFiles > DATA_SOURCE_LIMIT) {
        showError(`Adding this folder would exceed the ${DATA_SOURCE_LIMIT} file limit. Current: ${currentTotalFiles}, Folder has: ${validation.validFiles.length}`);
        return;
      }

      const folderBytes = validation.validFiles.reduce((sum, f: any) => sum + (Number(f.size) || 0), 0);
      const withinStorage = checkBeforeAddDriveSelection(folderBytes);
      if (!withinStorage) return;
      
      // Check if this is the first item being selected (before adding)
      const isFirstSelection = selectedFiles.length === 0 && selectedFolders.length === 0;
      
      // Add folder to selection
      setSelectedFolders(prev => [...prev, folder]);
      setFolderContents(prev => ({ ...prev, [folder.id]: validation.validFiles }));
      setSelectionOrder(prev => [...prev, { type: 'folder', id: folder.id }]);
      
      showSuccess(`Folder "${folder.name}" selected with ${validation.validFiles.length} valid file${validation.validFiles.length > 1 ? 's' : ''}`);
      
      // Smooth scroll to selected items section only if this is the first selection
      if (isFirstSelection) {
        setTimeout(() => {
          selectedItemsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
      
    } catch (error) {
      showError(`Failed to validate folder "${folder.name}"`);
      console.error('Error validating folder:', error);
    } finally {
      setLoadingFolderContents(prev => ({ ...prev, [folder.id]: false }));
    }
  };

  // Handle breadcrumb navigation (Home goes to home view; My Drive / Shared with me / Shared drives fetch)
  const handleBreadcrumbClick = async (index: number) => {
    if (!selectedApp) return;
    const isGoogleDrive = selectedApp.app_key === 'google_drive';
    const newPath = folderPath.slice(0, index + 1);
    const targetFolder = newPath[newPath.length - 1];
    // Clicking Home (index 0) for Google Drive: show home view with 3 options, no fetch
    if (isGoogleDrive && index === 0) {
      handleGoToHome();
      return;
    }
    let effectiveFolderId: string | undefined = targetFolder.id;
    let effectiveSharedDriveId: string | undefined = googleSharedDriveId;
    if (index === 0) {
      effectiveFolderId = undefined;
      if (isGoogleDrive && googleDriveType === 'sharedDrives') effectiveSharedDriveId = undefined;
      setGoogleSharedDriveId(effectiveSharedDriveId);
      setCurrentFolderId(undefined);
    } else if (isGoogleDrive && googleDriveType === 'sharedDrives' && index === 1) {
      effectiveSharedDriveId = targetFolder.id;
      effectiveFolderId = undefined;
      setGoogleSharedDriveId(effectiveSharedDriveId);
      setCurrentFolderId(undefined);
    } else {
      setCurrentFolderId(targetFolder.id);
    }
    setFolderPath(newPath);
    const ctx: DriveContext | undefined = isGoogleDrive && googleDriveType !== 'home'
      ? { driveType: googleDriveType, sharedDriveId: effectiveSharedDriveId, searchQuery: driveSearchQuery }
      : undefined;
    await fetchFilesAndFolders(selectedApp, effectiveFolderId, ctx);
  };

  // Get allowed file formats for display
  const getAllowedFileFormatsForDisplay = (): string => {
    // Display user-friendly format names
    const displayFormats = [
      'CSV', 'XLSX', 'XLS', 'JSON',
      'PDF', 'DOCX', 'DOC', 'TXT',
      'PPTX', 'PPT', 'MD',
      'Google Docs', 'Google Sheets', 'Google Slides'
    ];
    return displayFormats.join(', ');
  };

  // Handle checkbox acceptance
  // Step navigation handlers for Google Drive flow
  // Step 0: Select App (already shown in grid, considered complete when app is selected)
  // Step 1: Review Notes
  // Step 2: Browse Files
  // Step 3: Review Selection
  const fileSelectionSteps = ['Select App', 'Review Notes', 'Browse Files', 'Review Selection'];
  
  // Calculate active step for stepper display (0 = Select App, 1 = Review Notes, etc.)
  const getActiveStepperStep = () => {
    if (!selectedApp) return 0;
    return fileSelectionStep;
  };
  
  const handleNextStep = () => {
    if (fileSelectionStep === 1 && !dataSourceInfoAccepted) {
      showError('Please accept the terms to continue');
      return;
    }
    if (fileSelectionStep === 2 && selectedFiles.length === 0 && selectedFolders.length === 0) {
      showError('Please select at least one file or folder to continue');
      return;
    }
    setFileSelectionStep(prev => Math.min(prev + 1, fileSelectionSteps.length - 1));
  };
  
  const handleBackStep = () => {
    setFileSelectionStep(prev => Math.max(prev - 1, 1)); // Don't go back to step 0 (app selection)
  };
  
  // Fetch SharePoint sites. Returns true if sites were loaded so caller can decide whether to fetch files.
  const fetchSharePointSites = async (app: AppWithConnectionStatus): Promise<boolean> => {
    if (!app.connectionDetails) return false;

    setLoadingSites(true);
    setSitesError(null);

    const connectionSettings = app.connectionDetails.connection_settings as any;
    // Support both snake_case and camelCase from API
    let accessToken = connectionSettings?.access_token ?? connectionSettings?.accessToken;
    const refreshToken = connectionSettings?.refresh_token ?? connectionSettings?.refreshToken;
    const tenantId = connectionSettings?.tenant_id ?? connectionSettings?.tenantId;
    const clientId = connectionSettings?.client_id ?? connectionSettings?.clientId;
    const clientSecret = connectionSettings?.client_secret ?? connectionSettings?.clientSecret;
    let nodeApiHeaders = await getFreshNodeApiHeaders();

    if (!nodeApiHeaders.Authorization) {
      setLoadingSites(false);
      setSitesError('Your session has expired. Please sign in again and retry.');
      return false;
    }

    // Avoid 400 "Access token is required": get token first or try refresh, then bail with clear error
    if (!accessToken && refreshToken) {
      try {
        const refreshResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/refreshToken`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...nodeApiHeaders },
          body: JSON.stringify({
            refresh_token: refreshToken,
            redirectUri: `${window.location.origin}/auth/sharepoint/callback`,
            client_id: clientId,
            client_secret: clientSecret
          })
        });
        if (refreshResponse.ok) {
          const tokenData = await refreshResponse.json();
          accessToken = tokenData.access_token ?? (tokenData as any).access_token;
          // Persist new token to app account so future loads have it
            const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            if (token && accessToken) {
            const updated = {
              ...connectionSettings,
              access_token: accessToken,
              ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
              ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
            };
            fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${app.connectionDetails.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'accept': 'application/json' },
              body: JSON.stringify({ connection_settings: updated })
            }).catch(err => console.error('Failed to update token in backend:', err));
          }
        }
      } catch (e) {
        console.error('SharePoint token refresh failed:', e);
      }
    }

    if (!accessToken) {
      setLoadingSites(false);
      setSitesError('Access token is missing. Please reconnect your SharePoint account.');
      // Don't set sharePointFetchErrorRef — sites failure must not block file listing
      return false;
    }

    try {

      // Call SharePoint sites endpoint
      let response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/sharepoint/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...nodeApiHeaders,
        },
        body: JSON.stringify({
          access_token: accessToken,
          tenant_id: tenantId
        })
      });
      
      // A 401 may be the Shay session token or the SharePoint token. Renew the
      // Shay token and retry first so the provider refresh route is not blocked
      // by the local Node authentication middleware.
      if (response.status === 401) {
        const newNodeAccessToken = await refreshAccessToken();
        if (!newNodeAccessToken) {
          setSitesError('Your session has expired. Please sign in again and retry.');
          return false;
        }

        nodeApiHeaders = { Authorization: `Bearer ${newNodeAccessToken}` };
        response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/sharepoint/sites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify({
            access_token: accessToken,
            tenant_id: tenantId
          })
        });
      }

      // Handle SharePoint token refresh on 401.
      if (response.status === 401 && refreshToken) {
        const refreshEndpoint = `${API_CONFIG.NODE_BASE_URL}/rest/office365/refreshToken`;
        const refreshRequestBody = {
          refresh_token: refreshToken,
          redirectUri: `${window.location.origin}/auth/sharepoint/callback`,
          client_id: clientId,
          client_secret: clientSecret
        };
        
        const refreshResponse = await fetch(refreshEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify(refreshRequestBody)
        });
        
        if (refreshResponse.ok) {
          const tokenData = await refreshResponse.json();
          accessToken = tokenData.access_token || (tokenData as any).access_token;
          
          // Update token in backend
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
          if (token) {
            const updatedConnectionSettings = {
              ...connectionSettings,
              access_token: accessToken,
              ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
              ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
            };
            
            fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${app.connectionDetails.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'accept': 'application/json'
              },
              body: JSON.stringify({
                connection_settings: updatedConnectionSettings
              })
            }).catch(err => console.error('Failed to update token in backend:', err));
          }
          
          // Retry with new token
          response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/office365/sharepoint/sites`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...nodeApiHeaders,
            },
            body: JSON.stringify({
              access_token: accessToken,
              tenant_id: tenantId
            })
          });
        } else {
          setSitesError('Failed to refresh access token. Please reconnect your SharePoint account.');
          return false;
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        // Don't set sharePointFetchErrorRef — sites failure must not block file listing
        const message = response.status === 400 && errorData?.message
          ? errorData.message
          : (errorData?.message || 'Failed to fetch sites');
        setSitesError(message);
        throw new Error(message);
      }

      const data = await response.json();
      if (data.success && data.data?.sites) {
        setAvailableSites(data.data.sites);
        // Auto-select OneDrive as default if available
        const oneDriveSite = data.data.sites.find((s: any) => s.id === 'onedrive');
        if (oneDriveSite && !selectedSiteId) {
          setSelectedSiteId('onedrive');
          setSelectedSiteName('OneDrive');
          setSelectedSiteUrl(oneDriveSite.webUrl || '');
        }
        return true;
      }
      setSitesError('Failed to fetch SharePoint sites');
      return false;
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSitesError('Failed to fetch SharePoint sites');
      return false;
    } finally {
      setLoadingSites(false);
    }
  };

  const handleAcceptanceChange = async () => {
    const newAcceptance = !dataSourceInfoAccepted;
    setDataSourceInfoAccepted(newAcceptance);
    
    // If accepted and we have a selected app, fetch files (but don't auto-advance)
    if (newAcceptance && selectedApp) {
      // SharePoint: fetch files immediately via new backend API; mark done so step-2 useEffect skips re-fetch
      if (selectedApp.app_key === 'sharepoint') {
        await fetchFilesAndFolders(selectedApp, undefined);
        didInitialFetchForStep2Ref.current = true;
        // W-1: fetchSharePointSites is intentionally not awaited — it populates the site-selector
        // dropdown which is rendered with disabled={loadingSites} while the fetch is in flight.
        // The user cannot interact with the dropdown until it resolves, making it eventually
        // consistent by design. Files load immediately without waiting for the sites response.
        fetchSharePointSites(selectedApp);
      } else if (selectedApp.app_key === 'google_drive' && googleDriveType !== 'home') {
        await fetchFilesAndFolders(selectedApp, undefined);
      } else if (selectedApp.app_key !== 'google_drive') {
        await fetchFilesAndFolders(selectedApp, undefined);
      }
      // Google Drive in 'home' view: no fetch; user picks My Drive / Shared drives / Shared with me first
      
      // Smooth scroll to file browser section after a short delay to ensure DOM has updated
      setTimeout(() => {
        fileBrowserRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 150);
    }
  };

  // Check if a file is selectable based on its extension or MIME type
  const isFileSelectable = (file: any): boolean => {
    // Check by file extension
    if (file.fileExtension && ALLOWED_FILE_FORMATS.includes(file.fileExtension.toLowerCase())) {
      return true;
    }
    
    // Check by MIME type (for Google Drive files)
    if (file.mimeType && ALLOWED_FILE_FORMATS.includes(file.mimeType)) {
      return true;
    }
    
    return false;
  };

  // Helper function to fetch folder contents without updating UI state (pass drive context for Google Drive shared/shared drives)
  const fetchFolderContentsForValidation = async (folderId: string, app: AppWithConnectionStatus): Promise<any[]> => {
    if (!app.connectionDetails) return [];

    const connectionSettings = app.connectionDetails.connection_settings as any;
    // PR #656 review (M1, Medium) — this only read the snake_case access_token, so a legacy
    // connection stored under the camelCase accessToken alone (the primary browse path above
    // already supports both shapes) looked tokenless and returned [] before any refresh could
    // run. The caller reports that as an empty folder, blocking selection of an otherwise-
    // connected account. Mirror the primary path's read-both-shapes + refresh-before-empty
    // handling instead of bailing out immediately.
    let accessToken = connectionSettings?.access_token ?? connectionSettings?.accessToken;
    const refreshToken = connectionSettings?.refresh_token ?? connectionSettings?.refreshToken;
    const clientId = connectionSettings?.client_id ?? connectionSettings?.clientId;
    const clientSecret = connectionSettings?.client_secret ?? connectionSettings?.clientSecret;

    if (!accessToken && refreshToken) {
      try {
        const nodeApiHeaders = await getFreshNodeApiHeaders();
        if (nodeApiHeaders.Authorization) {
          const refreshResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/refreshToken`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...nodeApiHeaders,
            },
            body: JSON.stringify({
              refresh_token: refreshToken,
              redirectUri: `${window.location.origin}/auth/googledrive/callback`,
              client_id: clientId,
              client_secret: clientSecret
            })
          });

          if (refreshResponse.ok) {
            const tokenData = await refreshResponse.json();
            accessToken = tokenData.access_token;

            // Keep the refreshed token on the connection, same as the primary browse path, so
            // the next folder-validation call does not repeat this same recovery.
            const authToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            if (authToken && accessToken) {
              fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${app.connectionDetails.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authToken}`,
                  'accept': 'application/json'
                },
                body: JSON.stringify({
                  connection_settings: {
                    ...connectionSettings,
                    access_token: accessToken,
                    ...(tokenData.clientId ? { client_id: tokenData.clientId } : {}),
                    ...(tokenData.clientSecret ? { client_secret: tokenData.clientSecret } : {})
                  }
                })
              }).catch(err => console.error('Failed to save refreshed Drive token:', err));
            }
          }
        }
      } catch (e) {
        console.error('Drive token refresh (folder validation) failed:', e);
      }
    }

    if (!accessToken) return [];

    const isGoogleDrive = app.app_key === 'google_drive';
    const body = isGoogleDrive
      ? {
          access_token: accessToken,
          folder_id: folderId,
          drive_type: googleDriveType,
          shared_drive_id: googleSharedDriveId || undefined,
          search_query: undefined
        }
      : { access_token: accessToken, folder_id: folderId };

    try {
      let nodeApiHeaders = await getFreshNodeApiHeaders();
      if (!nodeApiHeaders.Authorization) {
        throw new Error('Session expired. Please sign in again and retry.');
      }

      let response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/drive/files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...nodeApiHeaders,
        },
        body: JSON.stringify(body)
      });

      if (response.status === 401) {
        const newNodeAccessToken = await refreshAccessToken();
        if (!newNodeAccessToken) {
          throw new Error('Session expired. Please sign in again and retry.');
        }

        nodeApiHeaders = { Authorization: `Bearer ${newNodeAccessToken}` };
        response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/drive/files`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify(body)
        });
      }
      
      // Handle token refresh if needed
      if (response.status === 401 && refreshToken) {
        const refreshResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/refreshToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...nodeApiHeaders,
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
            redirectUri: `${window.location.origin}/auth/googledrive/callback`,
            client_id: clientId,
            client_secret: clientSecret
          })
        });
        
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          const newAccessToken = refreshData.access_token;
          const retryBody = isGoogleDrive
            ? { access_token: newAccessToken, folder_id: folderId, drive_type: googleDriveType, shared_drive_id: googleSharedDriveId || undefined, search_query: undefined }
            : { access_token: newAccessToken, folder_id: folderId };
          const retryResponse = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/drive/files`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...nodeApiHeaders,
            },
            body: JSON.stringify(retryBody)
          });
          
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            return data.data?.files || [];
          }
        }
        throw new Error('Failed to refresh access token');
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch folder contents');
      }
      
      const data = await response.json();
      return data.data?.files || [];
    } catch (error) {
      console.error('Error fetching folder contents for validation:', error);
      throw error;
    }
  };

  // Validate folder contents
  const validateFolderContents = async (folder: any, app: AppWithConnectionStatus): Promise<{
    isValid: boolean;
    validFiles: any[];
    errors: string[];
  }> => {
    const maxSize = 100 * 1024 * 1024; // 100MB
    const errors: string[] = [];
    
    try {
      // Fetch folder contents
      const contents = await fetchFolderContentsForValidation(folder.id, app);
      
      // Check if folder has subfolders
      const hasSubfolders = contents.some(item => item.isFolder);
      if (hasSubfolders) {
        errors.push('Folder contains subfolders. Only folders without subfolders can be selected.');
        return { isValid: false, validFiles: [], errors };
      }
      
      // Filter files (exclude folders)
      const files = contents.filter(item => !item.isFolder);
      
      // Check if folder is empty
      if (files.length === 0) {
        errors.push('Folder is empty. Please select a folder that contains files.');
        return { isValid: false, validFiles: [], errors };
      }
      
      // Filter valid files and track reasons for invalidity
      const validFiles: any[] = [];
      let hasInvalidFormat = false;
      let hasOversizedFiles = false;
      
      files.forEach(file => {
        // Check file format
        if (!isFileSelectable(file)) {
          hasInvalidFormat = true;
          return;
        }
        
        // Check file size
        if (file.size && file.size > maxSize) {
          hasOversizedFiles = true;
          return;
        }
        
        validFiles.push(file);
      });
      
      // Check if there are any valid files
      if (validFiles.length === 0) {
        // Provide specific error message based on the reason
        if (hasOversizedFiles && !hasInvalidFormat) {
          // All files are oversized
          if (files.length === 1) {
            errors.push('The file in this folder exceeds the 100MB size limit.');
          } else {
            errors.push(`All ${files.length} files in this folder exceed the 100MB size limit.`);
          }
        } else if (hasInvalidFormat && !hasOversizedFiles) {
          // All files have invalid format
          const supportedFormats = getAllowedFileFormatsForDisplay();
          if (files.length === 1) {
            errors.push(`The file in this folder is not in a supported format. Supported formats: ${supportedFormats}`);
          } else {
            errors.push(`None of the ${files.length} files in this folder are in a supported format. Supported formats: ${supportedFormats}`);
          }
        } else {
          // Mix of both issues or other reasons
          const supportedFormats = getAllowedFileFormatsForDisplay();
          if (files.length === 1) {
            errors.push(`The file in this folder cannot be selected. Files must be in a supported format and under 100MB. Supported formats: ${supportedFormats}`);
          } else {
            errors.push(`None of the ${files.length} files in this folder can be selected. Files must be in a supported format and under 100MB. Supported formats: ${supportedFormats}`);
          }
        }
        return { isValid: false, validFiles: [], errors };
      }
      
      if (validFiles.length > DATA_SOURCE_LIMIT) {
        errors.push(`Folder contains ${validFiles.length} files, but maximum ${DATA_SOURCE_LIMIT} files allowed`);
        return { isValid: false, validFiles: [], errors };
      }
      
      return { isValid: true, validFiles, errors: [] };
      
    } catch (error) {
      errors.push('Failed to fetch folder contents');
      return { isValid: false, validFiles: [], errors };
    }
  };

  // Handle file selection (storage vs one-time baseline is synchronous)
  const handleFileSelect = (file: any) => {
    // Only allow selection of files with allowed formats
    if (!isFileSelectable(file)) {
      const supportedFormats = getAllowedFileFormatsForDisplay();
      showError(`Unsupported file format. Supported formats: ${supportedFormats}`);
      return;
    }
    
    const isSelected = selectedFiles.some(f => f.id === file.id);
    
    if (isSelected) {
      // Deselect file
      const removed = Number(file.size) || 0;
      const totalAfterDeselect = getDriveSelectedTotalBytes() - removed;
      setSelectedFiles(prev => prev.filter(f => f.id !== file.id));
      setSelectionOrder(prev => prev.filter(item => !(item.type === 'file' && item.id === file.id)));
      refreshDriveStorageBlockAfterDeselect(totalAfterDeselect);
    } else {
      // Check if we're at the limit
      if (selectedFiles.length >= DATA_SOURCE_LIMIT) {
        showError(`Maximum ${DATA_SOURCE_LIMIT} files can be selected`);
        return;
      }
      
      // Check file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (file.size && file.size > maxSize) {
        showError(`File "${file.name}" exceeds 100MB size limit`);
        return;
      }

      const addBytes = Number(file.size) || 0;
      const withinStorage = checkBeforeAddDriveSelection(addBytes);
      if (!withinStorage) return;
      
      // Check if this is the first item being selected (before adding)
      const isFirstSelection = selectedFiles.length === 0 && selectedFolders.length === 0;
      
      // Add file to selection
      setSelectedFiles(prev => [...prev, file]);
      setSelectionOrder(prev => [...prev, { type: 'file', id: file.id }]);
      
      // Smooth scroll to selected items section only if this is the first selection
      if (isFirstSelection) {
        setTimeout(() => {
          selectedItemsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  };

  // Handle select all files (honors plan storage vs one-time baseline on Drives browse step)
  const handleSelectAllFiles = () => {
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    
    // Filter for selectable files that meet size requirements
    const allSelectableFiles = filesAndFolders
      .filter(item => !item.isFolder && isFileSelectable(item))
      .filter(file => !file.size || file.size <= maxSize); // Only include files under 100MB
    
    if (selectedFiles.length === allSelectableFiles.length) {
      // Deselect all visible files (same as before)
      const removedBytes = selectedFiles.reduce((s, f) => s + (Number(f.size) || 0), 0);
      const totalAfterDeselect = getDriveSelectedTotalBytes() - removedBytes;
      setSelectedFiles([]);
      refreshDriveStorageBlockAfterDeselect(totalAfterDeselect);
    } else {
      // Check if this is the first item being selected (before selecting)
      const isFirstSelection = selectedFiles.length === 0 && selectedFolders.length === 0;
      
      // Select up to DATA_SOURCE_LIMIT files
      let filesToSelect = allSelectableFiles.slice(0, DATA_SOURCE_LIMIT);
      
      if (allSelectableFiles.length > DATA_SOURCE_LIMIT) {
        showError(`Only ${DATA_SOURCE_LIMIT} files can be selected. Selecting first ${DATA_SOURCE_LIMIT} files.`);
      }

      if (
        tabValue === 3 &&
        showFileBrowser &&
        fileSelectionStep === 2 &&
        !DISABLE_SUBSCRIPTION &&
        driveStorageAvailableBaseline !== null
      ) {
        const currentTotal = getDriveSelectedTotalBytes();
        const room = Math.max(0, driveStorageAvailableBaseline - currentTotal);
        let acc = 0;
        const capped: any[] = [];
        for (const f of filesToSelect) {
          const sz = Number(f.size) || 0;
          if (acc + sz > room) break;
          capped.push(f);
          acc += sz;
        }
        if (capped.length < filesToSelect.length) {
          if (capped.length === 0) {
            setDriveStorageSelectionBlocked(true);
            setOpenAttachmentRestrictionModal(true);
            return;
          }
          showError('Some files were not selected — not enough storage left on your plan.');
          setDriveStorageSelectionBlocked(true);
          setOpenAttachmentRestrictionModal(true);
        }
        filesToSelect = capped;
      }
      
      setSelectedFiles(filesToSelect);
      
      // Smooth scroll to selected items section only if this is the first selection
      if (isFirstSelection) {
        setTimeout(() => {
          selectedItemsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType: string) => {
    if (!mimeType) return <Description sx={{ fontSize: 20, color: '#616161' }} />;
    
    if (mimeType.startsWith('image/')) return <ImageIcon sx={{ fontSize: 20, color: '#4caf50' }} />;
    if (mimeType.startsWith('video/')) return <VideoLibrary sx={{ fontSize: 20, color: '#f44336' }} />;
    if (mimeType.startsWith('audio/')) return <AudioFile sx={{ fontSize: 20, color: '#9c27b0' }} />;
    if (mimeType.includes('pdf')) return <PictureAsPdf sx={{ fontSize: 20, color: '#d32f2f' }} />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) {
      return <TableChart sx={{ fontSize: 20, color: '#2e7d32' }} />;
    }
    if (mimeType.includes('document') || mimeType.includes('word')) {
      return <Description sx={{ fontSize: 20, color: '#1976d2' }} />;
    }
    return <InsertDriveFile sx={{ fontSize: 20, color: '#616161' }} />;
  };

  const deviceFiles: File[] = Array.isArray(dataSources) ? dataSources.filter(f => f instanceof File) as File[] : [];
  const selectedFile: File | undefined = deviceFiles[0]; // Keep for backward compatibility

  const getFileExtension = (name: string) => name.split('.').pop()?.toLowerCase() || '';

  const extractFilenameFromUrl = (url: string): string => {
    try {
      let filename = '';
      
      // Handle s3:// protocol URLs
      if (url.startsWith('s3://')) {
        const withoutProtocol = url.substring(5); // Remove 's3://'
        const parts = withoutProtocol.split('/');
        filename = parts[parts.length - 1] || 'file';
      } else if (url.startsWith('gs://')) {
        // Handle gs:// protocol URLs (GCP)
        const withoutProtocol = url.substring(5); // Remove 'gs://'
        const parts = withoutProtocol.split('/');
        filename = parts[parts.length - 1] || 'file';
      } else {
        // Handle HTTP/HTTPS URLs (Azure, S3, or GCP)
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/').filter(part => part.length > 0);
        filename = pathParts[pathParts.length - 1] || 'file';
      }
      
      // Decode URL-encoded characters (e.g., %20 -> space)
      try {
        return decodeURIComponent(filename);
      } catch (decodeError) {
        // If decoding fails, return the original filename
        return filename;
      }
    } catch (error) {
      // Fallback: try to extract from the URL string directly
      const parts = url.split('/');
      const filename = parts[parts.length - 1];
      if (filename && filename.trim()) {
        try {
          return decodeURIComponent(filename.trim());
        } catch (decodeError) {
          return filename.trim();
        }
      }
      return 'file';
    }
  };

  const getFileTypeFromUrl = (url: string): string => {
    const extension = getFileExtension(url);
    const fileTypeMap: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'csv': 'text/csv',
      'json': 'application/json',
      'txt': 'text/plain',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    };
    return fileTypeMap[extension] || 'application/octet-stream';
  };

  const parseAzureUrl = (url: string): { account_name: string; container_name: string; file_path: string } => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(part => part.length > 0);
      if (pathParts.length < 2) {
        throw new Error('Invalid Azure URL format');
      }
      // Extract account_name from hostname
      const account_name = urlObj.hostname.split('.')[0];
      // Extract container_name and file_path from path
      const container_name = pathParts[0]; // First part is container_name
      const file_path = pathParts.slice(1).join('/'); // Rest is file_path
      return {
        account_name,
        container_name,
        file_path
      };
    } catch (error) {
      console.error('Error parsing Azure URL:', error);
      // Return default values if parsing fails
      return {
        account_name: 'ggshaydevmkstracc',
        container_name: 'mldatatesting',
        file_path: url
      };
    }
  };

  const parseS3Url = (url: string): { bucket_name: string; file_path: string; region: string } => {
    try {
      // Handle s3:// protocol URLs
      if (url.startsWith('s3://')) {
        const withoutProtocol = url.substring(5); // Remove 's3://'
        const parts = withoutProtocol.split('/');
        if (parts.length < 1 || !parts[0]) {
          throw new Error('Invalid S3 URL format: missing bucket name');
        }
        const bucket_name = parts[0];
        const file_path = parts.slice(1).join('/');
        return {
          bucket_name,
          file_path,
          region: 'us-east-1' // Default region for s3:// URLs
        };
      }
      
      // Handle HTTP/HTTPS URLs
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(part => part.length > 0);
      if (pathParts.length < 1) {
        throw new Error('Invalid S3 URL format');
      }
      // Extract bucket_name from hostname
      const bucket_name = urlObj.hostname.split('.')[0];
      // Extract file_path from path
      const file_path = pathParts.join('/');
      // Determine region from hostname
      let region = 'us-east-1'; // default
      if (urlObj.hostname.includes('amazonaws.com')) {
        const hostnameParts = urlObj.hostname.split('.');
        if (hostnameParts.length >= 3) {
          region = hostnameParts[1]; // e.g., s3.us-east-1.amazonaws.com -> us-east-1
        }
      }

      return {
        bucket_name,
        file_path,
        region
      };
    } catch (error) {
      console.error('Error parsing S3 URL:', error);
      // Return default values if parsing fails
      return {
        bucket_name: 'mldatatesting',
        file_path: url,
        region: 'us-east-1'
      };
    }
  };

  const parseGcpUrl = (url: string): { bucket_name: string; file_path: string } => {
    try {
      // Handle gs:// protocol URLs
      if (url.startsWith('gs://')) {
        const withoutProtocol = url.substring(5); // Remove 'gs://'
        const parts = withoutProtocol.split('/');
        if (parts.length < 1 || !parts[0]) {
          throw new Error('Invalid GCP URL format: missing bucket name');
        }
        const bucket_name = parts[0];
        const file_path = parts.slice(1).join('/');
        return { bucket_name, file_path: file_path || '' };
      }
      
      // Handle HTTPS URLs
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/').filter(part => part.length > 0);
        
        // For storage.googleapis.com URLs, first path part is bucket, rest is file path
        if (urlObj.hostname.includes('storage.googleapis.com') || 
            urlObj.hostname.includes('storage.cloud.google.com')) {
          if (pathParts.length < 1) {
            throw new Error('Invalid GCP URL format');
          }
          const bucket_name = pathParts[0];
          const file_path = pathParts.slice(1).join('/');
          return { bucket_name, file_path: file_path || '' };
        }
        
        // For bucket-name.storage.googleapis.com format
        if (urlObj.hostname.includes('.storage.googleapis.com')) {
          const bucket_name = urlObj.hostname.split('.')[0];
          const file_path = pathParts.join('/');
          return { bucket_name, file_path: file_path || '' };
        }
      }
      
      // If URL doesn't match patterns, assume it's just a file path (no bucket)
      return { bucket_name: '', file_path: url };
    } catch (error) {
      console.error('Error parsing GCP URL:', error);
      // Return empty bucket and URL as file path if parsing fails
      return { bucket_name: '', file_path: url };
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const truncateFilename = (filename: string, maxLength: number = 50): string => {
    if (filename.length <= maxLength) return filename;

    const extension = filename.split('.').pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const maxNameLength = maxLength - (extension ? extension.length + 1 : 0);
    if (maxNameLength <= 0) return filename.substring(0, maxLength);
    return `${nameWithoutExt.substring(0, maxNameLength)}.${extension}`;
  };

  const renderFileIcon = (ext: string) => {
    if (ext === 'pdf') return <PictureAsPdf sx={{ fontSize: 64, color: '#d32f2f', mb: 1 }} />;
    if (['csv', 'xls', 'xlsx'].includes(ext)) return <InsertDriveFile sx={{ fontSize: 64, color: '#2e7d32', mb: 1 }} />;
    if (['doc', 'docx'].includes(ext)) return <InsertDriveFile sx={{ fontSize: 64, color: '#1cabde', mb: 1 }} />;
    return <InsertDriveFile sx={{ fontSize: 64, color: '#616161', mb: 1 }} />;
  };

  const allowedExtensions = ['csv', 'xlsx', 'xls', 'json'] as const;
  const selectedExt = selectedFile ? getFileExtension(selectedFile.name) : '';
  const isAllowedExt = selectedFile ? allowedExtensions.includes(selectedExt as any) : false;
  const isDeviceTab = tabValue === 0;
  const isCloudTab = tabValue === 1;
  const isDatabaseTab = tabValue === 2;
  const isDrivesTab = tabValue === 3;
  const isAppTab = tabValue === 4;
  const isWebsiteTab = tabValue === 5;

  // Filter apps for Drives tab (hidden apps already excluded via shouldShowAppInMarketplace on fetch)
  const drivesApps = datasourceApps.filter(app => isDriveApp(app));

  // Filter apps for Connect App tab
  const connectApps = datasourceApps.filter(app => !isDriveApp(app));

  const showAddDataSourceButton = true;

  // Fetch existing database connections for the selected database type
  const fetchExistingDbConnections = async (dbType: string) => {
    if (!dbType || (!channelId && !selectedWorkspace)) return;
    
    setLoadingExistingConnections(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const params = new URLSearchParams({
        storage_type: 'database',
        channel_id: channelId || selectedWorkspace,
      });

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const datasources = data.datasources || [];
      
      console.log('Fetched datasources for database type:', dbType, datasources);
      
      // Filter by database type and group by connection key (hostname+username+database)
      const connectionMap = new Map<string, ExistingDbConnection>();
      
      datasources.forEach((ds: any) => {
        const dsDbType = ds.provider || ds.database_type || ds.config?.database_type;
        console.log('Checking datasource:', ds.name, 'type:', dsDbType, 'full ds:', ds);
        
        if (dsDbType?.toLowerCase() === dbType.toLowerCase()) {
          // Get vault_id from datasource_metadata.vault_unique_id
          const hostname = ds.config?.hostname || '';
          const username = ds.config?.username || '';
          const databaseName = ds.config?.databaseName || ds.config?.database_name || '';
          const vaultId = ds.datasource_metadata?.vault_unique_id;
          
          console.log('Found vault_id:', vaultId, 'for datasource:', ds.name);
          
          // Use vault_id as connection key, or fall back to connection details
          const connectionKey = vaultId || `${hostname}-${username}-${databaseName}`;
          
          if (!connectionKey || connectionKey === '--') {
            console.log('Skipping datasource without connection key:', ds.name);
            return;
          }
          
          if (connectionMap.has(connectionKey)) {
            // Add table to existing connection
            const existing = connectionMap.get(connectionKey)!;
            const tableName = ds.config?.tableName || ds.config?.table_name || ds.name;
            if (tableName && !existing.tables.includes(tableName)) {
              existing.tables.push(tableName);
            }
          } else {
            // Create new connection entry
            const tableName = ds.config?.tableName || ds.config?.table_name || ds.name;
            const displayName = username && hostname 
              ? `${username}@${hostname}${databaseName ? '/' + databaseName : ''}`
              : ds.name;
            connectionMap.set(connectionKey, {
              id: ds.id,
              name: displayName,
              database_type: dsDbType,
              vault_id: vaultId || connectionKey, // Store actual vault_id if available, otherwise use connectionKey
              config: ds.config,
              tables: tableName ? [tableName] : [],
            });
          }
        }
      });
      
      const connections = Array.from(connectionMap.values());
      console.log('Found existing connections:', connections);
      setExistingDbConnections(connections);
      
      // If no existing connections, show new connection form
      if (connections.length === 0) {
        setShowNewConnectionForm(true);
        setSelectedExistingConnection('');
      } else {
        setShowNewConnectionForm(false);
      }
    } catch (error) {
      console.error('Error fetching existing database connections:', error);
      setExistingDbConnections([]);
      setShowNewConnectionForm(true);
    } finally {
      setLoadingExistingConnections(false);
    }
  };

  // Fetch existing database connections when database type changes
  useEffect(() => {
    if (databaseType && tabValue === 2 && (channelId || selectedWorkspace)) {
      fetchExistingDbConnections(databaseType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseType, tabValue, channelId, selectedWorkspace]);

  // Handle selecting an existing connection
  const handleSelectExistingConnection = async (connectionId: string) => {
    setSelectedExistingConnection(connectionId);
    const connection = existingDbConnections.find(c => c.vault_id === connectionId);
    
    if (connection) {
      // Set the vault ID and mark as connected (no need to test again)
      setVaultId(connection.vault_id);
      setConnectionTested(true);
      
      // Pre-select already added tables
      setOriginalSelectedTables([...connection.tables]);
      setSelectedTables([...connection.tables]);
      
      // Fetch all available tables for this connection
      handleGetAllTables(connection.vault_id);
    }
  };

  // Database tab functions
  const handleTestConnection = async () => {
    console.log('Test connection clicked');
    // Validate required fields based on database type
    let hasErrors = false;
    
    if (!databaseType) {
      setDatabaseTypeError('Please select database type');
      hasErrors = true;
    }
    
    if (!channelId && !selectedWorkspace) {
      setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
      hasErrors = true;
    }
    
    console.log('Validation check - hasErrors:', hasErrors, 'databaseType:', databaseType);
    
    // Common required fields (not for DynamoDB, Snowflake, or BigQuery)
    if (databaseType !== 'dynamodb' && databaseType !== 'snowflake' && databaseType !== 'bigquery') {
      if (!dbHostname) {
        setDbHostnameError('Please enter hostname');
        hasErrors = true;
      }
      if (!dbPort) {
        setDbPortError('Please enter port');
        hasErrors = true;
      }
    }
    
    // Username and Password (required for all except BigQuery and DynamoDB)
    if (databaseType !== 'bigquery' && databaseType !== 'dynamodb') {
      if (!dbUsername) {
        setDbUsernameError('Please enter username');
        hasErrors = true;
      }
      if (!dbPassword) {
        setDbPasswordError('Please enter password');
        hasErrors = true;
      }
    }
    
    // Database-specific validations
    if (databaseType === 'oracle') {
      if (!dbDatabaseName) {
        setDbDatabaseNameError('Please enter database name');
        hasErrors = true;
      }
      if (!dbServiceName) {
        hasErrors = true;
        showError('Please enter service name');
      }
    }
    if (databaseType === 'dynamodb') {
      if (!dbAccessKeyId) {
        hasErrors = true;
        showError('Please enter access key ID');
      }
      if (!dbSecretAccessKey) {
        hasErrors = true;
        showError('Please enter secret access key');
      }
      if (!dbRegionName) {
        hasErrors = true;
        showError('Please enter region name');
      }
    }
    if (databaseType === 'snowflake') {
      if (!dbAccountId) {
        hasErrors = true;
        showError('Please enter account ID');
      }
      if (!dbDatabaseName) {
        setDbDatabaseNameError('Please enter database name');
        hasErrors = true;
      }
      if (!dbWarehouse) {
        hasErrors = true;
        showError('Please enter warehouse');
      }
      if (!dbSchemaName) {
        hasErrors = true;
        showError('Please enter schema name');
      }
      if (!dbUsername) {
        setDbUsernameError('Please enter username');
        hasErrors = true;
      }
      if (!dbPassword) {
        setDbPasswordError('Please enter password');
        hasErrors = true;
      }
    }
    if (databaseType === 'redshift') {
      if (!dbDatabaseName) {
        setDbDatabaseNameError('Please enter database name');
        hasErrors = true;
      }
      if (!dbSchemaName) {
        hasErrors = true;
        showError('Please enter schema');
      }
    }
    if (databaseType === 'bigquery') {
      if (!dbDatabaseName) {
        setDbDatabaseNameError('Please enter project ID');
        hasErrors = true;
      }
      if (!dbSchemaName) {
        hasErrors = true;
        showError('Please enter dataset ID');
      }
      if (!bigQueryServiceAccountFile || !bigQueryServiceAccountKey) {
        setBigQueryServiceAccountKeyError('Please upload service account JSON file');
        hasErrors = true;
      }
    }
    
    // Database Name required for PostgreSQL, MySQL, MariaDB
    // Note: Oracle database name validation is in the Oracle-specific section above
    if ((databaseType === 'postgresql' || databaseType === 'mysql' || databaseType === 'mariadb') && !dbDatabaseName) {
      setDbDatabaseNameError('Please enter database name');
      hasErrors = true;
    }
    
    if (hasErrors) {
      console.log('Validation failed, returning early');
      return;
    }

    const attachmentAllowed = await ensureCanCreateAttachment();
    if (!attachmentAllowed) return;
    
    console.log('Starting connection test...');
    setTestingConnection(true);
    setConnectionTested(false);
    setTablesError('');
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const payload: any = {
        databaseType: databaseType,
        storageType: 'database',
        useCaseType: 'texttosql',
        channel_id: channelId || selectedWorkspace,
        is_embedding_required: false,
      };
      
      // Add common fields (not for DynamoDB, Snowflake, or BigQuery)
      if (databaseType !== 'dynamodb' && databaseType !== 'snowflake' && databaseType !== 'bigquery') {
        payload.hostname = dbHostname;
        payload.port = dbPort;
        payload.databaseName = dbDatabaseName || '';
      }
      
      // Username and Password (for all except BigQuery and DynamoDB)
      if (databaseType !== 'bigquery' && databaseType !== 'dynamodb') {
        payload.username = dbUsername;
        payload.password = await encryptPassword(dbPassword);
      }
      
      // Add database-specific fields
      if (databaseType === 'oracle') {
        payload.serviceName = dbServiceName;
        if (dbSchemaName) payload.schemaName = dbSchemaName;
        if (dbHostname) payload.hostname = dbHostname;
        if (dbPort) payload.port = dbPort;
        if (dbDatabaseName) payload.databaseName = dbDatabaseName;
      }
      if (databaseType === 'postgresql') {
        if (dbSchemaName) payload.schemaName = dbSchemaName;
      }
      if (databaseType === 'redshift') {
        if (dbDatabaseName) payload.databaseName = dbDatabaseName;
        if (dbSchemaName) payload.schemaName = dbSchemaName;
      }
      if (databaseType === 'snowflake') {
        payload.accountId = dbAccountId;
        if (dbDatabaseName) payload.databaseName = dbDatabaseName;
        payload.warehouse = dbWarehouse;
        payload.schemaName = dbSchemaName;
        if (dbHostname) payload.hostname = dbHostname;
        if (dbPort) payload.port = dbPort;
        if (dbDatabaseName) payload.databaseName = dbDatabaseName;
      }
      if (databaseType === 'dynamodb') {
        payload.accessKeyId = await encryptPassword(dbAccessKeyId);
        payload.secretAccessKey = await encryptPassword(dbSecretAccessKey);
        payload.regionName = dbRegionName;
        if (dbHostname) payload.hostname = dbHostname;
        if (dbPort) payload.port = dbPort;
      }
      if (databaseType === 'bigquery') {
        // BigQuery uses projectId and datasetId
        payload.projectId = dbDatabaseName; // Using dbDatabaseName for projectId
        payload.datasetId = dbSchemaName; // Using dbSchemaName for datasetId
        // Service account credentials JSON as string
        payload.serviceAccountCredentials = bigQueryServiceAccountKey;
      }
      
      console.log('Sending test connection request:', {
        url: `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.DATABASE.TEST_CONNECTION}`,
        payload: { 
          ...payload, 
          password: '***', 
          secretAccessKey: '***',
          serviceAccountCredentials: payload.serviceAccountCredentials ? '***' : undefined
        } // Hide sensitive data
      });
      
      const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.DATABASE.TEST_CONNECTION}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        const errorMessage = data?.message || data?.detail || data?.error || `Connection test failed (${response.status})`;
        throw new Error(errorMessage);
      }
      
      // Check for vaultIdentifier in different possible response formats
      const vaultIdentifier = data?.vaultIdentifier || data?.vault_id || data?.vaultId;
      if (!vaultIdentifier) {
        console.error('Response data:', data);
        throw new Error('Connection test succeeded but no vault ID received. Please try again.');
      }
      
      setVaultId(vaultIdentifier);
      setConnectionTested(true);
      showSuccess('Database connection successful');
      
      // Automatically fetch tables after successful connection
      handleGetAllTables(vaultIdentifier);
    } catch (error: any) {
      console.error('Connection test failed:', error);
      const errorMessage = error.message || 'Connection test failed. Please check your credentials and try again.';
      showError(errorMessage);
      setConnectionTested(false);
      setVaultId('');
    } finally {
      setTestingConnection(false);
    }
  };
  
  // Get All Tables API handler
  const handleGetAllTables = async (vaultIdParam?: string) => {
    if (!vaultId && !vaultIdParam) {
      showError('Please test connection first');
      return;
    }
    
    const currentVaultId = vaultIdParam || vaultId;
    setLoadingTables(true);
    setTablesError('');
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.DATABASE.GET_ALL_TABLES}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          channelId: channelId || selectedWorkspace,
          databaseType: databaseType,
          vaultId: currentVaultId
        })
      });
      
      let data = await response.json();
      
      // Check response status
      if (!response.ok) {
        throw new Error(data.message || data.error || `Failed to fetch tables (${response.status})`);
      }
      
      // Validate response structure
      if (!data) {
        console.error('Empty response received');
        throw new Error('Invalid response: empty data received');
      }
      
      // Handle case where response might be an array directly
      if (Array.isArray(data)) {
        data = { tables: data } as any;
      } else if (!data.tables) {
        console.error('Unexpected response structure:', data);
        throw new Error((data as any).message || 'Invalid response format: tables array not found');
      }
      
      // Ensure we have a valid tables array
      const tablesArray = data.tables || (Array.isArray(data) ? data : []);
      
      console.log('Tables fetched:', tablesArray.length, 'tables');
      setTables(tablesArray);
      
      if (tablesArray.length > 0) {
        showSuccess(`Found ${tablesArray.length} table(s)`);
      } else {
        showError('No tables found in the database');
      }
    } catch (error: any) {
      console.error('Failed to fetch tables:', error);
      const errorMessage = error.message || 'Failed to fetch tables';
      showError(errorMessage);
      setTablesError(errorMessage);
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  };
  
  // Get Table Rows/Preview API handler
  const handleGetTablePreview = async (tableName: string) => {
    if (!vaultId || !tableName) return;
    
    setLoadingPreview(true);
    setPreviewError('');
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.DATABASE.GET_ROWS}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          channelId: channelId || selectedWorkspace,
          databaseType: databaseType,
          maxCount: 10,
          tableName: tableName,
          schemaName: dbSchemaName || undefined,
          vaultId: vaultId,
          tableNames: [tableName]
        })
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.data || data.data.length === 0) {
        throw new Error(data.message || 'Failed to fetch table preview');
      }
      
      const tableData = data.data[0];
      if (tableData && tableData.data) {
        setTablePreview(prev => ({
          ...prev,
          [tableName]: {
            columns: tableData.data.columns || [],
            rows: tableData.data.rows || []
          }
        }));
      }
    } catch (error: any) {
      console.error('Failed to fetch table preview:', error);
      setPreviewError(error.message || 'Failed to fetch table preview');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const handleOpenPreviewModal = () => {
    if (selectedTables.length === 0) {
      showError('Please select at least one table');
      return;
    }
    setOpenPreviewModal(true);
    setPreviewTableName(selectedTables[0]);
    handleGetTablePreview(selectedTables[0]);
  };
  
  const handlePreviewTableChange = (tableName: string) => {
    setPreviewTableName(tableName);
    if (!tablePreview[tableName]) {
      handleGetTablePreview(tableName);
    }
  };

  const runCloudTabGuards = () => {
    let blocked = false;
    if (isCloudTab) {
      // Only validate channel selection if no channelId is provided (standalone mode)
      if (!channelId && !cloudWorkspace) {
                   setCloudWorkspaceError(`Please select ${LanguageConstants.topic.toLowerCase()}`);
        blocked = true;
      } else if (channelId) {
        // When channelId is provided, ensure it's set
        setCloudWorkspace(channelId);
        setCloudWorkspaceError('');
      } else {
        setCloudWorkspaceError('');
      }
      if (!storageProvider) {
        setStorageProviderError('Please select storage provider');
        blocked = true;
      } else {
        setStorageProviderError('');
      }
      if (!locationUrl) {
        setLocationUrlError('Please enter location URL');
        blocked = true;
      } else {
        setLocationUrlError('');
      }
      if (storageProvider !== 'gcp' && !secretAccessKey) {
        setSecretAccessKeyError('Please enter secret access key');
        blocked = true;
      } else {
        setSecretAccessKeyError('');
      }
      if (storageProvider === 'aws' && !accessKeyId) {
        setAccessKeyIdError('Please enter access key ID');
        blocked = true;
      } else {
        setAccessKeyIdError('');
      }
      if (storageProvider === 'gcp') {
        if (!gcpServiceAccountFile || !gcpServiceAccountKey) {
          setGcpServiceAccountKeyError('Please upload service account JSON file');
          blocked = true;
        } else if (!gcpProjectId) {
          setGcpServiceAccountKeyError('Service account JSON is missing project_id field');
          blocked = true;
        } else {
          // JSON format and project_id already validated during file upload
          setGcpServiceAccountKeyError('');
        }
      }
    }
    return blocked;
  };

  const handleAddDataSource = async () => {
    // Check if user is on Device tab
    if (isDeviceTab) {
      let hasErrors = false;
      // Only validate channel selection if no channelId is provided (standalone mode)
      if (!channelId && !selectedWorkspace) {
                   setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
        hasErrors = true;
      } else if (channelId) {
        // When channelId is provided, ensure it's set
        setSelectedWorkspace(channelId);
        setWorkspaceError('');
      } else {
        setWorkspaceError('');
      }
      // if (!logType) {
      //   setLogTypeError('Log type is required');
      //   hasErrors = true;
      // } else {
      //   setLogTypeError('');
      // }
      if (deviceFiles.length === 0) {
        setHasTouchedFile(true);
        hasErrors = true;
      }
      if (hasErrors) {
        return false; // Return false on validation errors
      }
    }

    // Check if user is on Cloud tab - always validate if on cloud tab
    if (isCloudTab) {
      if (runCloudTabGuards()) return false; // Return false on guard failures
      const cloudAttachmentOk = await ensureCanCreateAttachment();
      if (!cloudAttachmentOk) return false;
    }

    // Check if user is on Database tab - validate database connection
    if (isDatabaseTab) {
      if (!connectionTested || !vaultId) {
        showError('Please test connection first');
        return false;
      }
      // Check if there are changes to make (either adding or removing tables)
      const hasChanges = selectedTables.some(t => !originalSelectedTables.includes(t)) || 
                         originalSelectedTables.some(t => !selectedTables.includes(t));
      if (selectedTables.length === 0 && originalSelectedTables.length === 0) {
        showError('Please select at least one table');
        return false;
      }
      if (!hasChanges && originalSelectedTables.length > 0) {
        showError('No changes to save. Please add or remove tables.');
        return false;
      }
      if (!channelId && !selectedWorkspace) {
        setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
        return false;
      }
    }

    // Handle device tab datasource creation
    if (isDeviceTab && deviceFiles.length > 0) {
      try {
        setIsCreatingDataSource(true);
        // Step 1: Upload all files
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const formData = new FormData();
        deviceFiles.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('isValidationRequired', String(false));
        const uploadResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.UPLOAD_BULK}`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          const errorMessage = errorData.message || `Failed to upload files (Error ${uploadResponse.status})`;
          showError(errorMessage);
          return false;
        }
        const uploadResult = await uploadResponse.json();
        // First bulk API succeeded: notify so panel can refresh and show these as "In progress" while create-bulk runs
        onFirstBulkSuccess?.(deviceFiles.map((f) => f.name));

        // Step 2: Create datasources in background (do not block; caller can move to next step / close modal)
        const datasourcePayloads = uploadResult.uploaded_files.map((uploadedFile: any, index: number) => {
          const file = deviceFiles[index];
          return {
            storage_type: "local",
            channel_id: channelId || selectedWorkspace,
            filename: truncateFilename(file.name),
            file_size: file.size,
            file_type: getFileTypeFromUrl(file.name),
            is_embedding_required: true,
            //  log_type: logType,
            config: {
              file_path: uploadedFile.storage_path
            }
          };
        });

        const createBulkUrl = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.CREATE_BULK}`;
        const createBulkHeaders: Record<string, string> = {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
        const createBulkBody = JSON.stringify({ datasources: datasourcePayloads });

        // Fire create/bulk in background so NEXT can proceed and button loading clears
        fetch(createBulkUrl, {
          method: 'POST',
          headers: createBulkHeaders,
          body: createBulkBody
        })
          .then(async (createResponse) => {
            if (!createResponse.ok) {
              const errorData = await createResponse.json().catch(() => ({}));
              const errorMessage = errorData.message || `Failed to add ${LanguageConstants.datasource_s.toLowerCase()} (Error ${createResponse.status})`;
              showError(errorMessage);
              return;
            }
            const result = await createResponse.json();
            if (result.total_failed > 0) {
              let errorMessage = `Failed to add ${result.total_failed} ${LanguageConstants.datasource_s.toLowerCase()}`;
              if (result.total_created > 0) {
                errorMessage += `. ${result.total_created} ${LanguageConstants.datasource_s.toLowerCase()} added successfully.`;
              }
              showError(errorMessage);
              return;
            }
            if (result.total_created > 0) {
              // Skip duplicate toaster when modal already closed after first bulk (upload) success
              if (!onFirstBulkSuccess) {
                const successMessage = result.total_created === 1
                  ? `Success! Your ${LanguageConstants.datasource.toLowerCase()} has been added. We are now generating the embeddings. Please check back in a few minutes`
                  : `Success! Your ${result.total_created} ${LanguageConstants.datasource.toLowerCase()} have been added. We are now generating the embeddings. Please check back in a few minutes`;
                showSuccess(successMessage);
              }
              // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
              if (channelId) {
                try {
                  const eventData = {
                    type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                    data: { channelId }
                  };
                  socketService.sendSocketMessage(eventData);
                } catch (error) {
                  // Handle error silently
                }
              }
              onDataSourceAdded?.();
              setRefreshTrigger(prev => prev + 1);
              if (!hideInternalButtons && !onFirstBulkSuccess) {
                onClose?.();
              }
            } else {
              showError(`No ${LanguageConstants.datasource_s.toLowerCase()} were added`);
            }
          })
          .catch((err) => {
            console.error(`Error adding ${LanguageConstants.datasource_s.toLowerCase()} (create/bulk):`, err);
            const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource_s.toLowerCase()}. Please try again.`;
            showError(errorMessage);
          });

        // Success: upload/bulk done; create/bulk runs in background; allow step to advance and loading to clear
        return true;
      } catch (err) {
        console.error(`Error adding ${LanguageConstants.datasource_s.toLowerCase()}:`, err);
        const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource_s.toLowerCase()}. Please try again.`;
        showError(errorMessage);
        return false;
      } finally {
        setIsCreatingDataSource(false);
      }
    }

    // Handle cloud tab datasource creation
    if (isCloudTab) {
      try {
        setIsCreatingDataSource(true);
        let datasourcePayload;
         if (storageProvider === 'aws') {
           // S3 payload
           const { bucket_name, file_path, region } = parseS3Url(locationUrl);
           datasourcePayload = {
             storage_type: "cloud",
             provider: "s3",
             channel_id: channelId || cloudWorkspace,
             location: locationUrl,
             accessToken: secretAccessKey,
             filename: extractFilenameFromUrl(locationUrl),
             file_size: 0, // Cloud files don't have size info
             file_type: getFileTypeFromUrl(locationUrl),
             log_type: cloudLogType,
             is_embedding_required: true,
             config: {
               bucket_name: bucket_name,
               file_path: file_path,
               region: region,
               access_key: accessKeyId,
               secret_key: secretAccessKey
             }
           };
         } else if (storageProvider === 'azure') {
           // Azure payload
           const { account_name, container_name, file_path } = parseAzureUrl(locationUrl);
           datasourcePayload = {
             storage_type: "cloud",
             provider: "azure",
             channel_id: channelId || cloudWorkspace,
             location: locationUrl,
             accessToken: secretAccessKey,
             filename: extractFilenameFromUrl(locationUrl),
             file_size: 0, // Cloud files don't have size info
             file_type: getFileTypeFromUrl(locationUrl),
             log_type: cloudLogType,
             is_embedding_required: true,
             config: {
               account_name: account_name,
               container_name: container_name,
               file_path: file_path,
               secret_access_key: secretAccessKey
             }
           };
        } else if (storageProvider === 'gcp') {
          // GCP payload
          const { bucket_name, file_path } = parseGcpUrl(locationUrl);
          
          // Base64 encode the service account JSON
          const base64EncodedJson = btoa(gcpServiceAccountKey);
          
          datasourcePayload = {
            storage_type: "cloud",
            provider: "gcp",
            channel_id: channelId || cloudWorkspace,
            location: locationUrl,
            accessToken: '', // Empty string for GCP
            filename: extractFilenameFromUrl(locationUrl),
            file_size: 0, // Cloud files don't have size info
            file_type: getFileTypeFromUrl(locationUrl),
            log_type: cloudLogType,
            is_embedding_required: true,
            config: {
              bucket_name: bucket_name,
              file_path: file_path,
              project_id: gcpProjectId, // Extracted from JSON
              service_account_json: base64EncodedJson // Base64 encoded JSON string
            }
          };
        } else {
          throw new Error('Unsupported storage provider');
        }
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const createBulkUrl = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.CREATE_BULK}`;
        const createBulkHeaders: Record<string, string> = {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
        const createBulkBody = JSON.stringify({ datasources: [datasourcePayload] });
        // Same as From Device: notify + close modal while create/bulk runs (AddLogDataSourceModal uses onFirstBulkSuccess)
        const pendingCloudName = extractFilenameFromUrl(locationUrl);
        onFirstBulkSuccess?.([pendingCloudName]);

        fetch(createBulkUrl, {
          method: 'POST',
          headers: createBulkHeaders,
          body: createBulkBody
        })
          .then(async (createResponse) => {
            if (!createResponse.ok) {
              const errorData = await createResponse.json().catch(() => ({}));
              const errorMessage = errorData.message || `Failed to add ${LanguageConstants.datasource.toLowerCase()} (Error ${createResponse.status})`;
              showError(errorMessage);
              return;
            }
            const result = await createResponse.json();
            if (result.total_failed > 0) {
              let errorMessage = `Failed to add ${LanguageConstants.datasource.toLowerCase()}`;
              if (result.failed_datasources?.[0]?.error) {
                const dbError = result.failed_datasources[0].error;
                if (dbError.includes('StringDataRightTruncationError')) {
                  errorMessage = 'File name is too long. Please use a shorter filename.';
                } else if (dbError.includes('duplicate key')) {
                  errorMessage = `A ${LanguageConstants.datasource.toLowerCase()} with this name already exists.`;
                } else {
                  errorMessage = `Database error occurred while creating ${LanguageConstants.datasource.toLowerCase()}.`;
                }
              }
              showError(errorMessage);
              return;
            }
            if (result.total_created > 0) {
              if (!onFirstBulkSuccess) {
                showSuccess(`Success! Your ${LanguageConstants.datasource.toLowerCase()} has been added. We are now generating the embeddings. Please check back in a few minutes`);
              }
              if (channelId) {
                try {
                  const eventData = {
                    type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                    data: { channelId: channelId }
                  };
                  socketService.sendSocketMessage(eventData);
                } catch (error) {
                  // Handle error silently
                }
              }
              onDataSourceAdded?.();
              setRefreshTrigger(prev => prev + 1);
              if (!hideInternalButtons && !onFirstBulkSuccess) {
                onClose?.();
              }
            } else {
              showError(`failed to add ${LanguageConstants.datasource.toLowerCase()}`);
            }
          })
          .catch((err) => {
            console.error(`Error adding ${LanguageConstants.datasource.toLowerCase()} (create/bulk):`, err);
            const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource.toLowerCase()}. Please try again.`;
            showError(errorMessage);
          });

        return true;
      } catch (err) {
        console.error(`Error adding ${LanguageConstants.datasource.toLowerCase()}:`, err);
        const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource.toLowerCase()}. Please try again.`;
        showError(errorMessage);
        return false;
      } finally {
        setIsCreatingDataSource(false);
      }
    }

    // Handle database tab datasource creation
    if (isDatabaseTab && connectionTested && vaultId) {
      // Calculate tables to add and remove
      const tablesToAdd = selectedTables.filter(t => !originalSelectedTables.includes(t));
      const tablesToRemove = originalSelectedTables.filter(t => !selectedTables.includes(t));
      
      // Check if there are any changes
      if (tablesToAdd.length === 0 && tablesToRemove.length === 0) {
        showError('No changes to save. Please add or remove tables.');
        return false;
      }
      
      try {
        setIsCreatingDataSource(true);
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        let totalRemoved = 0;
        let hasErrors = false;
        
        // Get connection config from existing connection if using one
        const existingConnection = existingDbConnections.find(c => c.vault_id === selectedExistingConnection);
        const connectionConfig = existingConnection?.config;
        
        // Remove tables that were deselected
        if (tablesToRemove.length > 0 && existingConnection) {
          // Fetch datasources to get IDs for deletion
          const params = new URLSearchParams({
            storage_type: 'database',
            channel_id: channelId || selectedWorkspace,
          });
          
          const listResponse = await fetch(
            `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`,
            {
              method: 'GET',
              headers: {
                accept: 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          );
          
          if (listResponse.ok) {
            const listData = await listResponse.json();
            const datasourcesToDelete = (listData.datasources || []).filter((ds: any) => {
              const dsVaultId = ds.config?.vault_id;
              const dsTableName = ds.config?.tableName || ds.name;
              return dsVaultId === selectedExistingConnection && tablesToRemove.includes(dsTableName);
            });
            
            // Delete each datasource
            for (const ds of datasourcesToDelete) {
              try {
                const deleteResponse = await fetch(
                  `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.DELETE(ds.id)}`,
                  {
                    method: 'DELETE',
                    headers: {
                      accept: 'application/json',
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                  }
                );
                if (deleteResponse.ok) {
                  totalRemoved++;
                }
              } catch (error) {
                console.error('Error deleting datasource:', error);
                hasErrors = true;
              }
            }
          }
        }
        
        // Add new tables
        if (tablesToAdd.length > 0) {
          const datasourcePayloads = await Promise.all(tablesToAdd.map(async (tableName) => {
            const payload: any = {
              storage_type: 'database',
              provider: databaseType,
              channel_id: channelId || selectedWorkspace,
              filename: tableName,
              is_embedding_required: true,
              config: {
                tableName: tableName,
                vault_id: vaultId, // Store vault_id for reuse
              }
            };
            
            // If using existing connection, copy config from it
            if (connectionConfig) {
              payload.config = {
                ...connectionConfig,
                tableName: tableName,
                vault_id: vaultId,
              };
            } else {
              // Add schema name if available
              if (dbSchemaName) {
                payload.config.schemaName = dbSchemaName;
              }
              
              // Add database connection details
              if (databaseType !== 'dynamodb' && databaseType !== 'bigquery') {
                payload.config.hostname = dbHostname;
                payload.config.port = dbPort;
                payload.config.databaseName = dbDatabaseName || '';
              }
              
              if (databaseType !== 'bigquery' && databaseType !== 'dynamodb') {
                payload.config.username = dbUsername;
                payload.config.password = await encryptPassword(dbPassword);
              }
              
              if (databaseType === 'oracle') {
                payload.config.serviceName = dbServiceName;
              }
              if (databaseType === 'redshift') {
                if (dbSchemaName) {
                  payload.config.schemaName = dbSchemaName;
                }
              }
              if (databaseType === 'snowflake') {
                payload.config.accountId = dbAccountId;
                payload.config.warehouse = dbWarehouse;
              }
              if (databaseType === 'dynamodb') {
                payload.config.accessKeyId = await encryptPassword(dbAccessKeyId);
                payload.config.secretAccessKey = await encryptPassword(dbSecretAccessKey);
                payload.config.regionName = dbRegionName;
                if (dbHostname) payload.config.hostname = dbHostname;
                if (dbPort) payload.config.port = dbPort;
              }
              if (databaseType === 'bigquery') {
                payload.config.projectId = dbDatabaseName;
                payload.config.datasetId = dbSchemaName;
                payload.config.serviceAccountCredentials = bigQueryServiceAccountKey;
              }
            }
            
            return payload;
          }));

          const removedCountSnapshot = totalRemoved;
          const createBulkUrlDb = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.CREATE_BULK}`;
          const createBulkHeadersDb: Record<string, string> = {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          };
          const createBulkBodyDb = JSON.stringify({ datasources: datasourcePayloads });
          // Fire create/bulk in background so the popup can close like From Device / cloud / drives
          onFirstBulkSuccess?.(tablesToAdd);

          fetch(createBulkUrlDb, {
            method: 'POST',
            headers: createBulkHeadersDb,
            body: createBulkBodyDb
          })
            .then(async (response) => {
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || `Failed to add ${LanguageConstants.datasource_s.toLowerCase()} (Error ${response.status})`;
                showError(errorMessage);
                return;
              }
              const result = await response.json();
              const created = result.total_created || 0;
              if (result.total_failed > 0) {
                let message = '';
                if (created > 0) message += `${created} table(s) added. `;
                if (removedCountSnapshot > 0) message += `${removedCountSnapshot} table(s) removed. `;
                message += 'Some operations failed.';
                showError(message);
                return;
              }
              if (created > 0 || removedCountSnapshot > 0) {
                if (!onFirstBulkSuccess) {
                  let message = '';
                  if (created > 0) message += `${created} table(s) added. `;
                  if (removedCountSnapshot > 0) message += `${removedCountSnapshot} table(s) removed. `;
                  message += 'We are now generating the embeddings. Please check back in a few minutes.';
                  showSuccess(message);
                }
                if (channelId) {
                  try {
                    const eventData = {
                      type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                      data: { channelId: channelId }
                    };
                    socketService.sendSocketMessage(eventData);
                  } catch (error) {
                    // Handle error silently
                  }
                }
                onDataSourceAdded?.();
                setRefreshTrigger(prev => prev + 1);
                if (!hideInternalButtons && !onFirstBulkSuccess) {
                  onClose?.();
                }
              }
            })
            .catch((err) => {
              console.error(`Error adding ${LanguageConstants.datasource_s.toLowerCase()} (create/bulk):`, err);
              const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource_s.toLowerCase()}. Please try again.`;
              showError(errorMessage);
            });

          return true;
        }

        if (hasErrors) {
          let message = '';
          if (totalRemoved > 0) message += `${totalRemoved} table(s) removed. `;
          message += 'Some operations failed.';
          showError(message);
        } else if (totalRemoved > 0) {
          let message = '';
          message += `${totalRemoved} table(s) removed. `;
          message += 'We are now generating the embeddings. Please check back in a few minutes.';
          showSuccess(message);
          if (channelId) {
            try {
              const eventData = {
                type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                data: { channelId: channelId }
              };
              socketService.sendSocketMessage(eventData);
            } catch (error) {
              // Handle error silently
            }
          }
          onDataSourceAdded?.();
          setRefreshTrigger(prev => prev + 1);
          if (!hideInternalButtons) {
            onClose?.();
          }
          return true;
        }

        return !hasErrors;
      } catch (err) {
        console.error(`Error managing ${LanguageConstants.datasource_s.toLowerCase()}:`, err);
        const errorMessage = err instanceof Error ? err.message : `Failed to manage ${LanguageConstants.datasource_s.toLowerCase()}. Please try again.`;
        showError(errorMessage);
        return false;
      } finally {
        setIsCreatingDataSource(false);
      }
    }

    // Check if user is on Drives or Connect App tab and has started configuring
    const hasAppConfig = (isDrivesTab || isAppTab) && (selectedApp || showFileBrowser);
    if ((isDrivesTab || isAppTab) && hasAppConfig) {
      if (!selectedApp) {
        showError('Please select an app');
        return false;
      }
      // Check if file browser is shown and files/folders are selected
      if (showFileBrowser && selectedFiles.length === 0 && selectedFolders.length === 0) {
        showError('Please select at least one file or folder');
        return false;
      }
      
      try {
        setIsCreatingDataSource(true);
        
        // Get the app account details for the selected app
        if (!selectedApp.connectionDetails) {
          showError('No account found for the selected app');
          return false;
        }
        
        const connectedAccountDetails = selectedApp.connectionDetails as any;
        const connectionSettings = connectedAccountDetails.connection_settings as any;
        const connectionId = connectedAccountDetails.id;
        const appId = selectedApp.id;
        
        // Combine individual files and files from selected folders
        const allFilesToSubmit = [
          ...selectedFiles,
          ...selectedFolders.flatMap(folder => folderContents[folder.id] || [])
        ];
        
        // Remove duplicates based on file ID (in case a file was selected individually and also part of a selected folder)
        const uniqueFilesMap = new Map();
        allFilesToSubmit.forEach(file => {
          if (!uniqueFilesMap.has(file.id)) {
            uniqueFilesMap.set(file.id, file);
          }
        });
        const uniqueFiles = Array.from(uniqueFilesMap.values());
        
        // Show info message if duplicates were found
        if (uniqueFiles.length < allFilesToSubmit.length) {
          const duplicateCount = allFilesToSubmit.length - uniqueFiles.length;
          showSuccess(`Removed ${duplicateCount} duplicate file${duplicateCount > 1 ? 's' : ''} from submission`);
        }
        
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        
        // Check if this is a Connect App (non-drive app) without file selection
        const isConnectAppWithoutFiles = !isDriveApp(selectedApp) && !showFileBrowser && uniqueFiles.length === 0;
        
        if (isConnectAppWithoutFiles) {
          // For Connect Apps (non-drive apps), use APP_ACCOUNTS API
          // Check if app is already connected to this channel (similar to Jira handling)
          const targetChannelId = channelId || selectedWorkspace;
          const isAlreadyConnected = connectedAccountDetails.channel_id === targetChannelId;
          
          let url: string;
          let method: string;
          let requestBody: any;
          
          if (isAlreadyConnected && connectionId) {
            // Update existing connection - use PUT (similar to Jira)
            method = 'PUT';
            url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}${connectionId}`;
            requestBody = {
              connection_settings: connectionSettings
            };
          } else {
            // Create new connection - use POST
            method = 'POST';
            url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APP_ACCOUNTS.LIST}`;
            requestBody = {
              app_id: appId,
              channel_id: targetChannelId,
              connection_settings: connectionSettings
            };
          }
          
          const response = await fetch(url, {
            method: method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            // Handle "already exists" error gracefully
            if (response.status === 409 || errorData.message?.toLowerCase().includes('already') || errorData.message?.toLowerCase().includes('exists')) {
              showSuccess(`App is already connected to this channel.`);
              onDataSourceAdded?.();
              setRefreshTrigger(prev => prev + 1);
              if (!hideInternalButtons) {
                onClose?.();
              }
              return true;
            }
            const errorMessage = errorData.message || `Failed to ${isAlreadyConnected ? 'update' : 'add'} app account (Error ${response.status})`;
            showError(errorMessage);
            return false;
          }

          const data = await response.json();
          
          // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
          if (channelId) {
            try {
              const eventData = {
                type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                data: {
                  channelId: channelId
                }
              };
              socketService.sendSocketMessage(eventData);
            } catch (error) {
              console.error('Error emitting socket event:', error);
            }
          }
          
          showSuccess(`Success! Your app has been ${isAlreadyConnected ? 'updated' : 'connected'}.`);
          onDataSourceAdded?.();
          setRefreshTrigger(prev => prev + 1); // Refresh the connected datasources count
          if (!hideInternalButtons) {
            onClose?.(); // Only close if not in AddSpaceModal mode
          }
          return true; // Return true on success
        } else {
          // For drive apps or apps with file selection, use DATASOURCES API
          // Create datasource payloads
          let datasourcePayloads;
          
          // For drive apps with file selection, create a separate datasource payload for each unique file
          if (uniqueFiles.length === 0) {
            showError('Please select at least one file or folder');
            return false;
          }
          
          // Check if this is SharePoint to include additional fields
          const isSharePoint = selectedApp.app_key === 'sharepoint';
          
          datasourcePayloads = uniqueFiles.map(file => {
            // Base payload structure
            const basePayload = {
              storage_type: "app",
              app_provider: selectedApp.app_key,
              app_type: selectedApp.app_key,
              channel_id: channelId || selectedWorkspace,
              filename: file.name,
              file_size: file.size || 0,
              file_type: file.mimeType || "",
              is_embedding_required: true,
              config: {
                app_id: appId,
                connection_id: connectionId,
                connection_settings: {
                  account: {
                    "file_id": file.id,
                    "mime_type": file.mimeType,
                    "file_name": file.name
                  }
                },
              }
            };
            
            // Add SharePoint-specific fields
            if (isSharePoint) {
              // Get folder path as string (e.g., "Home > Folder1 > Folder2")
              const folderPathString = folderPath
                .map(f => f.name)
                .join(' > ');
              
              // Calculate relative file path (e.g., "/Documents/Folder1/file.csv")
              // Skip "Home" from the path and build relative URL
              const relativePathParts = folderPath
                .filter(f => f.name !== 'Home' && f.id !== undefined) // Skip root/Home
                .map(f => f.name);
              
              // Add filename to the path
              const relativeFilePath = relativePathParts.length > 0
                ? `/${relativePathParts.join('/')}/${file.name}`
                : `/${file.name}`;
              
              // Add SharePoint-specific fields to account object
              basePayload.config.connection_settings.account = {
                ...basePayload.config.connection_settings.account,
                "site_id": selectedSiteId || 'onedrive',
                "site_name": selectedSiteName || 'OneDrive',
                "site_url": selectedSiteUrl || '', // SharePoint site URL
                "tenant_id": connectionSettings.tenant_id,
                "folder_path": folderPathString, // Display path (e.g., "Home > Folder1")
                "file_path": relativeFilePath, // Relative URL path (e.g., "/Folder1/file.csv")
                "web_view_link": file.webViewLink,
                "download_url": file.downloadUrl
              } as any;
            }
            
            return basePayload;
          });
          
          // Safety check: ensure we have at least one datasource payload
          if (!datasourcePayloads || datasourcePayloads.length === 0) {
            showError('No data source to add. Please select files or ensure the app is properly configured.');
            return false;
          }

          const createBulkUrlDrive = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.CREATE_BULK}`;
          const createBulkHeadersDrive: Record<string, string> = {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          };
          const createBulkBodyDrive = JSON.stringify({ datasources: datasourcePayloads });
          // Same as From Device: panel refresh + close while POST /datasources/bulk finishes
          onFirstBulkSuccess?.(uniqueFiles.map((f) => f.name));

          fetch(createBulkUrlDrive, {
            method: 'POST',
            headers: createBulkHeadersDrive,
            body: createBulkBodyDrive
          })
            .then(async (createResponse) => {
              if (!createResponse.ok) {
                const errorData = await createResponse.json().catch(() => ({}));
                const errorMessage = errorData.message || `Failed to add ${LanguageConstants.datasource.toLowerCase()} (Error ${createResponse.status})`;
                showError(errorMessage);
                return;
              }
              const result = await createResponse.json();
              if (result.total_failed > 0) {
                let errorMessage = `Failed to add ${LanguageConstants.datasource.toLowerCase()}`;
                if (result.failed_datasources?.[0]?.error) {
                  const dbError = result.failed_datasources[0].error;
                  if (dbError.includes('duplicate key')) {
                    errorMessage = `A ${LanguageConstants.datasource.toLowerCase()} with this app already exists.`;
                  } else {
                    errorMessage = `Error occurred while creating ${LanguageConstants.datasource.toLowerCase()}.`;
                  }
                }
                showError(errorMessage);
                return;
              }
              if (result.total_created > 0) {
                if (!onFirstBulkSuccess) {
                  showSuccess(`Success! Your ${LanguageConstants.datasource.toLowerCase()} has been added. We are now generating the embeddings. Please check back in a few minutes`);
                }
                if (channelId) {
                  try {
                    const eventData = {
                      type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                      data: { channelId: channelId }
                    };
                    socketService.sendSocketMessage(eventData);
                  } catch (error) {
                    console.error('Error emitting socket event:', error);
                  }
                }
                // For SharePoint datasources: trigger backend file download via refresh endpoint.
                // W-1: collect all refresh promises with Promise.allSettled so failures are visible
                // to the user via a non-blocking warning toast instead of being silently dropped.
                if (selectedApp?.app_key === 'sharepoint' && Array.isArray(result.created_datasources)) {
                  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
                  const refreshPromises = result.created_datasources
                    .filter((ds: { id: string }) => ds?.id)
                    .map((ds: { id: string }) =>
                      fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.REFRESH(ds.id)}`, {
                        method: 'POST',
                        headers: {
                          'accept': 'application/json',
                          ...(refreshToken ? { 'Authorization': `Bearer ${refreshToken}` } : {}),
                        },
                      }).then(r => ({ id: ds.id, ok: r.ok }))
                       .catch(() => ({ id: ds.id, ok: false }))
                    );
                  Promise.allSettled(refreshPromises).then(results => {
                    const failed = results.filter(r => r.status === 'fulfilled' && !(r.value as any).ok).length;
                    if (failed > 0) {
                      showWarning(`${failed} SharePoint file(s) could not be indexed. They may appear empty. Please try re-adding them.`);
                    }
                  });
                }
                onDataSourceAdded?.();
                setRefreshTrigger(prev => prev + 1);
                if (!hideInternalButtons && !onFirstBulkSuccess) {
                  onClose?.();
                }
              } else {
                showError(`No ${LanguageConstants.datasource_s.toLowerCase()} were added`);
              }
            })
            .catch((err) => {
              console.error(`Error adding ${LanguageConstants.datasource.toLowerCase()} (create/bulk):`, err);
              const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource.toLowerCase()}. Please try again.`;
              showError(errorMessage);
            });

          return true;
        }
      } catch (err) {
        console.error(`Error adding ${LanguageConstants.datasource.toLowerCase()}:`, err);
        const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource.toLowerCase()}. Please try again.`;
        showError(errorMessage);
        return false;
      } finally {
        setIsCreatingDataSource(false);
      }
    }

    // Webscrapping tab (webscrapping_UI branch) — same single-POST pattern as Cloud (no
    // upload step; website has no file bytes), reusing the existing CREATE_BULK endpoint.
    if (isWebsiteTab) {
      let hasError = false;
      if (!websiteName.trim()) {
        setWebsiteNameError('Please enter a name');
        hasError = true;
      }
      const websiteUrlValidation = validateWebsiteUrl(websiteUrl);
      if (websiteUrlValidation.error) {
        setWebsiteUrlError(websiteUrlValidation.error);
        hasError = true;
      }
      if (!channelId && !selectedWorkspace) {
        setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
        hasError = true;
      }
      if (hasError) {
        return false;
      }

      try {
        setIsCreatingDataSource(true);
        // Level is a 1-3 dropdown (no invalid state possible); Max Pages is fixed at 50 and
        // not user-editable — both always sent explicitly per the scrape-preview spec.
        // Parsed with isNaN (falls back to the same 1 default as the dropdown's own initial
        // state) rather than assuming Number() always succeeds.
        const parsedCrawlDepth = Number(websiteCrawlDepth);
        const config = {
          url: websiteUrlValidation.normalizedUrl as string,
          crawl_depth: Number.isNaN(parsedCrawlDepth) ? 1 : parsedCrawlDepth,
          max_pages: 50,
        };

        const datasourcePayload = {
          storage_type: 'website',
          name: websiteName.trim(),
          channel_id: channelId || selectedWorkspace,
          // true — single-request flow: the backend crawls and embeds automatically
          // (status moves 4 scraping -> 1 in progress -> 2/3 completed/failed on its own),
          // no separate manual "Start Embedding" call needed. The Data Sources panel's
          // status-4 "Start Embedding" button (DataSourcesPanel.tsx) is left in place only
          // to unblock any pre-existing website datasource still parked at status 4 from
          // before this change.
          is_embedding_required: true,
          config,
        };

        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const createBulkUrl = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.CREATE_BULK}`;
        const createBulkHeaders: Record<string, string> = {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
        const createBulkBody = JSON.stringify({ datasources: [datasourcePayload] });
        const pendingWebsiteName = websiteName.trim();
        onFirstBulkSuccess?.([pendingWebsiteName]);

        fetch(createBulkUrl, {
          method: 'POST',
          headers: createBulkHeaders,
          body: createBulkBody
        })
          .then(async (createResponse) => {
            if (!createResponse.ok) {
              const errorData = await createResponse.json().catch(() => ({}));
              const errorMessage = errorData.message || `Failed to add ${LanguageConstants.datasource.toLowerCase()} (Error ${createResponse.status})`;
              showError(errorMessage);
              return;
            }
            const result = await createResponse.json();
            if (result.total_failed > 0) {
              let errorMessage = `Failed to add ${LanguageConstants.datasource.toLowerCase()}`;
              if (result.failed_datasources?.[0]?.error) {
                const websiteError = result.failed_datasources[0].error;
                if (websiteError.includes('duplicate key')) {
                  errorMessage = `A ${LanguageConstants.datasource.toLowerCase()} with this name already exists.`;
                } else {
                  errorMessage = `Failed to add ${LanguageConstants.datasource.toLowerCase()}: ${websiteError}`;
                }
              }
              showError(errorMessage);
              return;
            }
            if (result.total_created > 0) {
              if (!onFirstBulkSuccess) {
                showSuccess(`Success! Your ${LanguageConstants.datasource.toLowerCase()} has been added. We are now generating the embeddings. Please check back in a few minutes`);
              }
              if (channelId) {
                try {
                  const eventData = {
                    type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
                    data: { channelId: channelId }
                  };
                  socketService.sendSocketMessage(eventData);
                } catch (error) {
                  // Handle error silently
                }
              }
              onDataSourceAdded?.();
              setRefreshTrigger(prev => prev + 1);
              if (!hideInternalButtons && !onFirstBulkSuccess) {
                onClose?.();
              }
            } else {
              showError(`failed to add ${LanguageConstants.datasource.toLowerCase()}`);
            }
          })
          .catch((err) => {
            console.error(`Error adding ${LanguageConstants.datasource.toLowerCase()} (create/bulk):`, err);
            const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource.toLowerCase()}. Please try again.`;
            showError(errorMessage);
          });

        return true;
      } catch (err) {
        console.error(`Error adding ${LanguageConstants.datasource.toLowerCase()}:`, err);
        const errorMessage = err instanceof Error ? err.message : `Failed to add ${LanguageConstants.datasource.toLowerCase()}. Please try again.`;
        showError(errorMessage);
        return false;
      } finally {
        setIsCreatingDataSource(false);
      }
    }



    // If user hasn't configured anything on any tab, allow them to skip (return true)
    return true; // User can proceed to next step (datasource is optional)
  };
  const showFileError = Boolean(isDeviceTab && hasTouchedFile && !selectedFile);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header - Only show in AddSpaceModal flow */}
      {channelId && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {hideInternalButtons 
                      ? `Connect ${LanguageConstants.datasource.toLowerCase()} to your support ${LanguageConstants.topic.toLowerCase()}. You can add ${LanguageConstants.datasource.toLowerCase()} now or skip to continue.`
        : `Connect ${LanguageConstants.datasource.toLowerCase()} to your support ${LanguageConstants.topic.toLowerCase()}. This step is optional and can be completed later.`
            }
          </Typography>
        </>
      )}
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="${LanguageConstants.datasource.toLowerCase()} tabs"
          sx={modalStyles.tabs}
        >
          <Tab
            icon={<Computer />}
            label="From Device"
            iconPosition="start"
          />
          <Tab
            icon={<CloudUpload />}
            label="From Cloud"
            iconPosition="start"
          />
          <Tab
            icon={<Storage />}
            label="Connect to Database"
            iconPosition="start"
          />
          <Tab
            icon={<FolderShared />}
            label="Drives"
            iconPosition="start"
          />
          {/* Connect App hidden (webscrapping_UI branch) — kept as an invisible placeholder,
              not removed, so it stays at position 4 and every existing tabValue === 4 check
              elsewhere in this file (isAppTab, handleTabChange, fetchDatasourceApps, etc.)
              keeps targeting it exactly as before. The new Webscrapping tab below is a clean
              new index 5 so none of that pre-existing logic needs to change.
          <Tab
            icon={<Apps />}
            label="Connect App"
            iconPosition="start"
          />
          */}
          <Tab sx={{ display: 'none', minWidth: 0, padding: 0 }} disabled />
          <Tab
            icon={<Web />}
            label="Website"
            iconPosition="start"
          />
        </Tabs>
      </Box>
      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* From Device Tab */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!channelId && (
            <ChannelSelectDropdown
              value={selectedWorkspace}
              onChange={(value) => {
                setSelectedWorkspace(value);
                if (workspaceError) setWorkspaceError('');
              }}
              onBlur={() => {
                if (!selectedWorkspace) setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
              }}
              error={workspaceError}
              required={true}
              disabled={loadingChannels}
              onOpen={handleWorkspaceDropdownOpen}
              channels={channels}
              loadingChannels={loadingChannels}
              channelsError={channelsError}
            />
          )}

          {/* <FormControl fullWidth error={Boolean(logTypeError)}>
            <InputLabel>Type of Log *</InputLabel>
            <Select
              label="Type of Log *"
              value={logType}
              onChange={(e) => {
                setLogType(e.target.value as string);
                if (logTypeError) setLogTypeError('');
              }}
              onBlur={() => {
                if (!logType) setLogTypeError('Log type is required');
                else setLogTypeError('');
              }}
              disabled={loadingLogTypes}
            >
              <MenuItem value="">Select log type</MenuItem>
              {loadingLogTypes ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading log types...
                </MenuItem>
              ) : logTypesError ? (
                <MenuItem disabled>Error loading log types</MenuItem>
              ) : (
                supportedLogTypes.map((logType) => (
                  <MenuItem key={logType} value={logType}>
                    {capitalizeFirstLetter(logType)}
                  </MenuItem>
                ))
              )}
            </Select>
            {logTypeError && <FormHelperText>{logTypeError}</FormHelperText>}
          </FormControl> */}

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Upload {LanguageConstants.datasource}
            </Typography>
            <Box
              sx={{
                // modalStyles.uploadBox used #ccc/#f5f5f5 — breaks dark mode on hover
                border: `2px dashed ${muiTheme.palette.divider}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: deviceFiles.length === 0 ? 'pointer' : 'default',
                '&:hover':
                  deviceFiles.length === 0
                    ? {
                        borderColor: muiTheme.palette.primary.main,
                        backgroundColor: isDarkMode
                          ? muiTheme.palette.action.hover
                          : muiTheme.palette.grey[100],
                      }
                    : {},
              }}
              onClick={deviceFiles.length === 0 ? handleClickUploadBox : undefined}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (deviceFiles.length === 0 && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleClickUploadBox();
                }
              }}
              aria-label={deviceFiles.length > 0 ? 'Uploaded files preview' : 'Click to upload files from your device'}
            >
              {deviceFiles.length === 0 ? (
                <>
                  <CloudUpload sx={{ fontSize: 48, color: '#1cabde', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Click here to upload
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Supported Formats: csv, xlsx, xls, json, pdf, docx, doc, txt, pptx, ppt, md
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    File Size: upto 100 MB per file
                  </Typography>
                </>
              ) : (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      mb: 2,
                      justifyContent: 'center',
                      width: '100%'
                    }}
                  >
                    {deviceFiles.map((file, index) => {
                      const ext = getFileExtension(file.name);
                      return (
                        <Box
                          key={`${file.name}-${index}`}
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: 'background.paper',
                            width: 140,
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 1
                            }
                          }}
                        >
                          <IconButton
                            aria-label={`Remove ${file.name}`}
                            size="small"
                            onClick={() => handleRemoveFile(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              color: '#d32f2f',
                              backgroundColor: 'background.paper',
                              width: 24,
                              height: 24,
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'error.main'
                              }
                            }}
                          >
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Box sx={{ mb: 1, mt: 0.5 }}>
                            {ext === 'pdf' ? (
                              <PictureAsPdf sx={{ fontSize: 32, color: '#d32f2f' }} />
                            ) : ['csv', 'xls', 'xlsx'].includes(ext) ? (
                              <InsertDriveFile sx={{ fontSize: 32, color: '#2e7d32' }} />
                            ) : ['doc', 'docx'].includes(ext) ? (
                              <InsertDriveFile sx={{ fontSize: 32, color: '#1cabde' }} />
                            ) : (
                              <InsertDriveFile sx={{ fontSize: 32, color: '#616161' }} />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 500,
                              textAlign: 'center',
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                              lineHeight: 1.3,
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', mt: 0.5 }}
                          >
                            {formatBytes(file.size)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClickUploadBox}
                  >
                    Add more files
                  </Button>
                </Box>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept=".csv,.xlsx,.xls,.json,.pdf,.docx,.doc,.txt,.pptx,.ppt,.md"
                multiple
              />
            </Box>
            {showFileError && (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                Please upload at least one file
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Notes
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                We support key-value pairs scenarios in csv, xls, and xlsx formats.{" "}
                <Typography 
                  component="span" 
                  color="primary" 
                  sx={{ cursor: 'pointer' }}
                  onClick={handleDownloadKeyValueSample}
                >
                  Download sample file here
                </Typography>
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                The file format supported for text-to-SQL.{" "}
                <Typography 
                  component="span" 
                  color="primary" 
                  sx={{ cursor: 'pointer' }}
                  onClick={handleDownloadTextToSQLSample}
                >
                  Download sample file here
                </Typography>
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Ensure headers are unique, properly named, and free of duplicates or unnamed columns.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Headers should not include special characters, ASCII symbols, and not more than 255 characters long.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                The file should have a single sheet, and the sheet should contain only one table.
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                If a doc, docx or pdf has images, a query regarding image visuals will only work if GPT-4o-mini is connected in a group.
              </Typography>
              <Typography component="li" variant="body2">
                If the GPT-4o-mini model is not connected to the group, only queries related to the text inside the images will be answered.
              </Typography>
            </Box>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* From Cloud Tab */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!channelId && (
            <ChannelSelectDropdown
              value={cloudWorkspace}
              onChange={(value) => {
                setCloudWorkspace(value);
                if (cloudWorkspaceError) setCloudWorkspaceError('');
              }}
              onBlur={() => {
                if (!cloudWorkspace) setCloudWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
              }}
              error={cloudWorkspaceError}
              required={true}
              disabled={loadingChannels}
              onOpen={handleWorkspaceDropdownOpen}
              channels={channels}
              loadingChannels={loadingChannels}
              channelsError={channelsError}
            />
          )}

          <FormControl fullWidth error={Boolean(storageProviderError)}>
            <InputLabel>Storage Provider *</InputLabel>
            <Select
              label="Storage Provider *"
              value={storageProvider}
              MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
              onChange={(e) => {
                setStorageProvider(e.target.value as string);
                if (storageProviderError) setStorageProviderError('');
                // Clear all error messages when switching providers
                setLocationUrlError('');
                setSecretAccessKeyError('');
                setAccessKeyIdError('');
                setDescriptionError('');
                setCloudLogTypeError('');
                // Clear GCP-specific errors and reset file
                setGcpServiceAccountKeyError('');
                setGcpServiceAccountFile(null);
                setGcpServiceAccountKey('');
                setGcpProjectId('');
                if (gcpServiceAccountFileRef.current) {
                  gcpServiceAccountFileRef.current.value = '';
                }
              }}
              onBlur={() => {
                if (!storageProvider) setStorageProviderError('Please select storage provider');
              }}
            >
              <MenuItem value="">Select storage provider</MenuItem>
              <MenuItem value="aws">S3</MenuItem>
              <MenuItem value="azure">Azure</MenuItem>
              <MenuItem value="gcp">GCP</MenuItem>
            </Select>
            {storageProviderError && <FormHelperText>{storageProviderError}</FormHelperText>}
          </FormControl>

          {storageProvider && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Location URL *"
                placeholder="Enter location url.."
                value={locationUrl}
                onChange={(e) => {
                  setLocationUrl(e.target.value);
                  if (locationUrlError) setLocationUrlError('');
                }}
                onBlur={() => {
                  if (!locationUrl) setLocationUrlError('Please enter location URL');
                }}
                error={Boolean(locationUrlError)}
                helperText={locationUrlError}
                autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#bdbdbd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#bdbdbd',
                    },
                  },
                }}
              />
              {/* <FormControl fullWidth error={Boolean(cloudLogTypeError)}>
                <InputLabel>Type of Log *</InputLabel>
                <Select
                  label="Type of Log *"
                  value={cloudLogType}
                  onChange={(e) => {
                    setCloudLogType(e.target.value as string);
                    if (cloudLogTypeError) setCloudLogTypeError('');
                  }}
                  onBlur={() => {
                    if (!cloudLogType) setCloudLogTypeError('Log type is required');
                    else setCloudLogTypeError('');
                  }}
                  disabled={loadingLogTypes}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#bdbdbd',
                      },
                      '&:hover fieldset': {
                        borderColor: '#bdbdbd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#bdbdbd',
                      },
                    },
                  }}
                >
                  <MenuItem value="">Select log type</MenuItem>
                  {loadingLogTypes ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading log types...
                    </MenuItem>
                  ) : logTypesError ? (
                    <MenuItem disabled>Error loading log types</MenuItem>
                  ) : (
                    supportedLogTypes.map((logType) => (
                      <MenuItem key={logType} value={logType}>
                        {logType}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {cloudLogTypeError && <FormHelperText>{cloudLogTypeError}</FormHelperText>}
              </FormControl> */}
              {storageProvider === 'aws' && (
                <TextField
                  fullWidth
                  label="Access Key ID *"
                  placeholder="Enter access key ID.."
                  type={showAccessKeyId ? 'text' : 'password'}
                  value={accessKeyId}
                  onChange={(e) => {
                    setAccessKeyId(e.target.value);
                    if (accessKeyIdError) setAccessKeyIdError('');
                  }}
                  onBlur={() => {
                    if (!accessKeyId) setAccessKeyIdError('Please enter access key ID');
                  }}
                  error={Boolean(accessKeyIdError)}
                  helperText={accessKeyIdError}
                  autoComplete="off"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleToggleAccessKeyIdVisibility}
                          edge="end"
                        >
                          {showAccessKeyId ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#bdbdbd',
                      },
                      '&:hover fieldset': {
                        borderColor: '#bdbdbd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#bdbdbd',
                      },
                    },
                  }}
                />
              )}
              {storageProvider === 'gcp' && (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Service Account JSON *
                    </Typography>
                    {!gcpServiceAccountFile ? (
                      <Box
                        sx={{
                          border: '2px dashed',
                          borderColor: gcpServiceAccountKeyError ? 'error.main' : '#bdbdbd',
                          borderRadius: 1,
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => gcpServiceAccountFileRef.current?.click()}
                      >
                        <CloudUpload sx={{ fontSize: 48, color: '#1cabde', mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Click to upload Service Account JSON file
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Supported: .json files
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description sx={{ color: '#1976d2' }} />
                          <Typography variant="body2">{gcpServiceAccountFile.name}</Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setGcpServiceAccountFile(null);
                            setGcpServiceAccountKey('');
                            if (gcpServiceAccountFileRef.current) {
                              gcpServiceAccountFileRef.current.value = '';
                            }
                          }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    )}
                    <input
                      type="file"
                      ref={gcpServiceAccountFileRef}
                      style={{ display: 'none' }}
                      accept=".json"
                      onChange={handleGcpServiceAccountFileChange}
                    />
                    {gcpServiceAccountKeyError && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {gcpServiceAccountKeyError}
                      </FormHelperText>
                    )}
                  </Box>
              )}
              {storageProvider !== 'gcp' && (
                <TextField
                  fullWidth
                  label="Secret Access Key *"
                placeholder="Enter secret access key.."
                type={showSecretKey ? 'text' : 'password'}
                value={secretAccessKey}
                onChange={(e) => {
                  setSecretAccessKey(e.target.value);
                  if (secretAccessKeyError) setSecretAccessKeyError('');
                }}
                onBlur={() => {
                  if (!secretAccessKey) setSecretAccessKeyError('Please enter secret access key');
                }}
                error={Boolean(secretAccessKeyError)}
                helperText={secretAccessKeyError}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleSecretKeyVisibility}
                        edge="end"
                      >
                        {showSecretKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#bdbdbd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#bdbdbd',
                    },
                  },
                }}
                />
              )}
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Connect to Database Tab */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!channelId && (
            <ChannelSelectDropdown
              value={selectedWorkspace}
              onChange={(value) => {
                setSelectedWorkspace(value);
                if (workspaceError) setWorkspaceError('');
              }}
              onBlur={() => {
                if (!selectedWorkspace) setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
              }}
              error={workspaceError}
              required={true}
              disabled={loadingChannels}
              onOpen={handleWorkspaceDropdownOpen}
              channels={channels}
              loadingChannels={loadingChannels}
              channelsError={channelsError}
            />
          )}

          <FormControl fullWidth error={Boolean(databaseTypeError)} required>
            <InputLabel>Database Type</InputLabel>
            <Select
              label="Database Type"
              value={databaseType}
              MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
              onChange={(e) => {
                const newDbType = e.target.value as string;
                setDatabaseType(newDbType);
                if (databaseTypeError) setDatabaseTypeError('');
                // Reset all input fields when changing database type
                setDbHostname('');
                setDbHostnameError('');
                setDbPort('');
                setDbPortError('');
                setDbUsername('');
                setBigQueryServiceAccountFile(null);
                setBigQueryServiceAccountKey('');
                setBigQueryServiceAccountKeyError('');
                if (bigQueryServiceAccountFileRef.current) {
                  bigQueryServiceAccountFileRef.current.value = '';
                }
                setDbUsernameError('');
                setDbPassword('');
                setDbPasswordError('');
                setDbDatabaseName('');
                setDbDatabaseNameError('');
                setDbServiceName('');
                setDbSchemaName('');
                setDbWarehouse('');
                setDbAccountId('');
                setDbAccessKeyId('');
                setDbSecretAccessKey('');
                setDbRegionName('');
                // Reset connection state when changing database type
                setConnectionTested(false);
                setVaultId('');
                setTables([]);
                setSelectedTables([]);
                setTablesError('');
                setPreviewError('');
                setTablePreview({});
                setPreviewTableName('');
                // Reset existing connection state
                setExistingDbConnections([]);
                setSelectedExistingConnection('');
                setShowNewConnectionForm(false);
                setOriginalSelectedTables([]);
              }}
              onBlur={() => {
                if (!databaseType) setDatabaseTypeError('Please select database type');
              }}
            >
              <MenuItem value="">Select database type</MenuItem>
              <MenuItem value="oracle">Oracle</MenuItem>
              {/* Uncomment below for later use
              <MenuItem value="postgresql">PostgreSQL</MenuItem>
              <MenuItem value="mysql">MySQL</MenuItem>
              <MenuItem value="mariadb">MariaDB</MenuItem>
              */}
              <MenuItem value="redshift">Redshift</MenuItem>
              {/* Uncomment below for later use
              <MenuItem value="snowflake">Snowflake</MenuItem>
              <MenuItem value="bigquery">BigQuery</MenuItem>
              */}
              <MenuItem value="dynamodb">DynamoDB</MenuItem>
            </Select>
            {databaseTypeError && <FormHelperText>{databaseTypeError}</FormHelperText>}
          </FormControl>

          {databaseType && (
            <>
              {/* Existing Connections Section */}
              {loadingExistingConnections ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Checking existing connections...
                  </Typography>
                </Box>
              ) : existingDbConnections.length > 0 && !showNewConnectionForm ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {databaseType.charAt(0).toUpperCase() + databaseType.slice(1)} Connection
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select an account you'd like to continue with
                      </Typography>
                    </Box>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        setShowNewConnectionForm(true);
                        setSelectedExistingConnection('');
                        setConnectionTested(false);
                        setVaultId('');
                        setTables([]);
                        setSelectedTables([]);
                        setOriginalSelectedTables([]);
                      }}
                      sx={{ 
                        color: muiTheme.palette.primary.main,
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      + New account
                    </Link>
                  </Box>
                  <FormControl fullWidth required>
                    <InputLabel>Connected Accounts</InputLabel>
                    <Select
                      label="Connected Accounts"
                      value={selectedExistingConnection}
                      MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
                      onChange={(e) => handleSelectExistingConnection(e.target.value as string)}
                    >
                      {existingDbConnections.map((conn) => {
                        const hostname = conn.config?.hostname || '';
                        const dbName = conn.config?.databaseName || conn.config?.database_name || '';
                        const displayText = hostname && dbName ? `${hostname}/${dbName}` : hostname || dbName || conn.name;
                        return (
                          <MenuItem key={conn.vault_id} value={conn.vault_id}>
                            {displayText}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              ) : null}

              {/* Show back link when in new connection form and existing connections exist */}
              {showNewConnectionForm && existingDbConnections.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setShowNewConnectionForm(false);
                      setConnectionTested(false);
                      setVaultId('');
                      setTables([]);
                      setSelectedTables([]);
                      setOriginalSelectedTables([]);
                      // Reset form fields
                      setDbHostname('');
                      setDbPort('');
                      setDbUsername('');
                      setDbPassword('');
                      setDbDatabaseName('');
                      setDbServiceName('');
                      setDbSchemaName('');
                    }}
                    sx={{ 
                      color: muiTheme.palette.primary.main,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    ← Back to existing accounts
                  </Link>
                </Box>
              )}

              {/* Show connection form only when not loading AND (no existing connections OR showNewConnectionForm is true) */}
              {!loadingExistingConnections && (existingDbConnections.length === 0 || showNewConnectionForm) && (
                <>
              {/* Snowflake: Account ID shown first */}
              {databaseType === 'snowflake' && (
                <TextField
                  fullWidth
                  label="Account ID"
                  placeholder="Enter account ID"
                  value={dbAccountId}
                  onChange={(e) => {
                    setDbAccountId(e.target.value);
                  }}
                  onBlur={() => {
                    if (!dbAccountId) {
                      // Validation will be handled in test connection
                    }
                  }}
                  required
                />
              )}

              {/* Common fields: Hostname and Port (not for DynamoDB, BigQuery, optional for Snowflake) */}
              {databaseType !== 'dynamodb' && databaseType !== 'snowflake' && databaseType !== 'bigquery' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Hostname"
                      placeholder="Enter hostname"
                      value={dbHostname}
                      onChange={(e) => {
                        setDbHostname(e.target.value);
                        if (dbHostnameError) setDbHostnameError('');
                      }}
                      onBlur={() => {
                        if (!dbHostname) setDbHostnameError('Please enter hostname');
                      }}
                      error={Boolean(dbHostnameError)}
                      helperText={dbHostnameError}
                      required
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Port"
                      placeholder="Enter port number"
                      type="number"
                      value={dbPort}
                      onChange={(e) => {
                        setDbPort(e.target.value);
                        if (dbPortError) setDbPortError('');
                      }}
                      onBlur={() => {
                        if (!dbPort) setDbPortError('Please enter port');
                      }}
                      error={Boolean(dbPortError)}
                      helperText={dbPortError}
                      required
                    />
                  </Box>
                </Box>
              )}

              {/* Username and Password (for all except BigQuery and DynamoDB) */}
              {databaseType !== 'bigquery' && databaseType !== 'dynamodb' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Username"
                      placeholder="Enter username"
                      value={dbUsername}
                      onChange={(e) => {
                        setDbUsername(e.target.value);
                        if (dbUsernameError) setDbUsernameError('');
                      }}
                      onBlur={() => {
                        if (!dbUsername) setDbUsernameError('Please enter username');
                      }}
                      error={Boolean(dbUsernameError)}
                      helperText={dbUsernameError}
                      required
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Password"
                      placeholder="Enter password"
                      type={showPassword ? 'text' : 'password'}
                      value={dbPassword}
                      onChange={(e) => {
                        setDbPassword(e.target.value);
                        if (dbPasswordError) setDbPasswordError('');
                      }}
                      onBlur={() => {
                        if (!dbPassword) setDbPasswordError('Please enter password');
                      }}
                      error={Boolean(dbPasswordError)}
                      helperText={dbPasswordError}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Database Name (required for PostgreSQL, MySQL, MariaDB, optional for others) */}
              {databaseType !== 'dynamodb' && databaseType !== 'bigquery' && databaseType !== 'redshift' && databaseType !== 'snowflake' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: databaseType === 'oracle' ? '1 1 calc(50% - 8px)' : '0 1 calc(50% - 8px)', minWidth: '200px', maxWidth: databaseType === 'oracle' ? 'none' : 'calc(50% - 8px)' }}>
                    <TextField
                      fullWidth
                      label="Database Name"
                      placeholder="Enter database name"
                      value={dbDatabaseName}
                      onChange={(e) => {
                        setDbDatabaseName(e.target.value);
                        if (dbDatabaseNameError) setDbDatabaseNameError('');
                      }}
                      onBlur={() => {
                        if ((databaseType === 'postgresql' || databaseType === 'mysql' || databaseType === 'mariadb' || databaseType === 'oracle') && !dbDatabaseName) {
                          setDbDatabaseNameError('Please enter database name');
                        }
                      }}
                      error={Boolean(dbDatabaseNameError)}
                      helperText={dbDatabaseNameError}
                      required={databaseType === 'postgresql' || databaseType === 'mysql' || databaseType === 'mariadb' || databaseType === 'oracle'}
                    />
                  </Box>

                  {/* Database-specific fields */}
                  {databaseType === 'oracle' && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        label="Service Name"
                        placeholder="Enter service name"
                        value={dbServiceName}
                        onChange={(e) => setDbServiceName(e.target.value)}
                        required
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Database Name and Schema (required for Redshift) */}
              {databaseType === 'redshift' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Database Name"
                      placeholder="Enter database name"
                      value={dbDatabaseName}
                      onChange={(e) => {
                        setDbDatabaseName(e.target.value);
                        if (dbDatabaseNameError) setDbDatabaseNameError('');
                      }}
                      onBlur={() => {
                        if (!dbDatabaseName) {
                          setDbDatabaseNameError('Please enter database name');
                        }
                      }}
                      error={Boolean(dbDatabaseNameError)}
                      helperText={dbDatabaseNameError}
                      required
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Schema"
                      placeholder="Enter schema"
                      value={dbSchemaName}
                      onChange={(e) => setDbSchemaName(e.target.value)}
                      required
                    />
                  </Box>
                </Box>
              )}

              {/* Database Name and Warehouse (required for Snowflake) */}
              {databaseType === 'snowflake' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Database Name"
                      placeholder="Enter database name"
                      value={dbDatabaseName}
                      onChange={(e) => {
                        setDbDatabaseName(e.target.value);
                        if (dbDatabaseNameError) setDbDatabaseNameError('');
                      }}
                      onBlur={() => {
                        if (!dbDatabaseName) {
                          setDbDatabaseNameError('Please enter database name');
                        }
                      }}
                      error={Boolean(dbDatabaseNameError)}
                      helperText={dbDatabaseNameError}
                      required
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <TextField
                      fullWidth
                      label="Warehouse"
                      placeholder="Enter warehouse"
                      value={dbWarehouse}
                      onChange={(e) => setDbWarehouse(e.target.value)}
                      required
                    />
                  </Box>
                </Box>
              )}

              {/* Schema Name (required for Snowflake) */}
              {databaseType === 'snowflake' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '0 1 calc(50% - 8px)', minWidth: '200px', maxWidth: 'calc(50% - 8px)' }}>
                    <TextField
                      fullWidth
                      label="Schema Name"
                      placeholder="Enter schema name"
                      value={dbSchemaName}
                      onChange={(e) => setDbSchemaName(e.target.value)}
                      required
                    />
                  </Box>
                </Box>
              )}

              {/* BigQuery specific fields */}
              {databaseType === 'bigquery' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        label="Project ID"
                        placeholder="Enter project ID"
                        value={dbDatabaseName}
                        onChange={(e) => {
                          setDbDatabaseName(e.target.value);
                          if (dbDatabaseNameError) setDbDatabaseNameError('');
                        }}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        label="Dataset ID"
                        placeholder="Enter dataset ID"
                        value={dbSchemaName}
                        onChange={(e) => setDbSchemaName(e.target.value)}
                        required
                      />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Service Account JSON *
                    </Typography>
                    {!bigQueryServiceAccountFile ? (
                      <Box
                        sx={{
                          border: '2px dashed',
                          borderColor: bigQueryServiceAccountKeyError ? 'error.main' : '#bdbdbd',
                          borderRadius: 1,
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => bigQueryServiceAccountFileRef.current?.click()}
                      >
                        <CloudUpload sx={{ fontSize: 48, color: '#1cabde', mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Click to upload Service Account JSON file
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Supported: .json files
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description sx={{ color: '#1976d2' }} />
                          <Typography variant="body2">{bigQueryServiceAccountFile.name}</Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setBigQueryServiceAccountFile(null);
                            setBigQueryServiceAccountKey('');
                            if (bigQueryServiceAccountFileRef.current) {
                              bigQueryServiceAccountFileRef.current.value = '';
                            }
                          }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    )}
                    <input
                      type="file"
                      ref={bigQueryServiceAccountFileRef}
                      style={{ display: 'none' }}
                      accept=".json"
                      onChange={handleBigQueryServiceAccountFileChange}
                    />
                    {bigQueryServiceAccountKeyError && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {bigQueryServiceAccountKeyError}
                      </FormHelperText>
                    )}
                  </Box>
                </>
              )}

              {databaseType === 'dynamodb' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        label="Access Key ID"
                        placeholder="Enter access key ID"
                        type={showAccessKeyId ? 'text' : 'password'}
                        value={dbAccessKeyId}
                        onChange={(e) => setDbAccessKeyId(e.target.value)}
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowAccessKeyId(!showAccessKeyId)}
                                edge="end"
                              >
                                {showAccessKeyId ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                      <TextField
                        fullWidth
                        label="Secret Access Key"
                        placeholder="Enter secret access key"
                        type={showSecretKey ? 'text' : 'password'}
                        value={dbSecretAccessKey}
                        onChange={(e) => setDbSecretAccessKey(e.target.value)}
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowSecretKey(!showSecretKey)}
                                edge="end"
                              >
                                {showSecretKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '0 1 calc(50% - 8px)', minWidth: '200px', maxWidth: 'calc(50% - 8px)' }}>
                      <TextField
                        fullWidth
                        label="Region Name"
                        placeholder="Enter region name (e.g., us-east-1)"
                        value={dbRegionName}
                        onChange={(e) => setDbRegionName(e.target.value)}
                        required
                      />
                    </Box>
                  </Box>
                </>
              )}

              {!connectionTested && (
                <Button
                  variant="contained"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !databaseType}
                  startIcon={testingConnection ? <CircularProgress size={20} /> : null}
                  sx={{ alignSelf: 'flex-end' }} // Right-align in the Connect to Database column layout
                >
                  {testingConnection ? 'Testing Connection...' : 'Test Connection'}
                </Button>
              )}
                </>
              )}

              {/* Tables section - shown when connection is tested (either new or existing) */}
              {connectionTested && (
                <>
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Database connection successful
                  </Alert>

                  {loadingTables && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Fetching tables...</Typography>
                    </Box>
                  )}

                  {tablesError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {tablesError}
                    </Alert>
                  )}

                  {tables.length > 0 && (
                    <>
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <Autocomplete
                          multiple
                          options={tables}
                          value={selectedTables}
                          slotProps={{
                            popper: {
                              sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 },
                            },
                          }}
                          onChange={(event, newValue) => {
                            setSelectedTables(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Tables *"
                              placeholder="Select tables to add as data sources"
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option}
                                size="small"
                              />
                            ))
                          }
                        />
                      </FormControl>

                      {selectedTables.length > 0 && (
                        <Button
                          variant="outlined"
                          onClick={handleOpenPreviewModal}
                          startIcon={<TableChart />}
                          sx={{ alignSelf: 'flex-start', mt: 2 }}
                        >
                          Preview Tables ({selectedTables.length})
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        {/* Preview Modal */}
        <Dialog
          open={openPreviewModal}
          onClose={() => setOpenPreviewModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Preview Table Data</Typography>
              <IconButton onClick={() => setOpenPreviewModal(false)} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl sx={{ maxWidth: 400 }}>
                <InputLabel>Select Table to Preview</InputLabel>
                <Select
                  value={previewTableName}
                  MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
                  onChange={(e) => handlePreviewTableChange(e.target.value as string)}
                  label="Select Table to Preview"
                >
                  {selectedTables.map((table) => (
                    <MenuItem key={table} value={table}>
                      {table}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingPreview && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {previewError && (
                <Alert severity="error">{previewError}</Alert>
              )}

              {previewTableName && tablePreview[previewTableName] && !loadingPreview && (
                <TableContainer 
                  component={Paper} 
                  sx={{
                    width: '100%',
                    maxHeight: 400,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    backgroundColor: 'background.paper',
                    boxShadow: 2,
                  }}
                >
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        {tablePreview[previewTableName].columns.map((column) => (
                          <TableCell 
                            key={column} 
                            sx={{ 
                              ...getTableHeaderStyles(isDarkMode), 
                              ...getTableCellStyles(),
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {column}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tablePreview[previewTableName].rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex} hover sx={getTableRowStyles()}>
                          {row.map((cell, cellIndex) => (
                            <TableCell 
                              key={cellIndex}
                              sx={{
                                ...getTableCellStyles(),
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                minWidth: 150,
                              }}
                            >
                              {cell !== null && cell !== undefined ? String(cell) : '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {previewTableName && tablePreview[previewTableName] && tablePreview[previewTableName].rows.length === 0 && !loadingPreview && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No data available in this table
                </Typography>
              )}

              {previewTableName && tablePreview[previewTableName] && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Showing first 10 rows
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPreviewModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* Drives Tab */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Channel Selection Dropdown - Only show when channelId is not provided */}
          {!channelId && (
            <ChannelSelectDropdown
              value={selectedWorkspace}
              onChange={(value) => {
                setSelectedWorkspace(value);
                if (workspaceError) setWorkspaceError('');
              }}
              onBlur={() => {
                if (!selectedWorkspace) setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
              }}
              error={workspaceError}
              required={true}
              disabled={loadingChannels}
              onOpen={handleWorkspaceDropdownOpen}
              channels={channels}
              loadingChannels={loadingChannels}
              channelsError={channelsError}
            />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              Connect your drives to automatically import {LanguageConstants.datasource.toLowerCase()}.
            </Typography>

          {/* Loading State */}
          {loadingApps && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {appsError && (
            <Alert severity="error">{appsError}</Alert>
          )}

          {/* Apps Grid */}
          {!loadingApps && !appsError && drivesApps.length > 0 && (
            <Box 
              ref={appsScrollContainerRef}
              onScroll={handleAppsScroll}
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                maxHeight: '350px',
                overflowY: 'auto',
                pr: 1,
                pt: 0.5
              }}
            >
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 240px))',
                gap: 1.5
              }}>
              {drivesApps.map((app) => (
                <Paper
                  key={app.id}
                  elevation={selectedApp?.id === app.id ? 3 : 1}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: selectedApp?.id === app.id 
                      ? `2px solid ${muiTheme.palette.primary.main}` 
                      : `1px solid ${muiTheme.palette.divider}`,
                    backgroundColor: muiTheme.palette.background.paper,
                    cursor: app.isConnected ? 'pointer' : 'default',
                    transition: 'all 0.2s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: '140px',
                    position: 'relative',
                    '&:hover': app.isConnected ? {
                      boxShadow: 3,
                      transform: 'translateY(-2px)'
                    } : {}
                  }}
                  onClick={() => app.isConnected && handleSelectApp(app)}
                >
                  {/* Selection Indicator */}
                  {selectedApp?.id === app.id && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                      }}
                    >
                      <CheckCircle sx={{ color: muiTheme.palette.primary.main, fontSize: 20 }} />
                    </Box>
                  )}

                  {/* App Name + Connection Status */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ flex: 1, pr: selectedApp?.id === app.id ? 3 : 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.3 }}>
                        {app.app_name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: muiTheme.palette.text.secondary,
                          fontSize: '0.7rem'
                        }}
                      >
                        {app.category}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Connection Status Chip */}
                  {app.isConnected && (
                    <Chip
                      label="Connected"
                      size="small"
                      sx={{
                        backgroundColor: muiTheme.palette.success.light + '20',
                        color: muiTheme.palette.success.dark,
                        fontSize: '0.6rem',
                        height: 18,
                        mb: 1,
                        alignSelf: 'flex-start',
                        '& .MuiChip-label': {
                          px: 0.8
                        }
                      }}
                    />
                  )}

                  {/* App Icon + Connect Button */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 'auto',
                    pt: 1
                  }}>
                    {/* App Icon */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: imageErrors[app.id] ? muiTheme.palette.grey[200] : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        color: imageErrors[app.id] ? muiTheme.palette.text.secondary : 'transparent',
                        flexShrink: 0,
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      {imageErrors[app.id] ? (
                        app.app_name.charAt(0).toUpperCase()
                      ) : (
                        <img
                          src={app.app_image}
                          alt={`${app.app_name} icon`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                          onError={() => setImageErrors(prev => ({ ...prev, [app.id]: true }))}
                        />
                      )}
                    </Box>

                    {/* Connect/Select Button */}
                    {!app.isConnected ? (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={connectingApps[app.app_key] || !channelService.isUserAdmin()}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConnect(app.app_key, app.id);
                        }}
                        sx={{
                          backgroundColor: muiTheme.palette.primary.main,
                          color: 'white',
                          fontSize: '0.7rem',
                          py: 0.4,
                          px: 1.2,
                          minWidth: '80px',
                          '&:hover': {
                            backgroundColor: muiTheme.palette.primary.dark,
                          },
                          '&:disabled': {
                            backgroundColor: muiTheme.palette.grey[300],
                            color: muiTheme.palette.text.disabled,
                          }
                        }}
                      >
                        {connectingApps[app.app_key] ? (
                          <>
                            <CircularProgress size={12} sx={{ mr: 0.5, color: 'inherit' }} />
                            <Typography sx={{ fontSize: '0.65rem' }}>Connecting</Typography>
                          </>
                        ) : (
                          'CONNECT'
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant={selectedApp?.id === app.id ? "contained" : "outlined"}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectApp(app);
                        }}
                        sx={{
                          backgroundColor: selectedApp?.id === app.id ? muiTheme.palette.primary.main : 'transparent',
                          color: selectedApp?.id === app.id ? 'white' : muiTheme.palette.primary.main,
                          fontSize: '0.7rem',
                          py: 0.4,
                          px: 1.2,
                          minWidth: '70px',
                          borderColor: muiTheme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: selectedApp?.id === app.id ? muiTheme.palette.primary.dark : muiTheme.palette.primary.light + '20',
                            borderColor: muiTheme.palette.primary.main,
                          }
                        }}
                      >
                        {selectedApp?.id === app.id ? 'SELECTED' : 'SELECT'}
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))}
              </Box>

              {/* Loading more indicator */}
              {loadingMoreApps && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* End of results indicator */}
              {!hasMoreApps && drivesApps.length > 12 && (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    No more drives to load
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Empty State */}
          {!loadingApps && !appsError && drivesApps.length === 0 && (
            <Box sx={{ 
              textAlign: 'center', 
              py: 3,
              color: 'text.secondary'
            }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontSize: '0.95rem' }}>
                No drives available
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                There are currently no drive applications to connect.
              </Typography>
            </Box>
          )}

          {/* Step-based Google Drive Flow - Show for Drives tab */}
          {showFileBrowser && selectedApp && isDrivesTab && (
            <Box sx={{ mt: 2 }}>
              {/* Stepper */}
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  mb: 3,
                  border: `1px solid ${muiTheme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: driveWizardSurface, // Theme paper — dark in dark mode
                }}
              >
                <Stepper activeStep={getActiveStepperStep()} alternativeLabel>
                  {fileSelectionSteps.map((label, index) => (
                    <Step key={label} completed={index < getActiveStepperStep()}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Paper>

              {/* Step Content */}
              <Box>
                {/* Step 1: Review Notes */}
                {fileSelectionStep === 1 && (
                  <Box sx={{ 
                    border: `1px solid ${muiTheme.palette.divider}`, 
                    borderRadius: 2, 
                    overflow: 'hidden', 
                    backgroundColor: driveWizardSurface, // Match dialog / stepper in dark mode
                    transition: 'all 0.3s ease',
                    p: 1.5
                  }}>
                    <Typography variant="h6" sx={{ mb: 1.5, color: 'text.primary' }}>
                      Notes
                    </Typography>
                
                <Box component="ul" sx={{ 
                  listStyle: 'none', 
                  p: 0, 
                  m: 0,
                  '& li': {
                    mb: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    color: muiTheme.palette.text.secondary,
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    transition: 'color 0.3s ease'
                  }
                }}>
                  <li>
                    {dataSourceInfoAccepted ? (
                      <CheckCircle sx={{ 
                        mr: 1.5, 
                        mt: 0.2,
                        fontSize: 20,
                        color: muiTheme.palette.success.main,
                        flexShrink: 0
                      }} />
                    ) : (
                      <Box component="span" sx={{ 
                        mr: 1.5, 
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: muiTheme.palette.text.secondary,
                        flexShrink: 0,
                        alignSelf: 'center'
                      }} />
                    )}
                    Folders with maximum {DATA_SOURCE_LIMIT} files can be selected
                  </li>
                  <li>
                    {dataSourceInfoAccepted ? (
                      <CheckCircle sx={{ 
                        mr: 1.5, 
                        mt: 0.2,
                        fontSize: 20,
                        color: muiTheme.palette.success.main,
                        flexShrink: 0
                      }} />
                    ) : (
                      <Box component="span" sx={{ 
                        mr: 1.5, 
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: muiTheme.palette.text.secondary,
                        flexShrink: 0,
                        alignSelf: 'center'
                      }} />
                    )}
                    Each file has a size limit of 100 MB
                  </li>
                  <li>
                    {dataSourceInfoAccepted ? (
                      <CheckCircle sx={{ 
                        mr: 1.5, 
                        mt: 0.2,
                        fontSize: 20,
                        color: muiTheme.palette.success.main,
                        flexShrink: 0
                      }} />
                    ) : (
                      <Box component="span" sx={{ 
                        mr: 1.5, 
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: muiTheme.palette.text.secondary,
                        flexShrink: 0,
                        alignSelf: 'center'
                      }} />
                    )}
                    Accepted file formats: {getAllowedFileFormatsForDisplay()}
                  </li>
                  <li>
                    {dataSourceInfoAccepted ? (
                      <CheckCircle sx={{ 
                        mr: 1.5, 
                        mt: 0.2,
                        fontSize: 20,
                        color: muiTheme.palette.success.main,
                        flexShrink: 0
                      }} />
                    ) : (
                      <Box component="span" sx={{ 
                        mr: 1.5, 
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: muiTheme.palette.text.secondary,
                        flexShrink: 0,
                        alignSelf: 'center'
                      }} />
                    )}
                    Folders with sub-folders can't be selected
                  </li>
                  <li>
                    {dataSourceInfoAccepted ? (
                      <CheckCircle sx={{ 
                        mr: 1.5, 
                        mt: 0.2,
                        fontSize: 20,
                        color: muiTheme.palette.success.main,
                        flexShrink: 0
                      }} />
                    ) : (
                      <Box component="span" sx={{ 
                        mr: 1.5, 
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: muiTheme.palette.text.secondary,
                        flexShrink: 0,
                        alignSelf: 'center'
                      }} />
                    )}
                    For the chosen folders, read-only access will be applied
                  </li>
                </Box>

                {/* Acceptance Checkbox */}
                <Box 
                  ref={acceptanceCheckboxRef}
                  sx={{ 
                    mt: 1.5, 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 1.5,
                    backgroundColor: driveWizardMutedSurface, // Nested strip: dark grey in dark mode
                    borderRadius: 1,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    transition: 'all 0.3s ease'
                  }}>
                  <Checkbox
                    checked={dataSourceInfoAccepted}
                    onChange={handleAcceptanceChange}
                    id="accept_data_source_info"
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      },
                      p:0,
                      color: undefined,
                      '&.Mui-checked': {
                        color: muiTheme.palette.success.main
                      }
                    }}
                  />
                  <Typography 
                    component="label" 
                    htmlFor="accept_data_source_info"
                    sx={{ 
                      ml: 1, 
                      cursor: 'pointer',
                      fontWeight: 500,
                      color: muiTheme.palette.text.primary,
                      userSelect: 'none',
                      transition: 'color 0.3s ease'
                    }}
                  >
                    Yes, I understand
                  </Typography>
                </Box>
                  </Box>
                )}

                {/* Step 2: Browse Files */}
                {fileSelectionStep === 2 && dataSourceInfoAccepted && (
            <Box 
              ref={fileBrowserRef}
              sx={{ 
                mt: 2, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
                position: 'relative'
              }}>
              {/* File Explorer Section - Full Width */}
              <Box sx={{ 
                border: `1px solid ${muiTheme.palette.divider}`, 
                borderRadius: 2, 
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '500px',
                backgroundColor: driveWizardSurface,
              }}>
                  {/* Header with App Info */}
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: driveWizardMutedSurface, 
                    color: muiTheme.palette.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexShrink: 0,
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          // backgroundColor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}
                      >
                        {imageErrors[selectedApp.id] ? (
                          <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: muiTheme.palette.primary.main }}>
                            {selectedApp.app_name.charAt(0).toUpperCase()}
                          </Typography>
                        ) : (
                          <img
                            src={selectedApp.app_image}
                            alt={`${selectedApp.app_name} icon`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                            onError={() => setImageErrors(prev => ({ ...prev, [selectedApp.id]: true }))}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {selectedApp.app_name}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Browse and select files
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Site Selector - Only for SharePoint */}
                    {selectedApp?.app_key === 'sharepoint' && (
                      <FormControl 
                        size="small" 
                        sx={{ minWidth: 200 }}
                        disabled={loadingSites}
                      >
                        <InputLabel>Site</InputLabel>
                        <Select
                          value={selectedSiteId || ''}
                          MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
                          onChange={async (event) => {
                            const newSiteId = event.target.value as string;
                            const selectedSite = availableSites.find(s => s.id === newSiteId);
                            
                            setSelectedSiteId(newSiteId);
                            setSelectedSiteName(selectedSite?.name || 'OneDrive');
                            setSelectedSiteUrl(selectedSite?.webUrl || '');
                            
                            // Reset folder navigation
                            setCurrentFolderId(undefined);
                            setFolderPath([{ id: undefined, name: 'Home' }]);
                            setSelectedFiles([]);
                            setFilesAndFolders([]);
                            
                            // Fetch root files for new site — pass newSiteId directly to avoid stale state
                            if (selectedApp) {
                              await fetchFilesAndFolders(selectedApp, undefined, undefined, newSiteId);
                            }
                          }}
                          label="Site"
                        >
                          {loadingSites ? (
                            <MenuItem disabled>Loading sites...</MenuItem>
                          ) : (
                            availableSites.map(site => (
                              <MenuItem key={site.id} value={site.id}>
                                {site.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </Box>

                  {/* Google Drive: search bar (drive type chosen via folder icon menu in breadcrumb) */}
                  {selectedApp?.app_key === 'google_drive' && (
                    <Box sx={{ px: 2, pb: 1.5, pt: 0, borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Search folders or files..."
                          value={driveSearchQuery}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDriveSearchQuery(value);
                            if (driveSearchDebounceRef.current) clearTimeout(driveSearchDebounceRef.current);
                            driveSearchDebounceRef.current = setTimeout(() => {
                              driveSearchDebounceRef.current = null;
                              if (!selectedApp) return;
                              const trimmed = value.trim();
                              if (trimmed) {
                                if (googleDriveType === 'home') {
                                  fetchFilesAndFolders(selectedApp, undefined, { driveType: 'myDrive', sharedDriveId: undefined, searchQuery: trimmed });
                                } else {
                                  fetchFilesAndFolders(selectedApp, currentFolderId, { driveType: googleDriveType, sharedDriveId: googleSharedDriveId, searchQuery: trimmed });
                                }
                              } else {
                                if (googleDriveType === 'home') {
                                  setFilesAndFolders([]);
                                  setFilesError(null);
                                } else {
                                  fetchFilesAndFolders(selectedApp, currentFolderId, { driveType: googleDriveType, sharedDriveId: googleSharedDriveId, searchQuery: '' });
                                }
                              }
                            }, 400);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (driveSearchDebounceRef.current) clearTimeout(driveSearchDebounceRef.current);
                              driveSearchDebounceRef.current = null;
                              if (!selectedApp) return;
                              const trimmed = driveSearchQuery.trim();
                              if (trimmed) {
                                if (googleDriveType === 'home') {
                                  fetchFilesAndFolders(selectedApp, undefined, { driveType: 'myDrive', sharedDriveId: undefined, searchQuery: trimmed });
                                } else {
                                  fetchFilesAndFolders(selectedApp, currentFolderId, { driveType: googleDriveType, sharedDriveId: googleSharedDriveId, searchQuery: trimmed });
                                }
                              }
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                            endAdornment: driveSearchQuery ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (driveSearchDebounceRef.current) clearTimeout(driveSearchDebounceRef.current);
                                    driveSearchDebounceRef.current = null;
                                    setDriveSearchQuery('');
                                    if (googleDriveType === 'home') {
                                      setFilesAndFolders([]);
                                      setFilesError(null);
                                    } else if (selectedApp) {
                                      fetchFilesAndFolders(selectedApp, currentFolderId, { driveType: googleDriveType, sharedDriveId: googleSharedDriveId, searchQuery: '' });
                                    }
                                  }}
                                  aria-label="Clear search"
                                  sx={{ p: 0.5 }}
                                >
                                  <Close sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                            sx: { backgroundColor: driveSearchInputFill }
                          }}
                          sx={{ maxWidth: 320, flex: 1 }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* SharePoint: search bar (same as Google Drive – search + clear X) */}
                  {selectedApp?.app_key === 'sharepoint' && (
                    <Box sx={{ px: 2, pb: 1.5, pt: 0, borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Search folders or files..."
                          value={sharePointSearchQuery}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSharePointSearchQuery(value);
                            if (sharePointSearchDebounceRef.current) clearTimeout(sharePointSearchDebounceRef.current);
                            sharePointSearchDebounceRef.current = setTimeout(() => {
                              sharePointSearchDebounceRef.current = null;
                              if (selectedApp) {
                                fetchFilesAndFolders(selectedApp, currentFolderId);
                              }
                            }, 400);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (sharePointSearchDebounceRef.current) clearTimeout(sharePointSearchDebounceRef.current);
                              sharePointSearchDebounceRef.current = null;
                              if (selectedApp) {
                                fetchFilesAndFolders(selectedApp, currentFolderId);
                              }
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                            endAdornment: sharePointSearchQuery ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (sharePointSearchDebounceRef.current) clearTimeout(sharePointSearchDebounceRef.current);
                                    sharePointSearchDebounceRef.current = null;
                                    setSharePointSearchQuery('');
                                    if (selectedApp) {
                                      fetchFilesAndFolders(selectedApp, currentFolderId);
                                    }
                                  }}
                                  aria-label="Clear search"
                                  sx={{ p: 0.5 }}
                                >
                                  <Close sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                            sx: { backgroundColor: driveSearchInputFill }
                          }}
                          sx={{ maxWidth: 320, flex: 1 }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Breadcrumb Navigation (hidden when searching from Home – show Search results below instead) */}
                  {!(selectedApp?.app_key === 'google_drive' && googleDriveType === 'home' && driveSearchQuery.trim()) && (
                  <Paper sx={{ p: 1.5, borderRadius: 0, backgroundColor: driveWizardMutedSurface, borderBottom: `1px solid ${muiTheme.palette.divider}`, flexShrink: 0 }}>
                    <Breadcrumbs 
                      aria-label="folder breadcrumb"
                      separator={<ChevronRight sx={{ fontSize: 18, color: muiTheme.palette.text.secondary }} />}
                    >
                      {selectedApp?.app_key === 'google_drive' ? (
                        <IconButton
                          size="small"
                          onClick={handleGoToHome}
                          sx={{ p: 0.25, mr: 0.25 }}
                          aria-label="Go to Home"
                        >
                          <HomeIcon sx={{ fontSize: 18, color: muiTheme.palette.text.secondary }} />
                        </IconButton>
                      ) : (
                        <Folder sx={{ fontSize: 18, color: muiTheme.palette.text.secondary, mr: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center' }} />
                      )}
                      {folderPath.map((folder, index) => {
                        // Google Drive: skip first segment (root) – Home icon is enough; avoids "Home" text and duplicate "My Drive"
                        if (selectedApp?.app_key === 'google_drive' && index === 0 && folder.id === undefined) {
                          return null;
                        }
                        return (
                          <Link
                            key={folder.id || 'root'}
                            component="button"
                            variant="body2"
                            onClick={() => handleBreadcrumbClick(index)}
                            sx={{
                              color: index === folderPath.length - 1 ? muiTheme.palette.text.primary : muiTheme.palette.primary.main,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              },
                              fontWeight: index === folderPath.length - 1 ? 600 : 400,
                              fontSize: '0.9rem'
                            }}
                          >
                            {folder.name}
                          </Link>
                        );
                      })}
                    </Breadcrumbs>
                  </Paper>
                  )}

                  {/* Files and Folders List (or Home 3 options, or Search results when searching from Home) */}
                  <Box sx={{ backgroundColor: driveWizardSurface, flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    {selectedApp?.app_key === 'google_drive' && googleDriveType === 'home' && !driveSearchQuery.trim() ? (
                      <Box sx={{ py: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>Choose a location to browse files</Typography>
                        {[
                          { type: 'myDrive' as const, label: 'My Drive', icon: <Folder sx={{ fontSize: 28, color: muiTheme.palette.warning.main }} /> },
                          { type: 'sharedDrives' as const, label: 'Shared drives', icon: <FolderShared sx={{ fontSize: 28, color: muiTheme.palette.text.secondary }} /> },
                          { type: 'sharedWithMe' as const, label: 'Shared with me', icon: <PeopleIcon sx={{ fontSize: 28, color: muiTheme.palette.text.secondary }} /> },
                        ].map(({ type, label, icon }) => (
                          <Box
                            key={type}
                            onClick={() => handleDriveTypeSelect(type)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1.5,
                              borderBottom: `1px solid ${muiTheme.palette.divider}`,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: muiTheme.palette.action.hover },
                            }}
                          >
                            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{icon}</Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                            <ArrowForward sx={{ fontSize: 20, color: 'text.secondary', ml: 'auto' }} />
                          </Box>
                        ))}
                      </Box>
                    ) : loadingFiles ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Loading files...
                        </Typography>
                      </Box>
                    ) : filesError ? (
                      <Alert severity="error" sx={{ m: 2 }}>{filesError}</Alert>
                    ) : filesAndFolders.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        {selectedApp?.app_key === 'google_drive' && driveSearchQuery.trim() ? (
                          <>
                            <SearchIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body2">No results for &quot;{driveSearchQuery.trim()}&quot;</Typography>
                          </>
                        ) : selectedApp?.app_key === 'sharepoint' && sharePointSearchQuery.trim() ? (
                          <>
                            <SearchIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body2">No results for &quot;{sharePointSearchQuery.trim()}&quot;</Typography>
                          </>
                        ) : (
                          <>
                            <FolderOpen sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body2">This folder is empty</Typography>
                          </>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        {/* Search results heading when searching (Google Drive or SharePoint) */}
                        {((selectedApp?.app_key === 'google_drive' && driveSearchQuery.trim()) || (selectedApp?.app_key === 'sharepoint' && sharePointSearchQuery.trim())) && (
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, px: 2, py: 1.5, borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
                            Search results
                          </Typography>
                        )}
                        {/* Folders first, then files (same as drive view) */}
                        {filesAndFolders.filter(item => item.isFolder).map((folder) => {
                      const isFolderSelected = selectedFolders.some(f => f.id === folder.id);
                      const isFolderLoading = loadingFolderContents[folder.id];
                      const folderFileCount = folderContents[folder.id]?.length || 0;
                      
                      return (
                      <Box
                        key={folder.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          borderBottom: `1px solid ${muiTheme.palette.divider}`,
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          backgroundColor: isFolderSelected ? muiTheme.palette.primary.light + '10' : 'transparent',
                          '&:hover': {
                            backgroundColor: muiTheme.palette.action.hover,
                          }
                        }}
                      >
                        {/* Checkbox for folder selection (hidden for shared drive entries - they are navigable only) */}
                        {!folder.isSharedDrive && (
                          <Checkbox
                            checked={isFolderSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFolderSelect(folder);
                            }}
                            disabled={isFolderLoading || (!isFolderSelected && driveStorageSelectionBlocked)}
                            sx={{ 
                              mr: 1,
                              '& .MuiSvgIcon-root': {
                                fontSize: 22
                              }
                            }}
                          />
                        )}
                        {folder.isSharedDrive && <Box sx={{ width: 42, mr: 0.5 }} />}
                        
                        {/* Folder Icon */}
                        <Box 
                          sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
                          onClick={() => handleFolderClick(folder)}
                        >
                          {isFolderLoading ? (
                            <CircularProgress size={24} />
                          ) : (
                          <Folder sx={{ fontSize: 32, color: muiTheme.palette.warning.main }} />
                          )}
                        </Box>

                        {/* Folder Name with file count (or "Shared drive" for shared drive entries) */}
                        <Box 
                          sx={{ flex: 1 }}
                          onClick={() => handleFolderClick(folder)}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, color: muiTheme.palette.text.primary }}>
                            {folder.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {folder.isSharedDrive ? 'Shared drive' : isFolderSelected ? `Folder (${folderFileCount} file${folderFileCount !== 1 ? 's' : ''} selected)` : 'Folder'}
                          </Typography>
                        </Box>

                        {/* Arrow indicator */}
                        <ArrowForward 
                          sx={{ fontSize: 20, color: muiTheme.palette.text.secondary }}
                          onClick={() => handleFolderClick(folder)}
                        />
                      </Box>
                      );
                    })}
                    
                    {/* Files */}
                    {filesAndFolders.filter(item => !item.isFolder).map((file) => {
                      const isSelectable = isFileSelectable(file);
                      const maxSize = 100 * 1024 * 1024; // 100MB
                      const isOversized = file.size && file.size > maxSize;
                      const isFileSelectedRow = selectedFiles.some(f => f.id === file.id);
                      const isAtLimit = selectedFiles.length >= DATA_SOURCE_LIMIT && !isFileSelectedRow;
                      const canSelect = isSelectable && !isOversized && !isAtLimit;
                      
                      let tooltipMessage = '';
                      if (!isSelectable) {
                        const supportedFormats = getAllowedFileFormatsForDisplay();
                        tooltipMessage = `Unsupported file format. Supported formats: ${supportedFormats}`;
                      } else if (isOversized) {
                        tooltipMessage = 'File exceeds 100MB size limit';
                      } else if (isAtLimit) {
                        tooltipMessage = `Maximum ${DATA_SOURCE_LIMIT} files can be selected`;
                      }
                      
                      return (
                      <Box
                        key={file.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          borderBottom: `1px solid ${muiTheme.palette.divider}`,
                          transition: 'background-color 0.2s',
                          opacity: canSelect || isFileSelectedRow ? 1 : 0.5,
                          '&:hover': {
                            backgroundColor: muiTheme.palette.action.hover,
                          },
                          '&:last-child': {
                            borderBottom: 'none'
                          }
                        }}
                      >
                        {/* Checkbox for files */}
                        <Tooltip
                          title={tooltipMessage}
                          arrow
                          placement="top"
                        >
                          <span>
                        <Checkbox
                          checked={isFileSelectedRow}
                          onChange={() => void handleFileSelect(file)}
                          onClick={(e) => e.stopPropagation()}
                              disabled={(!canSelect && !isFileSelectedRow) || (!isFileSelectedRow && driveStorageSelectionBlocked)}
                          sx={{ 
                            mr: 1,
                            '& .MuiSvgIcon-root': {
                              fontSize: 22
                            }
                          }}
                        />
                          </span>
                        </Tooltip>
                        
                        {/* File Icon */}
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                          {getFileIcon(file.mimeType)}
                        </Box>

                        {/* File Name and details */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {file.name}
                          </Typography>
                          {file.size && (
                            <Typography variant="caption" color="text.secondary">
                              {formatBytes(file.size)}
                            </Typography>
                          )}
                        </Box>

                        {/* File type badge */}
                        {file.fileExtension && (
                          <Chip
                            label={file.fileExtension.toUpperCase()}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 22,
                              backgroundColor: muiTheme.palette.primary.light + '20',
                              color: muiTheme.palette.primary.main,
                              fontWeight: 600,
                              ml: 1
                            }}
                          />
                        )}
                      </Box>
                      );
                    })}
                      </Box>
                    )}
                  </Box>
              </Box>
                </Box>
                )}

                {/* Step 3: Review Selection */}
                {fileSelectionStep === 3 && (
                  <Box sx={{ 
                    border: `1px solid ${muiTheme.palette.divider}`, 
                    borderRadius: 2, 
                    overflow: 'hidden', 
                    backgroundColor: driveWizardSurface,
                    p: 3
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                      Review Your Selection
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Please review your selected files and folders before proceeding.
                    </Typography>
                    
                    {/* Summary Cards — turn green immediately when user confirms "Add Data Source" */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <Paper sx={{
                        p: 2, flex: 1,
                        backgroundColor: datasourcesQueued
                          ? muiTheme.palette.success.light + '20'
                          : muiTheme.palette.primary.light + '10',
                        transition: 'background-color 0.3s ease',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h4" sx={{
                            fontWeight: 600,
                            color: datasourcesQueued ? muiTheme.palette.success.main : muiTheme.palette.primary.main,
                            transition: 'color 0.3s ease',
                          }}>
                            {selectedFiles.length}
                          </Typography>
                          {datasourcesQueued && <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          File{selectedFiles.length !== 1 ? 's' : ''} {datasourcesQueued ? 'Added' : 'Selected'}
                        </Typography>
                      </Paper>
                      <Paper sx={{
                        p: 2, flex: 1,
                        backgroundColor: datasourcesQueued
                          ? muiTheme.palette.success.light + '20'
                          : muiTheme.palette.warning.light + '10',
                        transition: 'background-color 0.3s ease',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h4" sx={{
                            fontWeight: 600,
                            color: datasourcesQueued ? muiTheme.palette.success.main : muiTheme.palette.warning.main,
                            transition: 'color 0.3s ease',
                          }}>
                            {selectedFolders.length}
                          </Typography>
                          {datasourcesQueued && selectedFolders.length > 0 && <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Folder{selectedFolders.length !== 1 ? 's' : ''} {datasourcesQueued ? 'Added' : 'Selected'}
                        </Typography>
                      </Paper>
                    </Box>

                    {/* Success confirmation banner — appears immediately when "Add Data Source" is clicked */}
                    {datasourcesQueued && (
                      <Alert
                        severity="success"
                        icon={<CheckCircle fontSize="inherit" />}
                        sx={{ mb: 2, fontSize: '0.85rem' }}
                      >
                        <strong>
                          {selectedFiles.length + selectedFolders.length} item{selectedFiles.length + selectedFolders.length !== 1 ? 's' : ''} queued successfully.
                        </strong>{' '}
                        They will appear in your Data Sources panel and begin processing shortly.
                      </Alert>
                    )}

                    {/* Selected Items List */}
                    {(selectedFiles.length > 0 || selectedFolders.length > 0) && (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: 400,
                          boxShadow: 'none',
                          border: `1px solid ${datasourcesQueued ? muiTheme.palette.success.main : muiTheme.palette.divider}`,
                          transition: 'border-color 0.3s ease',
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, backgroundColor: driveReviewTableHeaderBg }}>
                                Name
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, backgroundColor: driveReviewTableHeaderBg, width: 120 }}>
                                Type
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, backgroundColor: driveReviewTableHeaderBg, width: 100 }}>
                                Size
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectionOrder.map((item) => {
                              if (item.type === 'folder') {
                                const folder = selectedFolders.find(f => f.id === item.id);
                                if (!folder) return null;
                                const fileCount = folderContents[folder.id]?.length || 0;
                                const folderTotalSize = folderContents[folder.id]?.reduce((total, file) => {
                                  return total + (file.size || 0);
                                }, 0) || 0;
                                const isExpanded = expandedFolders.has(folder.id);
                                const folderFiles = folderContents[folder.id] || [];
                                
                                return (
                                  <React.Fragment key={`folder-${folder.id}`}>
                                    <TableRow 
                                      hover 
                                      sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                          backgroundColor: muiTheme.palette.action.hover
                                        }
                                      }}
                                      onClick={() => {
                                        setExpandedFolders(prev => {
                                          const newSet = new Set(prev);
                                          if (newSet.has(folder.id)) {
                                            newSet.delete(folder.id);
                                          } else {
                                            newSet.add(folder.id);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    >
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedFolders(prev => {
                                                const newSet = new Set(prev);
                                                if (newSet.has(folder.id)) {
                                                  newSet.delete(folder.id);
                                                } else {
                                                  newSet.add(folder.id);
                                                }
                                                return newSet;
                                              });
                                            }}
                                            sx={{ 
                                              p: 0.5,
                                              ml: -1,
                                              mr: 0.5,
                                              color: muiTheme.palette.text.secondary,
                                              '&:hover': {
                                                backgroundColor: muiTheme.palette.action.hover
                                              }
                                            }}
                                          >
                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                          </IconButton>
                                          {isExpanded ? (
                                            <FolderOpen sx={{ fontSize: 20, color: muiTheme.palette.warning.main }} />
                                          ) : (
                                            <Folder sx={{ fontSize: 20, color: muiTheme.palette.warning.main }} />
                                          )}
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {folder.name}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={`${fileCount} file${fileCount !== 1 ? 's' : ''}`}
                                          size="small"
                                          sx={{
                                            fontSize: '0.65rem',
                                            height: 22,
                                            backgroundColor: muiTheme.palette.warning.light + '30',
                                            color: muiTheme.palette.warning.dark,
                                            fontWeight: 600
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                          {folderTotalSize > 0 ? formatBytes(folderTotalSize) : '-'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                    {/* Expanded folder files */}
                                    {isExpanded && folderFiles.map((file) => (
                                      <TableRow 
                                        key={`folder-${folder.id}-file-${file.id}`}
                                        sx={{
                                          backgroundColor: driveWizardMutedSurface,
                                          '&:hover': {
                                            backgroundColor: muiTheme.palette.action.hover
                                          }
                                        }}
                                      >
                                        <TableCell sx={{ pl: 6 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {getFileIcon(file.mimeType)}
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                              {file.name}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={file.fileExtension?.toUpperCase() || 'FILE'}
                                            size="small"
                                            sx={{
                                              fontSize: '0.65rem',
                                              height: 20,
                                              backgroundColor: muiTheme.palette.info.light + '30',
                                              color: muiTheme.palette.info.dark,
                                              fontWeight: 600
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            {file.size ? formatBytes(file.size) : '-'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </React.Fragment>
                                );
                              } else {
                                const file = selectedFiles.find(f => f.id === item.id);
                                if (!file) return null;
                                
                                return (
                                  <TableRow key={`file-${file.id}`} hover>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {getFileIcon(file.mimeType)}
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {file.name}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={file.fileExtension?.toUpperCase() || 'FILE'}
                                        size="small"
                                        sx={{
                                          fontSize: '0.65rem',
                                          height: 22,
                                          backgroundColor: muiTheme.palette.info.light + '30',
                                          color: muiTheme.palette.info.dark,
                                          fontWeight: 600
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        {file.size ? formatBytes(file.size) : '-'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Box>

              {/* Step Navigation Buttons - Only show if showStepNavigationButtons is true */}
              {fileSelectionStep > 0 && showStepNavigationButtons && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mt: 3,
                  pt: 2,
                  borderTop: `1px solid ${muiTheme.palette.divider}`
                }}>
                  <Button
                    onClick={handleBackStep}
                    startIcon={<ArrowBack />}
                    sx={{ textTransform: 'none' }}
                  >
                    Back
                  </Button>
                  
                  {fileSelectionStep < fileSelectionSteps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleNextStep}
                      endIcon={<ArrowForward />}
                      sx={{ textTransform: 'none' }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color={datasourcesQueued ? 'success' : 'primary'}
                      onClick={async () => {
                        const ok = await handleAddDataSource();
                        // Only show success state if the operation actually succeeded.
                        // handleAddDataSource() returns false on validation failure,
                        // subscription limit, or API error — in those cases the error
                        // is already surfaced via a toast/field error, so we must not
                        // commit to the green "Added!" state.
                        if (ok !== false) setDatasourcesQueued(true);
                      }}
                      disabled={isCreatingDataSource || datasourcesQueued}
                      startIcon={
                        datasourcesQueued
                          ? <CheckCircle fontSize="small" />
                          : isCreatingDataSource
                          ? <CircularProgress size={20} color="inherit" />
                          : undefined
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      {datasourcesQueued
                        ? 'Added!'
                        : isCreatingDataSource
                        ? `Adding ${LanguageConstants.datasource}...`
                        : `Add ${LanguageConstants.datasource}`}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Info Message */}
          {selectedApp && !showFileBrowser && (
            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                You have selected <strong>{selectedApp.app_name}</strong>. Click "Add {LanguageConstants.datasource}" to proceed.
              </Typography>
            </Alert>
          )}
        </Box>
      </TabPanel>

      {/* Connect App panel hidden (webscrapping_UI branch) — index 4 stays reserved (see the
          hidden placeholder Tab above) so isAppTab/tabValue===4 logic elsewhere is untouched;
          this panel simply never renders since nothing sets tabValue to 4 anymore. Inner JSX
          comments were stripped before wrapping, since JSX comments cannot be nested.
      <TabPanel value={tabValue} index={4}>
        Connect App Tab
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          Channel Selection Dropdown - Only show when channelId is not provided
          {!channelId && (
            <ChannelSelectDropdown
              value={selectedWorkspace}
              onChange={(value) => {
                setSelectedWorkspace(value);
                if (workspaceError) setWorkspaceError('');
              }}
              onBlur={() => {
                if (!selectedWorkspace) setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
              }}
              error={workspaceError}
              required={true}
              disabled={loadingChannels}
              onOpen={handleWorkspaceDropdownOpen}
              channels={channels}
              loadingChannels={loadingChannels}
              channelsError={channelsError}
            />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              Connect your applications to automatically import {LanguageConstants.datasource.toLowerCase()}.
            </Typography>

          <AppsGrid
            channelId={channelId || selectedWorkspace}
            pageSize={8}
            showSearch={false}
            showCategoryFilter={false}
            selectionMode={true}
            selectedApp={selectedApp}
            onAppSelect={handleSelectApp}
            connectingApps={connectingApps}
            onConnect={handleConnect}
            height="350px"
            disableInternalScroll={true}
            subCategory="Datasource"
            refreshTrigger={refreshTrigger}
            optimisticConnectedAppId={optimisticConnectedAppId}
          />

          Info Message
          {selectedApp && !showFileBrowser && isAppTab && (
            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                You have selected <strong>{selectedApp.app_name}</strong>. Click "Add {LanguageConstants.datasource}" to proceed.
              </Typography>
            </Alert>
          )}
        </Box>
      </TabPanel>
      */}

      <TabPanel value={tabValue} index={5}>
        {/* Webscrapping Tab (webscrapping_UI branch) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {!channelId && (
            <ChannelSelectDropdown
              value={selectedWorkspace}
              onChange={(value) => {
                setSelectedWorkspace(value);
                if (workspaceError) setWorkspaceError('');
              }}
              onBlur={() => {
                if (!selectedWorkspace) setWorkspaceError(`Please select a ${LanguageConstants.topic.toLowerCase()}`);
              }}
              error={workspaceError}
              required={true}
              disabled={loadingChannels}
              onOpen={handleWorkspaceDropdownOpen}
              channels={channels}
              loadingChannels={loadingChannels}
              channelsError={channelsError}
            />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
            We'll scrape this page and, optionally, linked pages on the same site.
          </Typography>

          <TextField
            fullWidth
            label="Website Name *"
            placeholder="Enter a name for this data source"
            value={websiteName}
            onChange={(e) => {
              setWebsiteName(e.target.value);
              if (websiteNameError) setWebsiteNameError('');
            }}
            onBlur={() => {
              if (!websiteName.trim()) setWebsiteNameError('Please enter a name');
            }}
            error={Boolean(websiteNameError)}
            helperText={websiteNameError}
            autoComplete="off"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#bdbdbd' },
                '&:hover fieldset': { borderColor: '#bdbdbd' },
                '&.Mui-focused fieldset': { borderColor: '#bdbdbd' },
              },
            }}
          />

          <TextField
            fullWidth
            type="url"
            label="Website URL*"
            placeholder="https://example.com/page or example.com"
            value={websiteUrl}
            onChange={(e) => {
              setWebsiteUrl(e.target.value);
              if (websiteUrlError) setWebsiteUrlError('');
            }}
            onBlur={() => {
              setWebsiteUrlError(validateWebsiteUrl(websiteUrl).error || '');
            }}
            error={Boolean(websiteUrlError)}
            helperText={websiteUrlError}
            autoComplete="off"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#bdbdbd' },
                '&:hover fieldset': { borderColor: '#bdbdbd' },
                '&.Mui-focused fieldset': { borderColor: '#bdbdbd' },
              },
            }}
          />

          <Box>
            <Button
              size="small"
              onClick={() => setShowWebsiteAdvanced((prev) => !prev)}
              endIcon={showWebsiteAdvanced ? <ExpandLess /> : <ExpandMore />}
              sx={{ textTransform: 'none', px: 0 }}
            >
              Advanced
            </Button>
            {showWebsiteAdvanced && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <FormControl sx={{ width: 360, maxWidth: '100%' }}>
                  <InputLabel>How deep should we crawl this website?</InputLabel>
                  <Select
                    label="How deep should we crawl this website?"
                    value={websiteCrawlDepth}
                    onChange={(e) => setWebsiteCrawlDepth(e.target.value)}
                    // This modal's own Dialog is deliberately elevated to
                    // DATA_SOURCES_DIALOG_Z_INDEX (1400, above the Data Sources panel's
                    // 1350) — higher than MUI's default Modal z-index (1300) that a plain
                    // Select's popup menu uses otherwise, which rendered it behind this
                    // dialog (invisible, not "not opening"). Push the menu above it explicitly.
                    MenuProps={{ sx: { zIndex: DATA_SOURCES_DIALOG_Z_INDEX + 10 } }}
                  >
                    <MenuItem value="1">Shallow crawl</MenuItem>
                    <MenuItem value="2">Medium crawl</MenuItem>
                    <MenuItem value="3">Deep crawl</MenuItem>
                  </Select>
                  <FormHelperText>Higher levels follow more linked pages.</FormHelperText>
                </FormControl>
                {/* webscrapping_fix branch: Scrapping Pages hidden — it was always a disabled,
                    fixed-at-50 display field (max_pages: 50 is still sent unchanged in the
                    payload below), not a real control, so there's nothing for the user to act on
                    here. Commented out rather than deleted in case it needs to become editable
                    later.
                <TextField
                  label="Scrapping Pages"
                  value="50"
                  disabled
                  helperText="Fixed at 50 pages"
                  sx={{ width: 180 }}
                />
                */}
              </Box>
            )}
          </Box>
        </Box>
      </TabPanel>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        {/* Skip for Now button - Only show when not hidden */}
        {channelId && !hideInternalButtons && (
          <Button
            variant="outlined"
            onClick={() => {
              onClose?.(); // Close the popup when skipping
            }}
          >
            Skip for Now
          </Button>
        )}

                  {/* Add ${LanguageConstants.datasource} button - Only show when not hidden */}
        {showAddDataSourceButton  && !hideInternalButtons && (
          <Button
            variant="contained"
            onClick={handleAddDataSource}
            disabled={isCreatingDataSource}
            startIcon={isCreatingDataSource ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isWebsiteTab
              ? (isCreatingDataSource ? 'Scraping Started...' : 'Start Scrapping')
              : (isCreatingDataSource ? `Adding ${LanguageConstants.datasource} ...` : `Add ${LanguageConstants.datasource}`)}
          </Button>
        )}
      </Box>

      {/* Connected Datasources Count Tile - Only show if not in footer mode */}
      {!hideInternalButtons && (
        <ConnectedDatasourcesCount 
          channelId={channelId} 
          tabValue={tabValue} 
          refreshTrigger={refreshTrigger}
          variant="tile"
        />
      )}

      {/* Attachment Restriction Modal */}
      <ChannelRestrictionModal
        open={openAttachmentRestrictionModal}
        onClose={() => {
          setOpenAttachmentRestrictionModal(false);
          // Failed pick (e.g. 7MB > 5MB left) never adds to selection but set this flag — re-enable drive checkboxes when user dismisses
          setDriveStorageSelectionBlocked(false);
        }}
        restrictionType="attachment"
      />
      
      {/* Basic Auth Dialog for Freshdesk */}
      <BasicAuthConnectionDialog
        open={basicAuthDialogOpen}
        appName={pendingBasicAuthConnection?.appName || 'Freshdesk'}
        onClose={handleBasicAuthDialogClose}
        onConnect={handleBasicAuthConnect}
      />
    </Box>
  );
});

DataSourceStep.displayName = 'DataSourceStep';

export default DataSourceStep; 
